/**
 * OneAuthClient — the primary entry point for the @rhinestone/1auth SDK.
 *
 * Manages all communication between an integrating app and the 1auth passkey
 * service (apps/passkey). Interactions happen in two ways:
 *
 *   1. HTTP — direct REST calls to the passkey service API for non-interactive
 *      operations such as intent preparation, status polling, and consent checks.
 *
 *   2. PostMessage — iframe/popup dialogs hosted at the passkey domain handle
 *      WebAuthn ceremonies and return results to the parent page.
 *
 * Supported flows:
 *   - Authentication (sign in / sign up) via modal, popup, or redirect
 *   - Passkey challenge signing for off-chain login
 *   - EIP-712 typed data signing
 *   - Cross-chain intents via the Rhinestone orchestrator
 *   - Batch intents (multiple chains, single passkey tap)
 *   - Data consent management
 */
import { hashTypedData, type TypedDataDefinition } from "viem";
import { fetchAssetsResponse } from "./assets";
import { tokenFetchError } from "./sponsorship-fetch";
import {
  executeErrorCode,
  executeErrorDetails,
  normalizeOneAuthErrorDetails,
  parseSigningResultError,
} from "./errors";
import type {
  AssetsResponse,
  PasskeyProviderConfig,
  SigningRequestOptions,
  SigningResult,
  CreateSigningRequestResponse,
  SigningRequestStatus,
  EmbedOptions,
  PasskeyCredentials,
  SigningPasskeyCredentials,
  AuthResult,
  AuthFlow,
  AuthWithModalOptions,
  LoginWithModalOptions,
  CreateAccountWithModalOptions,
  ConnectResult,
  AuthenticateOptions,
  AuthenticateResult,
  IntentCall,
  SendIntentOptions,
  SendIntentResult,
  PrepareIntentResponse,
  SponsorshipMode,
  ExecuteIntentResponse,
  OneAuthErrorDetails,
  WebAuthnSignature,
  ThemeConfig,
  SignMessageOptions,
  SignMessageResult,
  SignTypedDataOptions,
  SignTypedDataResult,
  IntentHistoryOptions,
  IntentHistoryResult,
  GetAssetsOptions,
  SendBatchIntentOptions,
  SendBatchIntentResult,
  PrepareBatchIntentResponse,
  PrepareBatchIntentDraftResponse,
  BatchIntentItemResult,
  CheckConsentOptions,
  CheckConsentResult,
  RequestConsentOptions,
  RequestConsentResult,
  GrantPermissionsOptions,
  GrantPermissionsResult,
  ListSessionGrantsOptions,
  ListSessionGrantsResult,
  SponsorshipConfig,
  SponsorshipCallbackConfig,
  OneAuthTelemetryAttributes,
  OneAuthTelemetryEvent,
  OneAuthTelemetryEventName,
  OneAuthTelemetryFlow,
  OneAuthTelemetryTraceContext,
} from "./types";

/** Require the server-owned slot before forwarding credentials to intent execution. */
function requireSigningPasskeyCredentials(
  value: PasskeyCredentials | undefined,
): SigningPasskeyCredentials | undefined {
  if (!value) return undefined;
  return typeof value.keyId === "number" ? (value as SigningPasskeyCredentials) : undefined;
}
import {
  BRAND_PURPLE,
  DARK_TOKENS,
  LIGHT_TOKENS,
  LOADING_BODY_GAP,
  LOADING_FONT_FAMILY,
  LOADING_ICON_PADDING,
  LOADING_LINE_HEIGHT,
  LOADING_MODAL_PADDING,
  LOADING_MODAL_RADIUS,
  LOADING_MODAL_WIDTH,
  LOADING_SUBTITLE,
  LOADING_SUBTITLE_FONT_SIZE,
  LOADING_SUBTITLE_FONT_WEIGHT,
  LOADING_TITLE,
  LOADING_TITLE_BLOCK_GAP,
  LOADING_TITLE_FONT_SIZE,
  LOADING_TITLE_FONT_WEIGHT,
  SPINNER_ARC,
  SPINNER_CIRCUMFERENCE,
  SPINNER_RADIUS,
  SPINNER_SIZE,
  SPINNER_SPIN_DURATION_MS,
  SPINNER_STROKE,
} from "@1auth/loading-tokens";

// Modal width is derived from LOADING_MODAL_WIDTH (single source of truth,
// owned by `@1auth/loading-tokens`). Changing the width here in isolation
// would let popup/iframe drift from the Figma spec; keep them tied.
const POPUP_WIDTH = LOADING_MODAL_WIDTH;
const POPUP_HEIGHT = 600;
const DEFAULT_EMBED_WIDTH = `${LOADING_MODAL_WIDTH}px`;
const DEFAULT_EMBED_HEIGHT = "500px";
const DEFAULT_PROVIDER_URL = "https://passkey.1auth.app";

/**
 * Return the embedding page origin when the SDK is running in a browser.
 * The dialog uses this only for first-paint attribution and postMessage
 * targeting; authenticated server-side origin decisions still use browser
 * provided referrer/event.origin values inside the passkey app.
 */
function currentWindowOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const origin = window.location?.origin;
  return typeof origin === "string" && origin.length > 0 ? origin : null;
}

/**
 * Add the host origin to a dialog URL so the iframe can render the origin pill
 * on its first client render instead of waiting for PASSKEY_INIT or
 * document.referrer effects. Kept as a URL hint, not an authorization signal.
 */
function appendParentOriginParam(params: URLSearchParams): void {
  const origin = currentWindowOrigin();
  if (origin) params.set("parentOrigin", origin);
}

// Max characters for IntentCall.label / .sublabel before the SDK truncates
// with an ellipsis. The sign dialog renders these in a ~200–260px column at
// text-[16px] bold (see SignDialogBody.tsx TokenMovementCard). At that font
// you fit ~17 typical chars; 20 is a forgiving cap that still avoids wrapping
// for most strings while letting CSS `truncate` handle the worst-case fonts
// as a safety net.
const MAX_LABEL_LEN = 20;

// Default scrim appearance — preserved verbatim from the pre-`backdrop`-option
// behavior so apps that never set `theme.backdrop` see no change. The dialog
// (apps/passkey) uses the identical defaults for its in-iframe scrim.
const DEFAULT_BACKDROP_COLOR = "#000000";
const DEFAULT_BACKDROP_OPACITY = 0.4;
const DEFAULT_BACKDROP_BLUR = 8;
// Only `#rrggbb` is allowed — same hard restriction the dialog applies to the
// accent color (apps/passkey/src/lib/dialog-messages.ts ACCENT_RE). The value
// is interpolated into a stylesheet string below, so anything looser (named
// colors, `rgb()`, escapes) could become a CSS-injection vector.
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Resolve the scrim tint (as an `rgba(...)` string) and blur radius (px) for
 * the SDK loading overlay from the dialog URL's `backdrop*` query params.
 *
 * The overlay is painted by the SDK on the host page before the iframe renders
 * (see {@link OneAuthClient.createModalDialog}); it must match the in-iframe
 * scrim the passkey app paints afterward so the host→iframe handoff is
 * seamless. Both sides read the same params and clamp identically.
 *
 * Every field falls back to its default independently and invalid/out-of-range
 * values are coerced back to the default, so a malformed param can never
 * produce a broken `background`/`backdrop-filter` declaration.
 */
export function resolveBackdropStyle(params: URLSearchParams): {
  background: string;
  blur: number;
} {
  const rawColor = params.get("backdropColor");
  const hex = (rawColor && HEX_COLOR_RE.test(rawColor) ? rawColor : DEFAULT_BACKDROP_COLOR).slice(1);
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Parse a numeric param back to its default when the field is absent.
  // `URLSearchParams.get` returns null for omitted params and "" for empty
  // ones, and `Number(null) === Number("") === 0` — which passes
  // `Number.isFinite`. Guarding on the raw value first keeps an omitted
  // `backdropOpacity`/`backdropBlur` on its historical default (0.4 / 8px)
  // instead of collapsing to a fully-transparent, unblurred overlay. An
  // explicit "0" is still a real, finite value and is honored.
  const clampNumber = (key: string, min: number, max: number, fallback: number): number => {
    const raw = params.get(key);
    if (!raw) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  };

  const opacity = clampNumber("backdropOpacity", 0, 1, DEFAULT_BACKDROP_OPACITY);
  const blur = clampNumber("backdropBlur", 0, 40, DEFAULT_BACKDROP_BLUR);

  return { background: `rgba(${r}, ${g}, ${b}, ${opacity})`, blur };
}

/**
 * Generate a unique nonce for a single modal session. Used by
 * `waitForModalAuthResponse` (and any other per-modal message listener)
 * to reject stale CLOSE / RESULT messages that a previous, already
 * cleaned-up iframe queued before its teardown — without the nonce the
 * new modal's listener would process the stale event and silently cancel
 * the fresh auth attempt. `crypto.randomUUID` is available in every
 * browser this SDK targets; fall back to a Math.random pair so non-
 * conformant environments don't throw at construction time.
 */
function generateModalNonce(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

type TelemetryOperation = {
  operationId: string;
  flow: OneAuthTelemetryFlow;
  startedAt: number;
  trace?: OneAuthTelemetryTraceContext;
  attributes?: OneAuthTelemetryAttributes;
};

type TelemetryEventPatch = Partial<
  Omit<OneAuthTelemetryEvent, "name" | "operationId" | "flow" | "timestamp">
>;

/**
 * Generate a stable correlation id for one SDK operation. It is intentionally
 * opaque and random so it can be logged safely without leaking user data.
 */
function generateTelemetryOperationId(): string {
  return `1auth-${generateModalNonce()}`;
}

/**
 * Keep telemetry attributes JSON/log friendly. Undefined values are dropped so
 * callbacks and headers do not need to normalize SDK-produced objects.
 */
function cleanTelemetryAttributes(
  attributes: OneAuthTelemetryAttributes | undefined,
): OneAuthTelemetryAttributes | undefined {
  if (!attributes) return undefined;
  const clean: OneAuthTelemetryAttributes = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) clean[key] = value;
  }
  return Object.keys(clean).length > 0 ? clean : undefined;
}

/**
 * Extract a consistent code/message pair from SDK result errors or thrown
 * exceptions without assuming a specific error class.
 */
function describeTelemetryError(error: unknown): {
  errorCode?: string;
  errorMessage?: string;
} {
  if (!error) return {};
  if (error instanceof Error) {
    return { errorCode: error.name, errorMessage: error.message };
  }
  if (typeof error === "object") {
    const err = error as { code?: unknown; message?: unknown };
    return {
      errorCode: typeof err.code === "string" ? err.code : undefined,
      errorMessage: typeof err.message === "string" ? err.message : String(error),
    };
  }
  return { errorMessage: String(error) };
}

/**
 * Truncate an IntentCall's `label` / `sublabel` to fit the sign dialog's
 * single-line label column. Returns the same call shape with capped strings;
 * leaves all other fields (to, data, value, icon) untouched. Returns the
 * input unchanged when `calls` is undefined or empty.
 *
 * The dialog also applies `truncate` CSS as a defense-in-depth measure — this
 * SDK cap exists so the request on the wire matches what the user sees, and
 * so apps can tell their label was too long (the truncated form ships).
 */
function capCallLabels(calls: IntentCall[] | undefined): IntentCall[] | undefined {
  if (!calls) return calls;
  return calls.map((c) => {
    const cappedLabel =
      c.label && c.label.length > MAX_LABEL_LEN
        ? c.label.slice(0, MAX_LABEL_LEN - 1) + "…"
        : c.label;
    const cappedSublabel =
      c.sublabel && c.sublabel.length > MAX_LABEL_LEN
        ? c.sublabel.slice(0, MAX_LABEL_LEN - 1) + "…"
        : c.sublabel;
    if (cappedLabel === c.label && cappedSublabel === c.sublabel) return c;
    return { ...c, label: cappedLabel, sublabel: cappedSublabel };
  });
}

/**
 * Reads the `signerType` from the stored session in localStorage. Absent or
 * malformed storage falls back to `"passkey"` so existing installs remain
 * backwards compatible.
 */
function getStoredSignerType(): "passkey" | "eoa" {
  if (typeof window === "undefined") return "passkey";
  try {
    const raw = window.localStorage.getItem("1auth-user");
    if (!raw) return "passkey";
    const parsed = JSON.parse(raw) as { signerType?: "passkey" | "eoa" };
    return parsed?.signerType === "eoa" ? "eoa" : "passkey";
  } catch {
    return "passkey";
  }
}

type NormalizedPasskeyProviderConfig = PasskeyProviderConfig & {
  providerUrl: string;
  dialogUrl: string;
};

type BlindSigningOption = { blind_signing?: boolean };

/**
 * Global default for blind signing, used when neither the per-call option nor
 * the client config expresses a preference.
 *
 * `true` means apps get blind signing out of the box and must *opt in* to
 * 1auth's visible review UI (clear signing) by passing `blind_signing: false`
 * on the client or per call. This is the single switch for the rollout: when
 * we're ready to change the platform-wide default, flip this one constant —
 * every app that hasn't set its own preference follows it.
 */
const DEFAULT_BLIND_SIGNING = true;

type SponsorshipTokens = { accessToken: string; extensionTokens: string[] };

type AccessTokenFetchResult =
  | { ok: true; accessToken: string }
  | { ok: false; code?: "MISSING_APP_CREDENTIALS"; message: string };

type ExtensionTokensFetchResult =
  | { ok: true; extensionTokens: string[] }
  | { ok: false; message: string };

const MISSING_APP_CREDENTIALS_MESSAGE =
  "No sponsorship configured on OneAuthClient. Every user intent must carry the app's JWT — pass `sponsorship: { accessToken, getExtensionToken }` (or URL form) to the constructor.";

/**
 * Normalize any `SponsorshipConfig` shape to the internal callback form.
 *
 * The URL form is a thin convenience — it gets wrapped into callbacks that
 * hit the app's own endpoints. Using same-origin URLs is recommended so the
 * browser forwards session cookies and the app can authenticate the user
 * server-side before minting a sponsorship grant.
 *
 * The access-token endpoint is GET and returns `{ token }`. The extension-
 * token endpoint is POST `{ intentOp }` and returns `{ token }`.
 */
function normalizeSponsorship(
  config: SponsorshipConfig | undefined,
): SponsorshipCallbackConfig | null {
  if (!config) return null;
  if ("accessToken" in config && "getExtensionToken" in config) {
    return config;
  }
  const { accessTokenUrl, extensionTokenUrl } = config;
  return {
    accessToken: async () => {
      const response = await fetch(accessTokenUrl, { credentials: "include" });
      if (!response.ok) {
        throw await tokenFetchError("access token", response);
      }
      const data = (await response.json()) as { token?: string };
      if (!data.token) {
        throw new Error("Access token response missing `token` field");
      }
      return data.token;
    },
    getExtensionToken: async (intentOp: string) => {
      const response = await fetch(extensionTokenUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentOp }),
      });
      if (!response.ok) {
        throw await tokenFetchError("extension token", response);
      }
      const data = (await response.json()) as { token?: string };
      if (!data.token) {
        throw new Error("Extension token response missing `token` field");
      }
      return data.token;
    },
  };
}

/**
 * JSON.stringify replacer for grant-permission payloads. Session policy
 * helpers naturally use bigint values, but postMessage payloads must be
 * structured-clone friendly across browsers and JSON-safe for server calls.
 */
function grantPermissionReplacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? value.toString() : value;
}

function cloneGrantPayload<T>(value: T): T {
  return JSON.parse(JSON.stringify(value, grantPermissionReplacer)) as T;
}

function normalizeGrantTargetChains(options: GrantPermissionsOptions): number[] {
  const chainCandidates = options.targetChains ?? (
    options.targetChain !== undefined ? [options.targetChain] : []
  );
  return Array.from(new Set(chainCandidates));
}

function normalizeGrantSourceChains(options: GrantPermissionsOptions): number[] {
  return Array.from(new Set(options.sourceChains ?? []));
}

const sessionForgetHandlers = new WeakMap<OneAuthClient, Set<() => void>>();

/** Register provider-owned cleanup for a client-level forget-session message. */
export function registerSessionForgetHandler(
  client: OneAuthClient,
  handler: () => void,
): () => void {
  const handlers = sessionForgetHandlers.get(client) ?? new Set<() => void>();
  handlers.add(handler);
  sessionForgetHandlers.set(client, handlers);
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) sessionForgetHandlers.delete(client);
  };
}

export class OneAuthClient {
  private config: NormalizedPasskeyProviderConfig;
  private theme: ThemeConfig;
  private sponsorship: SponsorshipCallbackConfig | null;
  // Persistent state for EOA forwarding via `/dialog/sign-eoa`. The iframe
  // is created lazily on the first `requestWithWallet` call and reused for
  // every subsequent call so its WalletConnect SignClient survives between
  // transactions — otherwise each call would spin up a fresh SignClient
  // that restores stale relay state from localStorage, producing
  // "Restore will override" warnings and "emitting session_request:N
  // without any listeners" errors on the second and later txs.
  private eoaDialogState: {
    dialog: HTMLDialogElement;
    iframe: HTMLIFrameElement;
  } | null = null;
  // Hidden iframe that warms the dialog ahead of the first real open (see
  // `prewarm()`). The passkey app runs on a long-lived server (not serverless),
  // so there is no backend cold-start to hide — what stays expensive is the
  // *browser-side* cost of a freshly created cross-origin iframe: downloading
  // the dialog HTML + JS bundle, parsing it, hydrating React, and loading the
  // font. Loading that bundle once into a parked hidden iframe lets the real
  // open hit the HTTP cache (Next.js chunks/fonts are immutable-cached) over an
  // already-warm connection, so the SDK preload overlay is shown only briefly.
  private prewarmState: {
    iframe: HTMLIFrameElement;
    ready: Promise<boolean>;
  } | null = null;
  // Serialises concurrent `requestWithWallet` calls — they would otherwise
  // both try to drive the same iframe and cross-resolve each other.
  private eoaSerialQueue: Promise<unknown> = Promise.resolve();

  /** Clear the default cache plus every provider bound to this client. */
  private forgetApplicationSessions(): void {
    if (typeof window !== "undefined") localStorage.removeItem("1auth-user");
    for (const handler of sessionForgetHandlers.get(this) ?? []) handler();
    try {
      this.config.onDisconnect?.();
    } catch {
      // Host callbacks are isolated from session cleanup so a rendering bug in
      // the integrator cannot leave the SDK/provider caches authenticated.
    }
  }

  /** Forget embedder-side sessions from any trusted dialog transport. */
  private installDisconnectListener(): void {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      typeof window.addEventListener !== "function"
    ) return;
    const dialogOrigin = this.getDialogOrigin();
    window.addEventListener("message", (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.data?.type === "PASSKEY_DISCONNECT") {
        this.forgetApplicationSessions();
      }
    });
  }

  /**
   * Create a new OneAuthClient.
   *
   * Normalizes the config (filling in default URLs), then immediately injects
   * `<link rel="preconnect">` tags for the passkey domain so that DNS lookup
   * and TLS handshake start before the first dialog is opened, shaving
   * noticeable latency from the first user interaction.
   *
   * @param config - Client configuration including optional providerUrl, dialogUrl,
   *   clientId, theme, and redirect settings.
   */
  constructor(config: PasskeyProviderConfig) {
    const providerUrl = config.providerUrl || DEFAULT_PROVIDER_URL;
    const dialogUrl = config.dialogUrl || providerUrl;
    this.config = { ...config, providerUrl, dialogUrl };
    this.theme = this.config.theme || {};
    this.sponsorship = normalizeSponsorship(config.sponsorship);
    this.installDisconnectListener();

    // Pre-warm DNS + TLS connections to passkey domain
    if (typeof document !== "undefined") {
      this.injectPreconnect(providerUrl);
      if (dialogUrl !== providerUrl) {
        this.injectPreconnect(dialogUrl);
      }
      // Opt-in deeper warming: load the dialog bundle into a hidden iframe once
      // the main thread is idle so it never competes with the host app's own
      // first paint. Errors are swallowed — prewarm is best-effort.
      if (this.config.prewarm) {
        const warm = () => void this.prewarm();
        const ric = (window as Window & {
          requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
        }).requestIdleCallback;
        if (typeof ric === "function") {
          ric(warm, { timeout: 2000 });
        } else {
          setTimeout(warm, 200);
        }
      }
    }

    const initOperation = this.createTelemetryOperation("client", {
      hasClientId: !!this.config.clientId,
      hasDialogUrlOverride: dialogUrl !== providerUrl,
    });
    this.emitTelemetry("client.init", initOperation, { outcome: "started" });
  }

  /**
   * Update the sponsorship configuration at runtime. Pass `undefined` to
   * disable sponsorship.
   */
  setSponsorship(sponsorship: SponsorshipConfig | undefined): void {
    this.sponsorship = normalizeSponsorship(sponsorship);
  }

  /**
   * Whether the caller opted into SDK telemetry propagation or callbacks.
   */
  private shouldUseTelemetry(): boolean {
    return !!this.config.telemetry && this.config.telemetry.enabled !== false;
  }

  /**
   * Resolve the host app's current trace context. Callback failures are
   * ignored because observability can never be allowed to break signing.
   */
  private getTelemetryTraceContext(): OneAuthTelemetryTraceContext | undefined {
    const traceContext = this.config.telemetry?.traceContext;
    if (!traceContext || !this.shouldUseTelemetry()) return undefined;
    try {
      return typeof traceContext === "function" ? traceContext() : traceContext;
    } catch {
      return undefined;
    }
  }

  /**
   * Resolve host-provided SDK event attributes and remove undefined fields.
   */
  private getTelemetryAttributes(): OneAuthTelemetryAttributes | undefined {
    const attributes = this.config.telemetry?.attributes;
    if (!attributes || !this.shouldUseTelemetry()) return undefined;
    try {
      const resolved = typeof attributes === "function" ? attributes() : attributes;
      return cleanTelemetryAttributes(resolved);
    } catch {
      return undefined;
    }
  }

  /**
   * Create per-operation telemetry context shared by callbacks, HTTP headers,
   * dialog URL params, and PASSKEY_INIT messages.
   */
  private createTelemetryOperation(
    flow: OneAuthTelemetryFlow,
    attributes?: OneAuthTelemetryAttributes,
  ): TelemetryOperation {
    return {
      operationId: generateTelemetryOperationId(),
      flow,
      startedAt: Date.now(),
      trace: this.getTelemetryTraceContext(),
      attributes: cleanTelemetryAttributes({
        ...this.getTelemetryAttributes(),
        ...attributes,
      }),
    };
  }

  /**
   * Emit a telemetry event to the host app. The callback is intentionally
   * fire-and-forget and exception-safe so app telemetry cannot affect users.
   */
  private emitTelemetry(
    name: OneAuthTelemetryEventName,
    operation: TelemetryOperation,
    patch: TelemetryEventPatch = {},
  ): void {
    const onEvent = this.config.telemetry?.onEvent;
    if (!onEvent || !this.shouldUseTelemetry()) return;

    const attributes = cleanTelemetryAttributes({
      ...operation.attributes,
      ...patch.attributes,
    });
    const event: OneAuthTelemetryEvent = {
      name,
      operationId: operation.operationId,
      flow: operation.flow,
      timestamp: new Date().toISOString(),
      durationMs: patch.durationMs ?? Date.now() - operation.startedAt,
      clientId: this.config.clientId,
      providerUrl: this.config.providerUrl,
      dialogUrl: this.getDialogUrl(),
      trace: operation.trace,
      ...patch,
      attributes,
    };

    try {
      onEvent(event);
    } catch {
      // Telemetry callbacks are intentionally isolated from SDK behavior.
    }
  }

  /**
   * Attach SDK correlation and optional W3C trace context to dialog URLs.
   */
  private appendTelemetryParams(
    params: URLSearchParams,
    operation: TelemetryOperation,
  ): void {
    if (!this.shouldUseTelemetry()) return;
    params.set("sdkOperationId", operation.operationId);
    params.set("sdkTelemetryFlow", operation.flow);
    if (operation.trace?.traceparent) {
      params.set("traceparent", operation.trace.traceparent);
    }
    if (operation.trace?.tracestate) {
      params.set("tracestate", operation.trace.tracestate);
    }
    if (operation.trace?.traceId) {
      params.set("sdkTraceId", operation.trace.traceId);
    }
    if (operation.trace?.spanId) {
      params.set("sdkSpanId", operation.trace.spanId);
    }
  }

  /**
   * Add SDK correlation headers to passkey API calls. These headers are also
   * valid W3C trace propagation inputs when the host app supplies traceparent.
   */
  private telemetryHeaders(operation?: TelemetryOperation): Record<string, string> {
    if (!operation || !this.shouldUseTelemetry()) return {};
    return {
      "x-1auth-sdk-operation-id": operation.operationId,
      "x-1auth-sdk-flow": operation.flow,
      ...(operation.trace?.traceparent ? { traceparent: operation.trace.traceparent } : {}),
      ...(operation.trace?.tracestate ? { tracestate: operation.trace.tracestate } : {}),
    };
  }

  /**
   * Embed telemetry context in PASSKEY_INIT payloads for dialog-side API calls.
   */
  private telemetryPayload(
    operation: TelemetryOperation,
  ): Record<string, string> | undefined {
    if (!this.shouldUseTelemetry()) return undefined;
    return {
      operationId: operation.operationId,
      flow: operation.flow,
      ...(operation.trace?.traceparent ? { traceparent: operation.trace.traceparent } : {}),
      ...(operation.trace?.tracestate ? { tracestate: operation.trace.tracestate } : {}),
      ...(operation.trace?.traceId ? { traceId: operation.trace.traceId } : {}),
      ...(operation.trace?.spanId ? { spanId: operation.trace.spanId } : {}),
    };
  }

  /** Resolve the public funding policy while preserving the legacy user-paid alias. */
  private resolveSponsorshipMode(options: {
    sponsorshipMode?: SponsorshipMode;
    sponsor?: boolean;
  }): SponsorshipMode {
    return options.sponsorshipMode ?? (options.sponsor === false ? "disabled" : "required");
  }

  /**
   * Determine whether this signing call should request invisible dialog mode.
   * Resolution order: per-call option wins over the constructor config, which
   * wins over the platform-wide {@link DEFAULT_BLIND_SIGNING}. This lets an app
   * flip the global default for itself while still overriding individual
   * high-risk calls.
   */
  private shouldRequestBlindSigning(options?: BlindSigningOption): boolean {
    return options?.blind_signing ?? this.config.blind_signing ?? DEFAULT_BLIND_SIGNING;
  }

  /**
   * Fetch the app's access token (JWT). Called up front, before
   * `/api/intent/prepare`, so the passkey server can authenticate the
   * orchestrator quote call with the app's JWT instead of a service-level
   * API key.
   *
   * Does not depend on `intentOp` — can run in parallel with anything that
   * also doesn't depend on the quote.
   */
  private async fetchAccessToken(): Promise<AccessTokenFetchResult> {
    if (!this.sponsorship) {
      return {
        ok: false,
        code: "MISSING_APP_CREDENTIALS",
        message: MISSING_APP_CREDENTIALS_MESSAGE,
      };
    }
    try {
      const accessToken = await this.sponsorship.accessToken();
      return { ok: true, accessToken };
    } catch (err) {
      return {
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  /**
   * Fetch one extension token per canonical sponsorship intent input, aligned
   * with `sponsorFlags`. Sponsored prepare uses the same token for the final
   * quote and later submission, so route construction and execution share one
   * digest-bound authorization.
   */
  private async fetchExtensionTokens(
    sponsorshipIntentInputs: string[],
    sponsorFlags: boolean[],
  ): Promise<ExtensionTokensFetchResult> {
    if (!this.sponsorship) {
      return { ok: false, message: MISSING_APP_CREDENTIALS_MESSAGE };
    }
    const anySponsored = sponsorFlags.some(Boolean);
    if (!anySponsored) return { ok: true, extensionTokens: [] };
    try {
      const sponsorship = this.sponsorship;
      const tokens = await Promise.all(
        sponsorshipIntentInputs.map((input, i) =>
          sponsorFlags[i] ? sponsorship.getExtensionToken(input) : Promise.resolve(""),
        ),
      );
      return { ok: true, extensionTokens: tokens };
    } catch (err) {
      return {
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  /**
   * Update the theme configuration at runtime
   */
  setTheme(theme: ThemeConfig): void {
    this.theme = theme;
  }

  /**
   * Serialize the active theme into URL query parameters for the dialog.
   *
   * Merges the instance-level theme (set via constructor or `setTheme`) with any
   * per-call override, so callers can temporarily change the appearance for a
   * single dialog invocation without mutating shared state.
   *
   * @param overrideTheme - One-shot theme values that take precedence over the
   *   instance theme for this call only.
   * @returns A URL-encoded query string (e.g. `"theme=dark&accent=%230090ff"`),
   *   or an empty string if no theme properties are set.
   */
  private getThemeParams(overrideTheme?: ThemeConfig): string {
    const theme = { ...this.theme, ...overrideTheme };
    const params = new URLSearchParams();

    if (theme.mode) {
      params.set('theme', theme.mode);
    }
    // `primaryColor` is the documented name; `accent` is the legacy alias.
    // Resolve each layer independently before merging so that a per-call
    // override (either field) wins over the instance value (either field) —
    // a plain spread merge would let an instance `primaryColor` shadow a
    // per-call `accent`, breaking the documented "overrides take precedence"
    // contract while `accent` remains a back-compat alias.
    const instancePrimary = this.theme.primaryColor ?? this.theme.accent;
    const overridePrimary = overrideTheme?.primaryColor ?? overrideTheme?.accent;
    const primary = overridePrimary ?? instancePrimary;
    if (primary) {
      params.set('accent', primary);
    }

    // Backdrop (scrim) tint/blur. Each field is emitted only when present so
    // the dialog keeps its per-field defaults (black / 0.4 / 8px) for the
    // common case where the app sets none. The dialog re-validates and clamps
    // each value, so we pass them through verbatim here.
    const backdrop = theme.backdrop;
    if (backdrop) {
      if (backdrop.color) {
        params.set('backdropColor', backdrop.color);
      }
      if (backdrop.opacity !== undefined) {
        params.set('backdropOpacity', String(backdrop.opacity));
      }
      if (backdrop.blur !== undefined) {
        params.set('backdropBlur', String(backdrop.blur));
      }
    }

    return params.toString();
  }

  /**
   * Resolve the URL of the dialog app (the Vite/Next.js frontend that renders
   * the WebAuthn UI inside the iframe).
   *
   * `dialogUrl` is a separate config option to support split deployments where
   * the API server and the dialog frontend run at different origins. Falls back
   * to `providerUrl` when not explicitly configured — the common case.
   *
   * @returns The base URL used when constructing all dialog endpoint paths.
   */
  private getDialogUrl(): string {
    return this.config.dialogUrl || this.config.providerUrl;
  }

  /**
   * Extract the trusted origin used to validate incoming postMessage events.
   *
   * All `window.addEventListener("message", ...)` handlers in this class check
   * `event.origin` against this value to prevent cross-origin spoofing. Wraps
   * `getDialogUrl()` in a `new URL()` parse so that paths and query strings are
   * stripped — only the scheme + host + port are compared.
   *
   * Falls back to the raw dialog URL string if URL parsing fails (e.g. during
   * unit tests with non-standard URL formats).
   *
   * @returns The scheme + host + optional port of the dialog app (e.g. `"https://passkey.1auth.app"`).
   */
  private getDialogOrigin(): string {
    const dialogUrl = this.getDialogUrl();
    try {
      return new URL(dialogUrl).origin;
    } catch {
      return dialogUrl;
    }
  }

  /**
   * Get the base provider URL
   */
  getProviderUrl(): string {
    return this.config.providerUrl;
  }

  /**
   * Get the configured client ID
   */
  getClientId(): string | undefined {
    return this.config.clientId;
  }

  /**
   * Whether this client operates on testnet chains only.
   */
  getTestnets(): boolean {
    return this.config.testnets ?? false;
  }

  /**
   * Get a unified token portfolio for a 1auth account across mainnets and
   * testnets.
   *
   * @example
   * ```typescript
   * const assets = await client.getAssets({
   *   accountAddress: "0x1111111111111111111111111111111111111111",
   * });
   * console.log(assets.mainnets.balances, assets.testnets.balances);
   * ```
   */
  async getAssets(options: GetAssetsOptions): Promise<AssetsResponse> {
    if (!options.accountAddress) {
      throw new Error("getAssets requires accountAddress");
    }

    const telemetry = this.createTelemetryOperation("assets", {
      hasAccountAddress: !!options.accountAddress,
    });

    try {
      const accessTokenResult = await this.fetchAccessToken();
      if (!accessTokenResult.ok) {
        throw new Error(accessTokenResult.message);
      }

      const result = await fetchAssetsResponse({
        providerUrl: this.config.providerUrl,
        identifier: options.accountAddress,
        clientId: this.config.clientId,
        accessToken: accessTokenResult.accessToken,
        headers: this.telemetryHeaders(telemetry),
      });
      this.emitTelemetry("assets.succeeded", telemetry, {
        outcome: "success",
        attributes: {
          balanceCount: result.balances.length,
          mainnetBalanceCount: result.mainnets.balances.length,
          testnetBalanceCount: result.testnets.balances.length,
        },
      });
      return result;
    } catch (error) {
      this.emitTelemetry("assets.failed", telemetry, {
        outcome: "failure",
        ...describeTelemetryError(error),
      });
      throw error;
    }
  }

  /**
   * Poll the intent status endpoint until a transaction hash appears or the
   * intent reaches a terminal failure state.
   *
   * This is used after `sendIntent` resolves (with `waitForHash: true`) to
   * continue waiting for the on-chain hash even after the dialog has been
   * dismissed. It is intentionally separate from the in-dialog polling loop so
   * callers that don't need the hash can skip it entirely.
   *
   * Errors during individual poll attempts are swallowed so that transient
   * network issues don't abort the wait prematurely.
   *
   * @param intentId - The Rhinestone intent ID returned by the execute endpoint.
   * @param options.timeoutMs - Maximum wait time in milliseconds (default 120 000).
   * @param options.intervalMs - Time between polls in milliseconds (default 2 000).
   * @returns The transaction hash string, or `undefined` if the deadline was
   *   reached or the intent failed/expired before a hash was produced.
   */
  private async waitForTransactionHash(
    intentId: string,
    options: { timeoutMs?: number; intervalMs?: number } = {},
    telemetry?: TelemetryOperation,
  ): Promise<string | undefined> {
    const timeoutMs = options.timeoutMs ?? 120_000;
    const intervalMs = options.intervalMs ?? 2_000;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      try {
        const response = await fetch(
          `${this.config.providerUrl}/api/intent/status/${intentId}`,
          {
            headers: {
              ...this.telemetryHeaders(telemetry),
              ...(this.config.clientId ? { "x-client-id": this.config.clientId } : {}),
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.transactionHash) {
            return data.transactionHash as string;
          }
          if (data.status === "failed" || data.status === "expired") {
            return undefined;
          }
        }
      } catch {
        // Keep polling until timeout.
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return undefined;
  }

  /**
   * Open the authentication modal (sign in + sign up).
   *
   * Handles both new user registration and returning user login in a single
   * call — the modal shows the appropriate flow based on whether the user
   * has a passkey registered.
   *
   * @param options.theme - Override the theme for this modal invocation
   * @param options.oauthEnabled - Set to false to hide OAuth sign-in buttons
   * @param options.flow - Force the login or account-creation route instead
   *   of the default auto-detect entry route
   * @returns {@link AuthResult} with user details and typed address
   *
   * @example
   * ```typescript
   * const result = await client.authWithModal();
   *
   * if (result.success) {
   *   console.log('Signed in as:', result.user?.address);
   *   console.log('Address:', result.user?.address); // typed `0x${string}`
   * } else if (result.error?.code === 'USER_CANCELLED') {
   *   // User closed the modal
   * }
   * ```
   */
  async authWithModal(options?: AuthWithModalOptions): Promise<AuthResult> {
    return this.openAuthModal(options);
  }

  /**
   * Open the sign-in modal directly.
   *
   * This bypasses the `/dialog/auth` auto-router and targets the dedicated
   * returning-user flow. Use it when the embedding app already knows the user
   * is creating a login session, for example after an app-level "Log in"
   * button. The result intentionally matches {@link authWithModal}.
   *
   * @param options.theme - Override the theme for this modal invocation
   * @returns {@link AuthResult} with user details and typed address
   */
  async loginWithModal(options?: LoginWithModalOptions): Promise<AuthResult> {
    return this.openAuthModal({ ...options, flow: "login" });
  }

  /**
   * Open the account-creation modal directly.
   *
   * This bypasses the `/dialog/auth` auto-router and targets the dedicated
   * sign-up flow. Account creation still happens server-side in the passkey
   * service; the SDK only opens the dialog and receives the postMessage result.
   *
   * @param options.theme - Override the theme for this modal invocation
   * @param options.oauthEnabled - Set to false to hide OAuth sign-in buttons
   * @returns {@link AuthResult} with the newly authenticated account details
   */
  async createAccountWithModal(options?: CreateAccountWithModalOptions): Promise<AuthResult> {
    return this.openAuthModal({ ...options, flow: "create-account" });
  }

  /**
   * Open one of the passkey authentication modal routes and wait for the
   * login-result message.
   *
   * Keeping URL construction in one helper prevents the explicit login/signup
   * methods from drifting away from the legacy `authWithModal` behavior.
   */
  private async openAuthModal(options?: AuthWithModalOptions): Promise<AuthResult> {
    const telemetry = this.createTelemetryOperation("auth", {
      flow: options?.flow ?? "auto",
    });
    const dialogUrl = this.getDialogUrl();
    const requestId = this.createDialogRequestId();
    const params = new URLSearchParams({
      mode: 'iframe',
      requestId,
    });
    if (this.config.clientId) {
      params.set('clientId', this.config.clientId);
    }
    if (options?.oauthEnabled === false) {
      params.set('oauth', '0');
    }
    if (options?.eoaConnect) {
      // Signals the dialog's wallet path to terminate as a pure EOA signer
      // session (plain tx, no intents) rather than wallet-assisted passkey
      // signup. Read by useSignupFlow and the wallet sub-routes.
      params.set('eoaConnect', '1');
    }
    if (options?.flow === "create-account") {
      // Explicit account creation must not be short-circuited by a stored
      // passkey-origin user; signup routes already treat switch=1 as that
      // "show signup anyway" signal.
      params.set('switch', '1');
    }
    // Seed first-paint attribution. The passkey app re-validates the actual
    // caller from document.referrer / PASSKEY_INIT before trusting it.
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);

    // Add theme params
    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }

    const authPath = this.getAuthDialogPath(options?.flow);
    const url = `${dialogUrl}${authPath}?${params.toString()}`;

    const { dialog, iframe, cleanup } = this.createModalDialog(url);

    this.emitTelemetry("dialog.opened", telemetry, { outcome: "started" });
    const result = await this.waitForModalAuthResponse(dialog, iframe, cleanup, requestId);
    if (result.success) {
      this.emitTelemetry("auth.succeeded", telemetry, {
        outcome: "success",
        attributes: { signerType: result.signerType ?? "passkey" },
      });
    } else {
      this.emitTelemetry(
        result.error?.code === "USER_CANCELLED" ? "dialog.cancelled" : "auth.failed",
        telemetry,
        {
          outcome: result.error?.code === "USER_CANCELLED" ? "cancelled" : "failure",
          ...describeTelemetryError(result.error),
        },
      );
    }
    return result;
  }

  /**
   * Generate a per-dialog nonce used to correlate iframe close messages with
   * the SDK modal that opened them. Chromium can report a different
   * `MessageEvent.source` WindowProxy after the `/dialog/auth` redirect, so
   * auth close handling needs a stable in-band identifier without accepting
   * stale close messages from earlier dialogs.
   */
  private createDialogRequestId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `dialog-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  /**
   * Resolve the passkey app route for a requested auth flow.
   */
  private getAuthDialogPath(flow: AuthFlow = "auto"): string {
    if (flow === "login") return "/dialog/auth/signin";
    if (flow === "create-account") return "/dialog/auth/signup";
    return "/dialog/auth";
  }

  /**
   * Open the connect dialog (lightweight connection without passkey auth).
   *
   * This method shows a simple connection confirmation dialog that doesn't
   * require a passkey signature. Users can optionally enable "auto-connect"
   * to skip this dialog in the future.
   *
   * If the user has never connected before, this will return action: "switch"
   * to indicate that the full auth modal should be opened instead.
   *
   * @example
   * ```typescript
   * const result = await client.connectWithModal();
   *
   * if (result.success) {
   *   console.log('Connected as:', result.user?.address);
   * } else if (result.action === 'switch') {
   *   // User needs to sign in first
   *   const authResult = await client.authWithModal();
   * }
   * ```
   */
  async connectWithModal(options?: {
    theme?: ThemeConfig;
  }): Promise<ConnectResult> {
    const telemetry = this.createTelemetryOperation("connect");
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({
      mode: 'iframe',
    });
    if (this.config.clientId) {
      params.set('clientId', this.config.clientId);
    }
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);

    // Add theme params
    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }

    const url = `${dialogUrl}/dialog/connect?${params.toString()}`;

    const { dialog, iframe, cleanup } = this.createModalDialog(url);
    this.emitTelemetry("dialog.opened", telemetry, { outcome: "started" });

    const ready = await this.waitForDialogReady(dialog, iframe, cleanup, {
      mode: "iframe",
      telemetry: this.telemetryPayload(telemetry),
    });
    if (!ready) {
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_CANCELLED",
        errorMessage: "Connection was cancelled before the dialog became ready",
      });
      return {
        success: false,
        action: "cancel",
        error: { code: "USER_CANCELLED", message: "Connection was cancelled" },
      };
    }
    this.emitTelemetry("dialog.ready", telemetry, { outcome: "started" });

    const result = await this.waitForConnectResponse(dialog, iframe, cleanup);
    if (result.success) {
      this.emitTelemetry("connect.succeeded", telemetry, {
        outcome: "success",
        attributes: {
          autoConnected: result.autoConnected === true,
          signerType: result.signerType ?? "passkey",
        },
      });
    } else {
      this.emitTelemetry(
        result.error?.code === "USER_CANCELLED" ? "dialog.cancelled" : "connect.failed",
        telemetry,
        {
          outcome: result.error?.code === "USER_CANCELLED" ? "cancelled" : "failure",
          ...describeTelemetryError(result.error),
          attributes: { action: result.action },
        },
      );
    }
    return result;
  }

  /**
   * High-level connect entry point — the method most integrators want.
   *
   * Tries the lightweight {@link connectWithModal} first (no passkey ceremony
   * for returning users on the same browser), and transparently falls back to
   * {@link authWithModal} when the connect dialog reports it has no stored
   * credentials for this origin (`action: "switch"`). First-time visitors get
   * one passkey ceremony to register; subsequent connects on the same browser
   * resolve with no biometric prompt — and silently when the user has enabled
   * "Connect automatically".
   *
   * The return shape is the same {@link ConnectResult} discriminated union as
   * {@link connectWithModal}, so callers can treat both paths uniformly. When
   * the auth-modal fallback runs, its successful result is normalized into a
   * `ConnectResult` (no `id` field — the embedder origin must not see it).
   *
   * Cancellation: if the user cancels either dialog, the result is
   * `{ success: false, action: "cancel", error: { code: "USER_CANCELLED", … } }`.
   *
   * @param options - Forwarded to whichever underlying modal is shown.
   *   `oauthEnabled` only applies to the auth-modal fallback path.
   * @returns A {@link ConnectResult} discriminated union.
   *
   * @example
   * ```typescript
   * const result = await client.connect();
   * if (result.success) {
   *   console.log("Connected as", result.user?.address);
   * }
   * ```
   */
  async connect(options?: {
    theme?: ThemeConfig;
    oauthEnabled?: boolean;
    /**
     * Opt into "wallet as signer" mode for the auth-modal fallback — see
     * {@link AuthWithModalOptions.eoaConnect}. Set by {@link createOneAuthConnection}.
     */
    eoaConnect?: boolean;
  }): Promise<ConnectResult> {
    const connectResult = await this.connectWithModal(
      options?.theme ? { theme: options.theme } : undefined,
    );

    // Returning user / auto-connect / explicit confirm — done, no biometrics.
    if (connectResult.success) return connectResult;

    // First-time visitor or cleared storage: connect dialog can't prove the
    // user, so it asks the SDK to switch to the full auth modal.
    if (connectResult.action === "switch") {
      const authResult = await this.authWithModal(options);
      if (authResult.success && authResult.user) {
        return {
          success: true,
          user: {
            address: authResult.user.address,
          },
          // Preserve the signer type from the auth modal — a wallet-picked
          // (EOA) signup must not be silently normalized to "passkey", or the
          // provider would route its transactions through intents.
          signerType: authResult.signerType ?? "passkey",
          autoConnected: false,
        };
      }
      // Auth modal cancelled or errored — surface as a ConnectResult so callers
      // don't have to handle two error shapes.
      return {
        success: false,
        action: "cancel",
        error: authResult.error ?? {
          code: "USER_CANCELLED",
          message: "Connection was cancelled",
        },
      };
    }

    // User cancelled the connect dialog directly — pass through.
    return connectResult;
  }

  /**
   * Open the account management dialog.
   *
   * Shows the account overview with guardian status and recovery setup
   * options. The dialog closes when the user clicks the X button or
   * completes their action.
   *
   * @example
   * ```typescript
   * await client.openAccountDialog();
   * ```
   */
  async openAccountDialog(options?: {
    theme?: ThemeConfig;
  }): Promise<void> {
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({
      mode: 'iframe',
    });

    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }

    const url = `${dialogUrl}/dialog/account?${params.toString()}`;

    // Guardian-setup and session-revoke inside the account dialog submit user
    // intents, which must carry the app's JWT. Fetch it and forward it in the
    // PASSKEY_INIT payload. Best-effort: if sponsorship isn't configured the
    // dialog still opens for read-only / backup actions, and the intent flows
    // surface a clear missing-authorization error rather than a cryptic
    // orchestrator 400.
    //
    // Fetch the token BEFORE creating the iframe: createModalDialog starts
    // loading the dialog, and the listener for its PASSKEY_READY is only
    // installed inside waitForDialogReady. Awaiting a network call between
    // those two would let a fast iframe post READY before the listener exists
    // (→ 10s timeout, dialog never opens). Resolving the token first keeps the
    // create→listen handoff synchronous, as it was originally.
    const accessTokenResult = await this.fetchAccessToken();
    const initMessage: Record<string, unknown> = { mode: "iframe" };
    if (accessTokenResult.ok) {
      initMessage.auth = { accessToken: accessTokenResult.accessToken };
    }

    const { dialog, iframe, cleanup } = this.createModalDialog(url);

    const ready = await this.waitForDialogReady(dialog, iframe, cleanup, initMessage);
    if (!ready) return;

    // Wait for the dialog to close (no result needed)
    const dialogOrigin = this.getDialogOrigin();
    return new Promise<void>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        if (event.data?.type === "PASSKEY_CLOSE" || event.data?.type === "PASSKEY_DISCONNECT") {
          window.removeEventListener("message", handleMessage);
          dialog.removeEventListener("close", handleClose);
          cleanup();
          resolve();
        }
      };
      const handleClose = () => {
        window.removeEventListener("message", handleMessage);
        resolve();
      };
      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Open the account-recovery backup flow directly.
   *
   * Deep-links into the "Backup your account" flow inside the account dialog
   * (rather than the account overview): the user verifies with their passkey,
   * then saves a recovery passphrase and downloads an encrypted backup file.
   * Use this to prompt users to secure their account after sign-up.
   *
   * Requires an authenticated user (call `authWithModal()` / `connect()`
   * first) — the dialog reads the signed-in user from the passkey origin.
   *
   * Resolves `{ completed: true }` when the user finishes the backup, or
   * `{ completed: false }` if they dismiss it — so callers can mark an
   * onboarding step done or re-prompt later.
   */
  async setupRecovery(options?: {
    theme?: ThemeConfig;
  }): Promise<{ completed: boolean }> {
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({
      mode: 'iframe',
      // Deep-link the account dialog straight into the backup nudge instead
      // of the account overview.
      action: 'backup',
    });

    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }

    const url = `${dialogUrl}/dialog/account?${params.toString()}`;

    const { dialog, iframe, cleanup } = this.createModalDialog(url);

    const ready = await this.waitForDialogReady(dialog, iframe, cleanup, {
      mode: "iframe",
    });
    if (!ready) return { completed: false };

    const dialogOrigin = this.getDialogOrigin();
    return new Promise<{ completed: boolean }>((resolve) => {
      let completed = false;
      const finish = () => {
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
        cleanup();
        resolve({ completed });
      };
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        const type = event.data?.type;
        if (type === "PASSKEY_RECOVERY_RESULT") {
          // The dialog reports success right before closing; record it so the
          // subsequent PASSKEY_CLOSE resolves with completed: true.
          completed = Boolean(event.data?.data?.completed ?? event.data?.success);
          return;
        }
        if (type === "PASSKEY_CLOSE" || type === "PASSKEY_DISCONNECT") {
          finish();
        }
      };
      const handleClose = () => {
        window.removeEventListener("message", handleMessage);
        resolve({ completed });
      };
      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Check if a user has already granted consent for the requested fields.
   *
   * @deprecated Data-sharing consent is disabled.
   *
   * Always returns `{ hasConsent: false }`.
   */
  async checkConsent(options: CheckConsentOptions): Promise<CheckConsentResult> {
    void options;
    return { hasConsent: false };
  }

  /**
   * Request consent from the user to share their data.
   *
   * @deprecated Data-sharing consent is disabled.
   *
   * Always returns an `INVALID_REQUEST` result.
   */
  async requestConsent(options: RequestConsentOptions): Promise<RequestConsentResult> {
    void options;
    return {
      success: false,
      error: {
        code: "INVALID_REQUEST",
        message: "Data-sharing consent is disabled",
      },
    };
  }

  /**
   * Open the dedicated SmartSession permission grant dialog.
   *
   * The app owns the session key material and sends only the public
   * `sessionKeyAddress`. 1auth renders the permission review, collects the
   * owner's passkey authorization, submits the install/enable intent, and
   * returns a persistable session handle.
   */
  async grantPermissions(options: GrantPermissionsOptions): Promise<GrantPermissionsResult> {
    const telemetry = this.createTelemetryOperation("grant_permission", {
      targetChains: normalizeGrantTargetChains(options).join(","),
      sponsor: options.sponsor !== false,
    });
    if (!options.accountAddress) {
      this.emitTelemetry("grant_permission.failed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "accountAddress is required",
      });
      return {
        success: false,
        error: {
          code: "INVALID_OPTIONS",
          message: "accountAddress is required",
        },
      };
    }
    const targetChains = normalizeGrantTargetChains(options);
    const sourceChains = normalizeGrantSourceChains(options);
    if (targetChains.length === 0 || !options.sessionKeyAddress || !options.permissions?.length) {
      this.emitTelemetry("grant_permission.failed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "targetChains, sessionKeyAddress, and permissions are required",
      });
      return {
        success: false,
        error: {
          code: "INVALID_OPTIONS",
          message: "targetChains, sessionKeyAddress, and permissions are required",
        },
      };
    }
    if (!this.sponsorship) {
      this.emitTelemetry("grant_permission.failed", telemetry, {
        outcome: "failure",
        errorCode: "MISSING_APP_CREDENTIALS",
        errorMessage: MISSING_APP_CREDENTIALS_MESSAGE,
      });
      return {
        success: false,
        error: {
          code: "MISSING_APP_CREDENTIALS",
          message: MISSING_APP_CREDENTIALS_MESSAGE,
        },
      };
    }

    const accessTokenResult = await this.fetchAccessToken();
    if (!accessTokenResult.ok) {
      this.emitTelemetry("grant_permission.failed", telemetry, {
        outcome: "failure",
        errorCode: "SPONSORSHIP_FETCH_FAILED",
        errorMessage: accessTokenResult.message,
      });
      return {
        success: false,
        error: {
          code: "SPONSORSHIP_FETCH_FAILED",
          message: accessTokenResult.message,
        },
      };
    }

    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams(options.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const grantUrl = `${dialogUrl}/dialog/grant-permission?${params.toString()}`;
    const { dialog, iframe, cleanup } = this.createModalDialog(grantUrl);
    const initPayload = {
      mode: "iframe",
      clientId: this.config.clientId,
      grant: cloneGrantPayload({
        accountAddress: options.accountAddress,
        targetChain: targetChains[0],
        targetChains,
        sourceChains,
        sessionKeyAddress: options.sessionKeyAddress,
        permissions: options.permissions,
        crossChainPermits: options.crossChainPermits,
        validUntil: options.validUntil,
        validAfter: options.validAfter,
        maxUses: options.maxUses,
        contracts: options.contracts,
      }),
      auth: { accessToken: accessTokenResult.accessToken },
      sponsor: options.sponsor !== false,
      telemetry: this.telemetryPayload(telemetry),
    };

    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      targetChains,
    });
    const result = await this.waitForGrantPermissionResponse(
      dialog,
      iframe,
      cleanup,
      options.sponsor !== false,
      initPayload,
    );
    if (result.success) {
      this.emitTelemetry("grant_permission.succeeded", telemetry, {
        outcome: "success",
        targetChains,
        status: result.status ? String(result.status) : undefined,
      });
      // The grant is already resolved; keep the dialog's "Permission granted"
      // success screen (green Done button) on screen until the user dismisses
      // it, then tear the iframe down. Without this the dialog would close the
      // instant the result arrived and the confirmation would never be seen —
      // unlike the sign flow, where sendIntent awaits the dialog close after a
      // confirmed signature.
      await this.waitForDialogClose(dialog, cleanup);
    } else {
      this.emitTelemetry(
        result.error?.code === "USER_CANCELLED" ? "dialog.cancelled" : "grant_permission.failed",
        telemetry,
        {
          outcome: result.error?.code === "USER_CANCELLED" ? "cancelled" : "failure",
          targetChains,
          ...describeTelemetryError(result.error),
        },
      );
    }
    return result;
  }

  /**
   * List SmartSession grants previously granted to the current browser origin.
   *
   * 1auth derives the app identity from the request Origin header. The SDK does
   * not send `clientId` for this lookup because grants are origin-scoped.
   */
  async listSessionGrants(
    options: ListSessionGrantsOptions,
  ): Promise<ListSessionGrantsResult> {
    if (!options.accountAddress) {
      return {
        success: false,
        error: {
          code: "INVALID_OPTIONS",
          message: "accountAddress is required",
        },
      };
    }

    const params = new URLSearchParams();
    if (options.accountAddress) params.set("accountAddress", options.accountAddress);
    if (options.includeRevoked) params.set("includeRevoked", "true");

    try {
      const response = await fetch(
        `${this.config.providerUrl}/api/sessions/grants?${params.toString()}`,
        { credentials: "include" },
      );
      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: {
            code: "LIST_SESSION_GRANTS_FAILED",
            message: data?.error ?? `Request failed with status ${response.status}`,
          },
        };
      }
      return { success: true, grants: data.grants ?? [] };
    } catch (err) {
      return {
        success: false,
        error: {
          code: "LIST_SESSION_GRANTS_FAILED",
          message: err instanceof Error ? err.message : String(err),
        },
      };
    }
  }

  /**
   * Authenticate a user with an optional challenge to sign.
   *
   * This method combines authentication (sign in / sign up) with optional
   * challenge signing, enabling off-chain login without on-chain transactions.
   *
   * When a challenge is provided:
   * 1. User authenticates (sign in or sign up) through the normal auth dialog
   * 2. User signs the challenge through the normal signing dialog
   * 3. Returns user info + signature for server-side verification
   *
   * This composes the same supported provider routes as calling
   * `authWithModal()` followed by `signMessage()`, so challenge auth cannot
   * drift onto a provider route that the passkey service does not expose.
   *
   * @example
   * ```typescript
   * const result = await client.authenticate({
   *   challenge: `Login to MyApp\nTimestamp: ${Date.now()}\nNonce: ${crypto.randomUUID()}`
   * });
   *
   * if (result.success && result.challenge) {
   *   // Verify signature server-side
   *   const isValid = await verifyOnServer(
   *     result.user?.address,
   *     result.challenge.signature,
   *     result.challenge.signedHash
   *   );
   * }
   * ```
   */
  async authenticate(options?: AuthenticateOptions & { theme?: ThemeConfig }): Promise<AuthenticateResult> {
    const telemetry = this.createTelemetryOperation("authenticate", {
      hasChallenge: !!options?.challenge,
    });
    const authResult = await this.authWithModal(options?.theme ? { theme: options.theme } : undefined);
    if (!authResult.success) {
      this.emitTelemetry(
        authResult.error?.code === "USER_CANCELLED" ? "dialog.cancelled" : "auth.failed",
        telemetry,
        {
          outcome: authResult.error?.code === "USER_CANCELLED" ? "cancelled" : "failure",
          ...describeTelemetryError(authResult.error),
        },
      );
      return authResult;
    }

    if (!options?.challenge) {
      this.emitTelemetry("auth.succeeded", telemetry, {
        outcome: "success",
        attributes: { challengeSigned: false },
      });
      return authResult;
    }

    const accountAddress = authResult.user?.address;
    if (!accountAddress) {
      const result: AuthenticateResult = {
        success: false,
        user: authResult.user,
        signerType: authResult.signerType,
        error: {
          code: "AUTHENTICATE_USER_MISSING",
          message: "Authentication succeeded but no account address was returned for challenge signing",
        },
      };
      this.emitTelemetry("auth.failed", telemetry, {
        outcome: "failure",
        ...describeTelemetryError(result.error),
      });
      return result;
    }

    const signResult = await this.signMessage({
      accountAddress,
      message: options.challenge,
      description: "Verify your identity",
      theme: options.theme,
    });

    if (signResult.success && signResult.signature && signResult.signedHash) {
      const result: AuthenticateResult = {
        ...authResult,
        challenge: {
          signature: signResult.signature,
          signedHash: signResult.signedHash,
        },
      };
      this.emitTelemetry("auth.succeeded", telemetry, {
        outcome: "success",
        attributes: { challengeSigned: true },
      });
      return result;
    }

    const error = signResult.error
      ? { code: signResult.error.code, message: signResult.error.message }
      : { code: "SIGNING_FAILED", message: "Challenge signing failed" };
    const result: AuthenticateResult = {
      success: false,
      user: authResult.user,
      signerType: authResult.signerType,
      error,
    };
    const cancelled = error.code === "USER_CANCELLED" || error.code === "USER_REJECTED";
    this.emitTelemetry(
      cancelled ? "dialog.cancelled" : "auth.failed",
      telemetry,
      {
        outcome: cancelled ? "cancelled" : "failure",
        ...describeTelemetryError(error),
      },
    );
    return result;
  }

  /**
   * Show signing in a modal overlay (iframe dialog)
   */
  async signWithModal(options: SigningRequestOptions & { theme?: ThemeConfig }): Promise<SigningResult> {
    const telemetry = this.createTelemetryOperation("sign", {
      signingMode: "transaction",
      hasTransaction: !!options.transaction,
      hasAccountAddress: !!options.accountAddress,
    });
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const signingUrl = `${dialogUrl}/dialog/sign?${params.toString()}`;
    const requestedBlindSigning = this.shouldRequestBlindSigning(options);

    const { dialog, iframe, cleanup, reveal } = this.createModalDialog(signingUrl, {
      hidden: requestedBlindSigning,
    });
    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      attributes: { blindSigning: requestedBlindSigning },
    });

    const ready = await this.waitForDialogReady(dialog, iframe, cleanup, {
      mode: "iframe",
      challenge: options.challenge,
      accountAddress: options.accountAddress,
      description: options.description,
      transaction: options.transaction,
      metadata: options.metadata,
      telemetry: this.telemetryPayload(telemetry),
    }, {
      blindSigning: requestedBlindSigning,
      reveal,
    });
    if (!ready) {
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_REJECTED",
        errorMessage: "User closed the dialog before signing",
      });
      return {
        success: false,
        error: {
          code: "USER_REJECTED",
          message: "User closed the dialog",
        },
      };
    }
    this.emitTelemetry("dialog.ready", telemetry, { outcome: "started" });

    const result = await this.waitForSigningResponse(dialog, iframe, cleanup);
    if (result.success) {
      this.emitTelemetry("sign.succeeded", telemetry, { outcome: "success" });
    } else {
      this.emitTelemetry(
        result.error?.code === "USER_REJECTED" ? "dialog.cancelled" : "sign.failed",
        telemetry,
        {
          outcome: result.error?.code === "USER_REJECTED" ? "cancelled" : "failure",
          ...describeTelemetryError(result.error),
        },
      );
    }
    return result;
  }

  /**
   * Send an intent to the Rhinestone orchestrator
   *
   * This is the high-level method for cross-chain transactions:
   * 1. Prepares the intent (gets quote from orchestrator)
   * 2. Shows the signing modal with real fees
   * 3. Submits the signed intent for execution
   * 4. Returns the transaction hash
   *
   * @example
   * ```typescript
   * const result = await client.sendIntent({
   *   accountAddress: '0x...',
   *   targetChain: 8453, // Base
   *   calls: [
   *     {
   *       to: '0x...',
   *       data: '0x...',
   *       label: 'Swap ETH for USDC',
   *       sublabel: '0.1 ETH → ~250 USDC',
   *     },
   *   ],
   * });
   *
   * if (result.success) {
   *   console.log('Transaction hash:', result.transactionHash);
   * }
   * ```
   */
  async sendIntent(options: SendIntentOptions): Promise<SendIntentResult> {
    const { accountAddress, targetChain } = options;
    // Cap label/sublabel up front so every downstream consumer (prepare
    // request body AND the dialog's phase-1 init payload) sees the same
    // truncated strings — keeps wire data and rendered UI in sync.
    const calls = capCallLabels(options.calls);
    const sponsorshipMode = this.resolveSponsorshipMode(options);
    const sponsor = sponsorshipMode !== "disabled";
    const telemetry = this.createTelemetryOperation("intent", {
      targetChain: targetChain ?? null,
      callsCount: calls?.length ?? 0,
      sponsor,
      sponsorshipMode,
      blindSigning: this.shouldRequestBlindSigning(options),
    });

    if (getStoredSignerType() === "eoa") {
      this.emitTelemetry("intent.completed", telemetry, {
        outcome: "failure",
        errorCode: "E_SIGNER_UNSUPPORTED",
        errorMessage: "Cross-chain intents are not supported for EOA sessions",
      });
      return {
        success: false,
        intentId: "",
        status: "failed",
        error: {
          code: "E_SIGNER_UNSUPPORTED",
          message:
            "Cross-chain intents are not supported for EOA sessions. Use the connected wallet's native RPC instead.",
        },
      };
    }

    if (!accountAddress) {
      this.emitTelemetry("intent.completed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "accountAddress is required",
      });
      return {
        success: false,
        intentId: "",
        status: "failed",
        error: {
          code: "INVALID_OPTIONS",
          message: "accountAddress is required",
        },
      };
    }

    const hasCalls = !!calls?.length;
    const hasTokenRequests = !!options.tokenRequests?.length;
    if (!targetChain || (!hasCalls && !hasTokenRequests)) {
      this.emitTelemetry("intent.completed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "targetChain and calls or tokenRequests are required",
      });
      return {
        success: false,
        intentId: "",
        status: "failed",
        error: {
          code: "INVALID_OPTIONS",
          message: "targetChain and calls or tokenRequests are required",
        },
      };
    }

    if (!this.sponsorship) {
      this.emitTelemetry("intent.prepare.failed", telemetry, {
        outcome: "failure",
        errorCode: "MISSING_APP_CREDENTIALS",
        errorMessage: "No sponsorship configured on OneAuthClient",
      });
      return {
        success: false,
        intentId: "",
        status: "failed",
        error: {
          code: "MISSING_APP_CREDENTIALS",
          message:
            "No sponsorship configured on OneAuthClient. Every user intent must carry the app's JWT — configure `sponsorship` on the client.",
        },
      };
    }

    // Convert bigint amounts to strings for API serialization
    const serializedTokenRequests = options.tokenRequests?.map((r) => ({
      token: r.token,
      amount: r.amount.toString(),
    }));
    let prepareResponse: PrepareIntentResponse;

    // 1. Show dialog immediately + prepare in parallel.
    //
    // Two-phase approach — why:
    //   - The dialog is shown right away so the user sees a UI immediately.
    //   - Access-token minting also runs behind the dialog boot. The prepare
    //     request still needs the token, but the iframe/chunk load does not;
    //     overlapping them removes a cold app-token round trip from the
    //     perceived loading path.
    //   - Intent preparation (API call to the orchestrator for a quote) can
    //     take 1–3 seconds. Hiding the dialog until the quote is ready would
    //     make the interaction feel sluggish.
    //   - Phase 1: send a stable transaction shell with raw calls but ask the
    //     dialog to hold the movement card skeleton until the quote arrives.
    //     Decoded previews are useful in isolation, but here they cause noisy
    //     value churn when quote-normalized token decimals replace them.
    //   - Phase 2: once the quote arrives, send a narrow quote-ready update
    //     instead of replaying the whole PASSKEY_INIT path.
    //
    // Fast path: if preparation finishes before the iframe signals PASSKEY_READY
    // (e.g. on a fast connection), we skip phase 1 entirely and send the full
    // payload in a single PASSKEY_INIT message.
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams();
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const signingUrl = `${dialogUrl}/dialog/sign?${params.toString()}`;
    const requestedBlindSigning = this.shouldRequestBlindSigning(options);
    const { dialog, iframe, cleanup, reveal } = this.createModalDialog(signingUrl, {
      hidden: requestedBlindSigning,
    });
    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      attributes: { blindSigning: requestedBlindSigning },
    });

    const dialogOrigin = this.getDialogOrigin();
    const dialogOpenedAt = Date.now();
    let iframeReadyObserved = false;
    type EarlyDialogClose = { type: "dialog_closed"; ready: boolean };
    let stopEarlyCloseWatch: () => void = () => undefined;
    const earlyDialogClosePromise = new Promise<EarlyDialogClose>((resolve) => {
      let settled = false;
      const stop = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
      };
      stopEarlyCloseWatch = stop;

      const resolveClosed = (ready: boolean) => {
        stop();
        cleanup();
        resolve({ type: "dialog_closed", ready });
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        // Scope the CLOSE to *this* sign dialog's iframe. authWithModal() and
        // sendIntent() share the same dialogOrigin (passkey.1auth.app), so the
        // auth dialog's PASSKEY_CLOSE — emitted async as it tears down on a
        // successful sign-in — can still be in-flight when sendIntent attaches
        // this listener for its brand-new dialog. Without the source check it
        // gets misattributed to the sign dialog and cancels it before it's
        // ready (USER_CANCELLED). Same guard the modal auth listener already
        // applies via event.source === iframe.contentWindow.
        if (
          event.data?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          resolveClosed(iframeReadyObserved);
        }
      };

      const handleClose = () => {
        resolveClosed(iframeReadyObserved);
      };

      // Covers the gap after PASSKEY_READY but before the full signing listener
      // is installed. Without this, closing the loading dialog during a cold
      // access-token fetch drops PASSKEY_CLOSE and lets prepare continue.
      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
    let dialogReadyDurationMs: number | undefined;
    const dialogReadyPromise = this.waitForDialogReadyDeferred(dialog, iframe, cleanup, {
      blindSigning: requestedBlindSigning,
      reveal,
    }).then((result) => {
      dialogReadyDurationMs = Date.now() - dialogOpenedAt;
      iframeReadyObserved = result.ready;
      return result;
    });
    const dialogClosedBeforeReadyPromise = dialogReadyPromise.then((result) =>
      result.ready
        ? new Promise<never>(() => undefined)
        : ({ type: "dialog_closed" as const, ready: false }),
    );

    const accessTokenStartedAt = Date.now();
    let accessTokenDurationMs: number | undefined;
    const accessTokenPromise = this.fetchAccessToken().then((result) => {
      accessTokenDurationMs = Date.now() - accessTokenStartedAt;
      return result;
    });

    const accessOrClose = await Promise.race([
      accessTokenPromise.then((result) => ({ type: "access_token" as const, result })),
      dialogClosedBeforeReadyPromise,
      earlyDialogClosePromise,
    ]);
    if (accessOrClose.type === "dialog_closed") {
      stopEarlyCloseWatch();
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_CANCELLED",
        errorMessage: accessOrClose.ready
          ? "User closed the dialog while intent setup was still loading"
          : "User closed the dialog before it became ready",
        attributes: {
          accessTokenDurationMs,
          dialogReadyDurationMs,
        },
      });
      return {
        success: false,
        intentId: "",
        status: "failed" as const,
        error: { code: "USER_CANCELLED", message: "User closed the dialog" },
      };
    }

    const accessTokenResult = accessOrClose.result;
    // Access-token failures are NOT returned early: they're converted into a
    // synthetic failed PrepareResult below so the dialog's prepare-error
    // retry ("Try Again" → PASSKEY_RETRY_PREPARE) covers them too — the
    // retry path re-mints the token first, which is exactly the recovery a
    // transient token-endpoint failure needs.
    let accessTokenFailure: { code: string; message: string } | null = null;
    if (!accessTokenResult.ok) {
      const errorCode = accessTokenResult.code ?? "SPONSORSHIP_FETCH_FAILED";
      this.emitTelemetry("intent.prepare.failed", telemetry, {
        outcome: "failure",
        errorCode,
        errorMessage: accessTokenResult.message,
        attributes: {
          accessTokenDurationMs,
          dialogReadyDurationMs,
          loadingPhase: "access_token",
        },
      });
      accessTokenFailure = { code: errorCode, message: accessTokenResult.message };
    }
    // Mutable: a dialog-initiated prepare retry re-mints the token (the most
    // common prepare failure is an expired app JWT) and later auth bundles
    // must carry the token the successful prepare actually used.
    let accessToken = accessTokenResult.ok ? accessTokenResult.accessToken : "";

    // Define requestBody outside try block so it's accessible for quote refresh.
    const requestBody = {
      accountAddress,
      targetChain,
      calls,
      tokenRequests: serializedTokenRequests,
      sourceAssets: options.sourceAssets,
      sourceChainId: options.sourceChainId,
      auth: { accessToken },
      ...(this.config.clientId && { clientId: this.config.clientId }),
    };

    // Start prepare in background; track completion via mutable variable.
    // When the iframe becomes ready, earlyPrepareResult will be non-null
    // if prepare finished first (fast path), or null if still in flight.
    type PrepareResult =
      | { success: true; data: PrepareIntentResponse; tier: string | null }
      | { success: false; error: { code: string; message: string; details?: OneAuthErrorDetails } };
    let earlyPrepareResult: PrepareResult | null = null;
    // Sponsored intents draft the canonical quote input, mint a single-use
    // quote grant, prepare the authoritative quote, then mint a distinct
    // single-use execution grant from the returned intentOp.
    // The promise exposed below always contains the execution-time grant.
    let extensionTokensPromise: Promise<ExtensionTokensFetchResult> =
      Promise.resolve({ ok: true, extensionTokens: [] });
    let extensionTokensDurationMs: number | undefined;
    this.emitTelemetry("intent.prepare.started", telemetry, {
      outcome: "started",
      targetChain,
      attributes: {
        accessTokenDurationMs,
        dialogReadyDurationMs,
      },
    });
    const prepareStartedAt = Date.now();
    let prepareDurationMs: number | undefined;
    // Wrapped so dialog-initiated retries re-run the complete draft → grant →
    // final quote handshake with the latest access token.
    const runPrepare = async (): Promise<PrepareResult> => {
      let result: PrepareResult;

      if (sponsorshipMode === "disabled") {
        result = await this.prepareIntent(
          { ...requestBody, quoteMode: "final", sponsorshipMode: "self-funded" },
          telemetry,
        );
      } else {
        const draft = await this.prepareSponsorshipDraft(
          { ...requestBody, sponsorshipMode: "required" },
          telemetry,
        );
        if (!draft.success) {
          result = draft;
        } else {
          const extensionTokensStartedAt = Date.now();
          const quoteGrantResult = await this.fetchExtensionTokens(
            [JSON.stringify(draft.sponsorshipIntentInput)],
            [true],
          );
          extensionTokensDurationMs = Date.now() - extensionTokensStartedAt;
          if (!quoteGrantResult.ok) {
            if (sponsorshipMode === "preferred") {
              extensionTokensPromise = Promise.resolve({ ok: true, extensionTokens: [] });
              result = await this.prepareIntent(
                { ...requestBody, quoteMode: "final", sponsorshipMode: "self-funded" },
                telemetry,
              );
            } else {
              result = {
                success: false,
                error: {
                  code: "SPONSORSHIP_FETCH_FAILED",
                  message: quoteGrantResult.message,
                },
              };
            }
          } else {
            const prepareFinalQuote = (quoteGrant: string) => this.prepareIntent(
              {
                ...requestBody,
                quoteMode: "final",
                sponsorshipMode: "required",
                auth: {
                  accessToken,
                  extensionToken: quoteGrant,
                },
              },
              telemetry,
            );
            let quoteIsSponsored = true;
            result = await prepareFinalQuote(quoteGrantResult.extensionTokens[0]);
            if (!result.success) {
              // User-service grants are single-use. If an upstream retry or
              // duplicate verification consumes the JTI, transparently mint
              // one replacement for the identical canonical quote input.
              const replacementQuoteGrant = await this.fetchExtensionTokens(
                [JSON.stringify(draft.sponsorshipIntentInput)],
                [true],
              );
              if (replacementQuoteGrant.ok) {
                result = await prepareFinalQuote(replacementQuoteGrant.extensionTokens[0]);
              }
            }
            if (!result.success && sponsorshipMode === "preferred") {
              // A valid grant does not guarantee the sponsor will accept the
              // authoritative quote. Preferred mode must preserve its public
              // contract by obtaining a fresh, explicitly self-funded quote.
              extensionTokensPromise = Promise.resolve({ ok: true, extensionTokens: [] });
              quoteIsSponsored = false;
              result = await this.prepareIntent(
                { ...requestBody, quoteMode: "final", sponsorshipMode: "self-funded" },
                telemetry,
              );
            }
            if (result.success && quoteIsSponsored) {
              const executionGrantResult = await this.fetchExtensionTokens(
                [result.data.intentOp],
                [true],
              );
              extensionTokensDurationMs = Date.now() - extensionTokensStartedAt;
              if (!executionGrantResult.ok) {
                if (sponsorshipMode === "preferred") {
                  extensionTokensPromise = Promise.resolve({ ok: true, extensionTokens: [] });
                  quoteIsSponsored = false;
                  result = await this.prepareIntent(
                    { ...requestBody, quoteMode: "final", sponsorshipMode: "self-funded" },
                    telemetry,
                  );
                } else {
                  result = {
                    success: false,
                    error: {
                      code: "SPONSORSHIP_FETCH_FAILED",
                      message: executionGrantResult.message,
                    },
                  };
                }
              } else {
                extensionTokensPromise = Promise.resolve(executionGrantResult);
              }
            }
          }
        }
      }

      earlyPrepareResult = result;
      prepareDurationMs = Date.now() - prepareStartedAt;
      this.emitTelemetry(
        result.success ? "intent.prepare.succeeded" : "intent.prepare.failed",
        telemetry,
        result.success
          ? {
              outcome: "success",
              targetChain,
              status: "quoted",
              attributes: { accessTokenDurationMs, dialogReadyDurationMs, prepareDurationMs },
            }
          : {
              outcome: "failure",
              targetChain,
              ...describeTelemetryError(result.error),
              attributes: {
                accessTokenDurationMs,
                dialogReadyDurationMs,
                prepareDurationMs,
                loadingPhase: "prepare",
              },
            },
      );
      return result;
    };
    // When the access token never materialised, seed the flow with a failed
    // result instead of calling /prepare with empty auth — the fast/
    // progressive retry loops below surface it in the dialog and the user's
    // retry re-mints the token before re-running prepare.
    const preparePromise = accessTokenFailure
      ? Promise.resolve<PrepareResult>({
          success: false,
          error: accessTokenFailure,
        }).then((r) => {
          earlyPrepareResult = r;
          return r;
        })
      : runPrepare();

    // Re-run prepare after the dialog requested a retry. Re-mints the app
    // access token first: the dominant cause of prepare failures is the app
    // JWT expiring (orchestrator 401), so retrying with the original token
    // would fail forever. Returns the new PrepareResult — failures feed back
    // into the caller's retry loop.
    const retryPrepare = async (): Promise<PrepareResult> => {
      const retryTokenResult = await this.fetchAccessToken();
      if (!retryTokenResult.ok) {
        return {
          success: false,
          error: {
            code: retryTokenResult.code ?? "SPONSORSHIP_FETCH_FAILED",
            message: retryTokenResult.message,
          },
        };
      }
      accessToken = retryTokenResult.accessToken;
      requestBody.auth = { accessToken };
      extensionTokensPromise = Promise.resolve({ ok: true, extensionTokens: [] });
      return runPrepare();
    };

    // Wait for iframe to be ready
    const readyOrClose = await Promise.race([
      dialogReadyPromise.then((result) => ({ type: "ready" as const, result })),
      earlyDialogClosePromise,
    ]);
    if (readyOrClose.type === "dialog_closed") {
      stopEarlyCloseWatch();
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_CANCELLED",
        errorMessage: readyOrClose.ready
          ? "User closed the dialog while intent setup was still loading"
          : "User closed the dialog before it became ready",
        attributes: {
          accessTokenDurationMs,
          dialogReadyDurationMs,
          prepareDurationMs,
        },
      });
      return {
        success: false,
        intentId: "",
        status: "failed" as const,
        error: { code: "USER_CANCELLED", message: "User closed the dialog" },
      };
    }
    const dialogResult = readyOrClose.result;

    // Handle dialog closed before ready
    if (!dialogResult.ready) {
      stopEarlyCloseWatch();
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_CANCELLED",
        errorMessage: "User closed the dialog before it became ready",
      });
      return {
        success: false,
        intentId: "",
        status: "failed" as const,
        error: { code: "USER_CANCELLED", message: "User closed the dialog" },
      };
    }
    const blindSigning = dialogResult.blindSigning;
    this.emitTelemetry("dialog.ready", telemetry, {
      outcome: "started",
      attributes: {
        accessTokenDurationMs,
        dialogReadyDurationMs,
      },
    });

    // Mutable init payload — starts as early preview, upgraded to full after quote
    let currentInitPayload: Record<string, unknown>;
    const refreshQuote = async () => {
      try {
        // Re-mint the app access token for the refreshed prepare. Quote
        // refreshes happen when the user lingers on the review screen, so
        // the token captured in `requestBody` may be close to (or past) its
        // 1h expiry — the orchestrator would reject it with a 401 and the
        // refresh would fail even though the quote itself is refreshable.
        const refreshedTokenResult = await this.fetchAccessToken();
        const refreshToken = refreshedTokenResult.ok
          ? refreshedTokenResult.accessToken
          : accessToken;
        accessToken = refreshToken;
        requestBody.auth = { accessToken: refreshToken };
        extensionTokensPromise = Promise.resolve({ ok: true, extensionTokens: [] });
        const refreshedResult = await runPrepare();
        if (!refreshedResult.success) {
          console.error("[SDK] Quote refresh failed:", refreshedResult.error.message);
          return null;
        }
        const refreshedData = refreshedResult.data;
        const extensionResult = await extensionTokensPromise;
        if (!extensionResult.ok) {
          console.error("[SDK] Extension token refresh failed:", extensionResult.message);
          return null;
        }
        const refreshedAuth: SponsorshipTokens = {
          accessToken: refreshToken,
          extensionTokens: extensionResult.extensionTokens,
        };

        // Keep execute (legacy SDK-submit path) aligned with the latest quote
        // the dialog is reviewing — token, grant, and quote move together.
        prepareResponse = refreshedData;
        extensionTokensPromise = Promise.resolve(extensionResult);
        // Keep iframe-remount replays (PASSKEY_READY → PASSKEY_INIT) on the
        // refreshed quote too.
        currentInitPayload = {
          ...currentInitPayload,
          transaction: refreshedData.transaction,
          challenge: refreshedData.challenge,
          originMessages: refreshedData.originMessages,
          expiresAt: refreshedData.expiresAt,
          intentOp: refreshedData.intentOp,
          digestResult: refreshedData.digestResult,
          // A refreshed quote mints a fresh binding — it MUST replace the old
          // one, or execute would verify the new intentOp against a stale
          // binding and reject (once enforce mode is on).
          binding: refreshedData.binding,
          auth: refreshedAuth,
        };
        return {
          intentOp: refreshedData.intentOp,
          expiresAt: refreshedData.expiresAt,
          challenge: refreshedData.challenge,
          originMessages: refreshedData.originMessages?.map((message) => ({
            chainId: message.chainId,
            hash: message.messageHash,
          })),
          transaction: refreshedData.transaction,
          digestResult: refreshedData.digestResult,
          binding: refreshedData.binding,
          // Dialog-executed intents read auth from dialog state — ship the
          // rebound bundle with the refresh so PASSKEY_REFRESH_COMPLETE
          // updates it alongside the quote.
          auth: refreshedAuth,
        };
      } catch (error) {
        console.error("[SDK] Quote refresh error:", error);
        return null;
      }
    };
    // Install the close/result listener before the progressive preview init is
    // sent. Otherwise a user can click Cancel while /prepare is still waiting
    // on quote or portfolio work, and the parent page drops the message.
    const signingResultPromise = this.waitForSigningWithRefresh(
      dialog,
      iframe,
      cleanup,
      dialogOrigin,
      refreshQuote,
    );
    stopEarlyCloseWatch();

    if (earlyPrepareResult) {
      // Fast path: prepare already finished — send full PASSKEY_INIT (no regression)
      // Type assertion needed: TS control flow doesn't track .then() mutations
      let prepareResult = earlyPrepareResult as PrepareResult;
      // Same retry contract as the progressive path below: any failure before
      // the full payload reaches the dialog surfaces as PASSKEY_PREPARE_ERROR,
      // and the dialog's "Try Again" re-enters via PASSKEY_RETRY_PREPARE.
      for (;;) {
        while (!prepareResult.success) {
          this.sendPrepareError(iframe, prepareResult.error.message);
          if (blindSigning) {
            // No visible dialog to host the error/retry UI — fail immediately.
            cleanup();
            return {
              success: false,
              intentId: "",
              status: "failed",
              error: prepareResult.error,
            };
          }
          const next = await this.waitForPrepareRetryOrClose(dialog, cleanup);
          if (next === "closed") {
            return {
              success: false,
              intentId: "",
              status: "failed",
              error: prepareResult.error,
            };
          }
          prepareResult = await retryPrepare();
        }
        prepareResponse = prepareResult.data;
        const extensionResult = await extensionTokensPromise;
        if (extensionResult.ok) {
          const sponsorshipTokens: SponsorshipTokens = {
            accessToken,
            extensionTokens: extensionResult.extensionTokens,
          };
          currentInitPayload = {
            mode: "iframe",
            calls,
            chainId: targetChain,
            transaction: prepareResponse.transaction,
            challenge: prepareResponse.challenge,
            accountAddress: prepareResponse.accountAddress,
            originMessages: prepareResponse.originMessages,
            tokenRequests: serializedTokenRequests,
            expiresAt: prepareResponse.expiresAt,
            userId: prepareResponse.userId,
            intentOp: prepareResponse.intentOp,
            digestResult: prepareResponse.digestResult,
            binding: prepareResponse.binding,
            tier: prepareResult.tier,
            auth: sponsorshipTokens,
            telemetry: this.telemetryPayload(telemetry),
          };
          dialogResult.sendInit(currentInitPayload);
          break;
        }
        this.emitTelemetry("intent.prepare.failed", telemetry, {
          outcome: "failure",
          errorCode: "SPONSORSHIP_FETCH_FAILED",
          errorMessage: extensionResult.message,
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
            loadingPhase: "extension_token",
          },
        });
        this.sendPrepareError(iframe, extensionResult.message);
        if (blindSigning) {
          cleanup();
          return {
            success: false,
            intentId: "",
            status: "failed",
            error: { code: "SPONSORSHIP_FETCH_FAILED", message: extensionResult.message },
          };
        }
        const next = await this.waitForPrepareRetryOrClose(dialog, cleanup);
        if (next === "closed") {
          return {
            success: false,
            intentId: "",
            status: "failed",
            error: { code: "SPONSORSHIP_FETCH_FAILED", message: extensionResult.message },
          };
        }
        // Retrying just the extension token would be unsafe — grants are
        // digest-bound to the intentOp — so re-run the full prepare.
        prepareResult = await retryPrepare();
      }
    } else {
      // Progressive path: send the raw calls up front so the dialog can
      // decode them locally (/api/decode) and reveal a fully-formed
      // transaction card the moment decode settles — without waiting on the
      // orchestrator quote. The quote follows via PASSKEY_QUOTE_READY below
      // and upgrades the fee row + any routed amounts in place. The dialog
      // gates the Sign button on that authoritative quote, so revealing the
      // decoded preview early never lets the user sign un-quoted data.
      currentInitPayload = {
        mode: "iframe",
        signingMode: "transaction",
        calls,
        chainId: targetChain,
        accountAddress: options.accountAddress,
        tokenRequests: serializedTokenRequests,
        telemetry: this.telemetryPayload(telemetry),
      };
      dialogResult.sendInit(currentInitPayload);

      // Wait for prepare to complete, then send quote data
      const prepareOrCancel = await Promise.race([
        preparePromise.then((prepareResult) => ({
          type: "prepare" as const,
          prepareResult,
        })),
        signingResultPromise.then((signingResult) => ({
          type: "signing" as const,
          signingResult,
        })),
      ]);
      let prepareResult: PrepareResult;
      if (prepareOrCancel.type === "signing") {
        const earlySigningResult = prepareOrCancel.signingResult;
        if (earlySigningResult.success) {
          // Signing cannot complete before the prepared challenge arrives, but
          // keep this branch total so TypeScript and future hidden flows have a
          // deterministic path to the quote data needed for execute.
          prepareResult = await preparePromise;
        } else {
          const signingError = earlySigningResult.error;
          this.emitTelemetry(
            signingError?.code === "USER_REJECTED"
              ? "dialog.cancelled"
              : "intent.sign.failed",
            telemetry,
            {
              outcome: signingError?.code === "USER_REJECTED" ? "cancelled" : "failure",
              ...describeTelemetryError(signingError),
            },
          );
          if (blindSigning) cleanup();
          return {
            success: false,
            intentId: "",
            status: "failed",
            error: signingError,
          };
        }
      } else {
        prepareResult = prepareOrCancel.prepareResult;
      }
      // Drive prepare → quote-ready → extension-token to completion, looping
      // back through the dialog's "Try Again" (PASSKEY_RETRY_PREPARE) on any
      // failure along the way. Each retry re-runs the FULL prepare so the
      // quote and the per-intent sponsorship grant always match — a grant is
      // digest-bound to its intentOp, so retrying just the extension token
      // against a re-quoted intent would produce an unusable auth bundle.
      for (;;) {
        while (!prepareResult.success) {
          this.sendPrepareError(iframe, prepareResult.error.message);
          if (blindSigning) {
            // No visible dialog to host the error/retry UI — fail immediately.
            cleanup();
            return {
              success: false,
              intentId: "",
              status: "failed",
              error: prepareResult.error,
            };
          }
          const next = await this.waitForPrepareRetryOrClose(dialog, cleanup);
          if (next === "closed") {
            return {
              success: false,
              intentId: "",
              status: "failed",
              error: prepareResult.error,
            };
          }
          prepareResult = await retryPrepare();
        }
        prepareResponse = prepareResult.data;
        const authReady =
          !sponsor
            ? ({
                accessToken,
                extensionTokens: [],
              } satisfies SponsorshipTokens)
            : undefined;

        // Upgrade the init payload with full quote data. For sponsored intents,
        // don't wait for the extension token here: review display needs the quote,
        // while submit auth can arrive as a small follow-up before Sign unlocks.
        currentInitPayload = {
          mode: "iframe",
          calls,
          chainId: targetChain,
          transaction: prepareResponse.transaction,
          challenge: prepareResponse.challenge,
          accountAddress: prepareResponse.accountAddress,
          originMessages: prepareResponse.originMessages,
          tokenRequests: serializedTokenRequests,
          expiresAt: prepareResponse.expiresAt,
          userId: prepareResponse.userId,
          intentOp: prepareResponse.intentOp,
          digestResult: prepareResponse.digestResult,
          binding: prepareResponse.binding,
          tier: prepareResult.tier,
          ...(authReady && { auth: authReady }),
          telemetry: this.telemetryPayload(telemetry),
        };

        // Narrow update: avoid replaying PASSKEY_INIT, which would rerun broad
        // dialog initialization and briefly replace the skeleton/preview state
        // before the authoritative quote-backed transaction lands.
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_QUOTE_READY", ...currentInitPayload, fullViewport: true, blindSigning },
          dialogOrigin,
        );

        if (!sponsor) break;

        const extensionResult = await extensionTokensPromise;
        if (extensionResult.ok) {
          const sponsorshipTokens: SponsorshipTokens = {
            accessToken,
            extensionTokens: extensionResult.extensionTokens,
          };
          currentInitPayload = {
            ...currentInitPayload,
            auth: sponsorshipTokens,
          };
          iframe.contentWindow?.postMessage(
            { type: "PASSKEY_QUOTE_READY", auth: sponsorshipTokens, fullViewport: true, blindSigning },
            dialogOrigin,
          );
          break;
        }

        this.emitTelemetry("intent.prepare.failed", telemetry, {
          outcome: "failure",
          errorCode: "SPONSORSHIP_FETCH_FAILED",
          errorMessage: extensionResult.message,
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
            loadingPhase: "extension_token",
          },
        });
        this.sendPrepareError(iframe, extensionResult.message);
        if (blindSigning) {
          cleanup();
          return {
            success: false,
            intentId: "",
            status: "failed",
            error: { code: "SPONSORSHIP_FETCH_FAILED", message: extensionResult.message },
          };
        }
        const next = await this.waitForPrepareRetryOrClose(dialog, cleanup);
        if (next === "closed") {
          return {
            success: false,
            intentId: "",
            status: "failed",
            error: { code: "SPONSORSHIP_FETCH_FAILED", message: extensionResult.message },
          };
        }
        prepareResult = await retryPrepare();
      }
    }

    // 2. Handle iframe remount: if the dialog re-sends PASSKEY_READY (e.g. due to
    // React strict mode, Next.js Suspense, or code-splitting), resend PASSKEY_INIT
    // so the sign page recovers its state. Uses currentInitPayload which is the
    // full payload after quote arrives.
    const handleReReady = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.data?.type === "PASSKEY_READY") {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...currentInitPayload, fullViewport: true, blindSigning },
          dialogOrigin,
        );
      }
    };
    window.addEventListener("message", handleReReady);

    // 3. Wait for signing result with auto-refresh support
    // This custom handler handles both signing results AND quote refresh requests.
    const signingResult = await signingResultPromise;

    window.removeEventListener("message", handleReReady);

    if (!signingResult.success) {
      this.emitTelemetry(
        signingResult.error?.code === "USER_REJECTED" ? "dialog.cancelled" : "intent.sign.failed",
        telemetry,
        {
          outcome: signingResult.error?.code === "USER_REJECTED" ? "cancelled" : "failure",
          ...describeTelemetryError(signingResult.error),
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
          },
        },
      );
      if (blindSigning) cleanup();
      return {
        success: false,
        intentId: "", // No intentId yet - signing was cancelled before execute
        status: "failed",
        error: signingResult.error,
      };
    }

    // Check if dialog already executed the intent (new secure flow)
    // In this case, signingResult contains intentId instead of signature
    const dialogExecutedIntent = "intentId" in signingResult && signingResult.intentId;

    // 5. Execute intent with signature (skip if dialog already executed)
    let executeResponse: ExecuteIntentResponse;

    if (dialogExecutedIntent) {
      // Dialog already executed - use the returned intentId
      executeResponse = {
        success: true,
        intentId: signingResult.intentId as string,
        status: "pending",
      };
      this.emitTelemetry("intent.sign.succeeded", telemetry, {
        outcome: "success",
        attributes: {
          accessTokenDurationMs,
          dialogReadyDurationMs,
          prepareDurationMs,
          extensionTokensDurationMs,
          dialogExecuted: true,
        },
      });
    } else {
      // Legacy flow - execute with signature from dialog. The extension
      // token was already fetched in parallel with signing above, so this
      // just awaits the (usually already-resolved) promise. `accessToken` was
      // fetched before /prepare, overlapped with the dialog iframe boot.
      const extensionResult = await extensionTokensPromise;
      if (!extensionResult.ok) {
        this.emitTelemetry("intent.sign.failed", telemetry, {
          outcome: "failure",
          errorCode: "SPONSORSHIP_FETCH_FAILED",
          errorMessage: extensionResult.message,
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
            loadingPhase: "extension_token",
          },
        });
        this.sendTransactionStatus(iframe, "failed");
        await this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
        return {
          success: false,
          intentId: "",
          status: "failed",
          error: { code: "SPONSORSHIP_FETCH_FAILED", message: extensionResult.message },
        };
      }
      const extensionTokens = extensionResult.extensionTokens;
      try {
        this.emitTelemetry("intent.sign.succeeded", telemetry, {
          outcome: "success",
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
            dialogExecuted: false,
          },
        });
        this.emitTelemetry("intent.execute.started", telemetry, {
          outcome: "started",
          targetChain: prepareResponse.targetChain,
          attributes: {
            accessTokenDurationMs,
            dialogReadyDurationMs,
            prepareDurationMs,
            extensionTokensDurationMs,
          },
        });
        const response = await fetch(`${this.config.providerUrl}/api/intent/execute`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...this.telemetryHeaders(telemetry),
          },
          body: JSON.stringify({
            // Data from prepare response (no intentId yet - created on execute)
            intentOp: prepareResponse.intentOp,
            userId: prepareResponse.userId,
            targetChain: prepareResponse.targetChain,
            calls: prepareResponse.calls,
            expiresAt: prepareResponse.expiresAt,
            digestResult: prepareResponse.digestResult,
            // Opaque prepare->execute binding, forwarded unchanged.
            binding: prepareResponse.binding,
            // Signature from dialog
            signature: signingResult.signature,
            passkey: requireSigningPasskeyCredentials(signingResult.passkey),
            // App JWT — `accessToken` is always present. `extensionToken` is
            // only included when sponsorship was requested (sponsor !== false).
            auth: {
              accessToken,
              ...(extensionTokens.length > 0 && {
                extensionToken: extensionTokens[0],
              }),
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Send failure status to dialog
          this.sendTransactionStatus(iframe, "failed");
          await this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
          this.emitTelemetry("intent.execute.failed", telemetry, {
            outcome: "failure",
            targetChain: prepareResponse.targetChain,
            errorCode: executeErrorCode(errorData),
            errorMessage: errorData.error || "Failed to execute intent",
          });
          return {
            success: false,
            intentId: "", // No intentId - execute failed before creation
            status: "failed",
            error: {
              code: executeErrorCode(errorData),
              message: errorData.error || "Failed to execute intent",
              details: executeErrorDetails(errorData.debug),
            },
          };
        }

        executeResponse = await response.json();
        this.emitTelemetry("intent.execute.succeeded", telemetry, {
          outcome: "success",
          targetChain: prepareResponse.targetChain,
          status: executeResponse.status,
        });
      } catch (error) {
        // Send failure status to dialog
        this.sendTransactionStatus(iframe, "failed");
        await this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
        this.emitTelemetry("intent.execute.failed", telemetry, {
          outcome: "failure",
          targetChain: prepareResponse.targetChain,
          ...describeTelemetryError(error),
        });
        return {
          success: false,
          intentId: "", // No intentId - network error before creation
          status: "failed",
          error: {
            code: "NETWORK_ERROR",
            message: error instanceof Error ? error.message : "Network error",
          },
        };
      }
    }

    // 6. Poll for completion with status updates to dialog
    let finalStatus = executeResponse.status;
    let finalTxHash = executeResponse.transactionHash;

    if (finalStatus === "pending") {
      // Send initial pending status to dialog
      this.sendTransactionStatus(iframe, "pending");
      this.emitTelemetry("intent.status.started", telemetry, {
        outcome: "started",
        status: finalStatus,
      });

      // Listen for early close (user clicking X) during polling.
      // Close the dialog immediately so the user gets instant feedback.
      let userClosedEarly = false;
      const dialogOrigin = this.getDialogOrigin();
      const earlyCloseHandler = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        // Scope to this dialog's iframe so a same-origin CLOSE from another
        // 1auth dialog (e.g. a queued auth-modal teardown) can't be
        // misattributed to this poll and cancel it. Mirrors the early-close
        // watcher above and the modal auth listener's source guard.
        if (
          event.data?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          userClosedEarly = true;
          cleanup();
        }
      };
      window.addEventListener("message", earlyCloseHandler);

      // Poll status endpoint for updates
      const maxAttempts = 120; // 3 minutes at 1.5s intervals
      const pollIntervalMs = 1500;
      let lastStatus = "pending";

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (userClosedEarly) break;

        try {
          const statusResponse = await fetch(
            `${this.config.providerUrl}/api/intent/status/${executeResponse.intentId}`,
            {
              method: "GET",
              headers: {
                ...this.telemetryHeaders(telemetry),
                ...(this.config.clientId ? { "x-client-id": this.config.clientId } : {}),
              },
            }
          );

          if (statusResponse.ok) {
            const statusResult = await statusResponse.json();
            finalStatus = statusResult.status;
            finalTxHash = statusResult.transactionHash;

            // Send status update to dialog if changed
            if (finalStatus !== lastStatus) {
              this.sendTransactionStatus(iframe, finalStatus, finalTxHash);
              lastStatus = finalStatus;
            }

            // Exit if terminal status reached
            // closeOn determines when to consider the intent successful (default: preconfirmed)
            const closeOn = options.closeOn || "preconfirmed";
            const successStatuses: Record<string, string[]> = {
              claimed: ["claimed", "preconfirmed", "filled", "completed"],
              preconfirmed: ["preconfirmed", "filled", "completed"],
              filled: ["filled", "completed"],
              completed: ["completed"],
            };
            const isTerminal = finalStatus === "failed" || finalStatus === "expired";
            const isSuccess = successStatuses[closeOn]?.includes(finalStatus) ?? false;
            if (isTerminal || isSuccess) {
              break;
            }
          }
        } catch (pollError) {
          console.error("Failed to poll intent status:", pollError);
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }

      window.removeEventListener("message", earlyCloseHandler);

      // If user closed during polling, clean up and return current status
      if (userClosedEarly) {
        cleanup();
        this.emitTelemetry("intent.status.failed", telemetry, {
          outcome: "cancelled",
          status: finalStatus,
          errorCode: "USER_CANCELLED",
          errorMessage: "User closed the dialog while status polling",
        });
        return {
          success: false,
          intentId: executeResponse.intentId,
          status: finalStatus,
          transactionHash: finalTxHash,
          operationId: executeResponse.operationId,
          error: {
            code: "USER_CANCELLED",
            message: "User closed the dialog",
          },
        };
      }
    }

    // 7. Send final status to dialog
    // Map successful statuses to "confirmed" based on closeOn setting
    const closeOn = options.closeOn || "preconfirmed";
    const successStatuses: Record<string, string[]> = {
      claimed: ["claimed", "preconfirmed", "filled", "completed"],
      preconfirmed: ["preconfirmed", "filled", "completed"],
      filled: ["filled", "completed"],
      completed: ["completed"],
    };
    const isSuccessStatus = successStatuses[closeOn]?.includes(finalStatus) ?? false;
    const displayStatus = isSuccessStatus ? "confirmed" : finalStatus;

    // Start listening for close BEFORE sending status to avoid race condition
    // where user clicks Done before the listener is registered
    const closePromise = this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
    this.sendTransactionStatus(iframe, displayStatus, finalTxHash);

    // Wait for dialog to be closed by user
    await closePromise;

    if (options.waitForHash && !finalTxHash) {
      const hash = await this.waitForTransactionHash(executeResponse.intentId, {
        timeoutMs: options.hashTimeoutMs,
        intervalMs: options.hashIntervalMs,
      }, telemetry);
      if (hash) {
        finalTxHash = hash;
        finalStatus = "completed";
      } else {
        finalStatus = "failed";
        this.emitTelemetry("intent.status.failed", telemetry, {
          outcome: "failure",
          status: finalStatus,
          errorCode: "HASH_TIMEOUT",
          errorMessage: "Timed out waiting for transaction hash",
        });
        return {
          success: false,
          intentId: executeResponse.intentId,
          status: finalStatus,
          transactionHash: finalTxHash,
          operationId: executeResponse.operationId,
          error: {
            code: "HASH_TIMEOUT",
            message: "Timed out waiting for transaction hash",
          },
        };
      }
    }

    this.emitTelemetry("intent.status.succeeded", telemetry, {
      outcome: isSuccessStatus ? "success" : "failure",
      status: finalStatus,
    });
    this.emitTelemetry("intent.completed", telemetry, {
      outcome: isSuccessStatus ? "success" : "failure",
      status: finalStatus,
      targetChain,
    });

    return {
      success: isSuccessStatus,
      intentId: executeResponse.intentId,
      status: finalStatus,
      transactionHash: finalTxHash,
      operationId: executeResponse.operationId,
      error: executeResponse.error,
    };
  }

  /**
   * Send a batch of intents for multi-chain execution with a single passkey tap.
   *
   * This method prepares multiple intents, shows a paginated review,
   * and signs all intents with a single passkey tap via a shared merkle tree.
   *
   * @example
   * ```typescript
   * const result = await client.sendBatchIntent({
   *   accountAddress: '0x...',
   *   intents: [
   *     {
   *       targetChain: 8453, // Base
   *       calls: [{ to: '0x...', data: '0x...', label: 'Swap on Base' }],
   *     },
   *     {
   *       targetChain: 42161, // Arbitrum
   *       calls: [{ to: '0x...', data: '0x...', label: 'Mint on Arbitrum' }],
   *     },
   *   ],
   * });
   *
   * if (result.success) {
   *   console.log(`${result.successCount} intents submitted`);
   * }
   * ```
   */
  async sendBatchIntent(options: SendBatchIntentOptions): Promise<SendBatchIntentResult> {
    const telemetry = this.createTelemetryOperation("batch_intent", {
      intentCount: options.intents?.length ?? 0,
      blindSigning: this.shouldRequestBlindSigning(options),
    });
    if (getStoredSignerType() === "eoa") {
      this.emitTelemetry("batch.completed", telemetry, {
        outcome: "failure",
        errorCode: "E_SIGNER_UNSUPPORTED",
        errorMessage: "Batch intents are not supported for EOA sessions",
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
        error:
          "Batch intents are not supported for EOA sessions. Use the connected wallet's native RPC instead.",
      };
    }

    if (!options.accountAddress) {
      this.emitTelemetry("batch.completed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "accountAddress is required",
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
      };
    }

    if (!options.intents?.length) {
      this.emitTelemetry("batch.completed", telemetry, {
        outcome: "failure",
        errorCode: "INVALID_OPTIONS",
        errorMessage: "At least one intent is required",
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
      };
    }

    if (!this.sponsorship) {
      this.emitTelemetry("batch.prepare.failed", telemetry, {
        outcome: "failure",
        errorCode: "MISSING_APP_CREDENTIALS",
        errorMessage: "No sponsorship configured on OneAuthClient",
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
        error:
          "No sponsorship configured on OneAuthClient. Every user intent must carry the app's JWT — configure `sponsorship` on the client.",
      };
    }

    const sponsorshipModes = options.intents.map((intent) => this.resolveSponsorshipMode(intent));

    // Fetch the app's access token up front — /api/intent/batch-prepare
    // requires it so the passkey server can authenticate its orchestrator
    // quote calls with the app's JWT instead of a service-level API key.
    const accessTokenResult = await this.fetchAccessToken();
    if (!accessTokenResult.ok) {
      this.emitTelemetry("batch.prepare.failed", telemetry, {
        outcome: "failure",
        errorCode: accessTokenResult.code ?? "SPONSORSHIP_FETCH_FAILED",
        errorMessage: accessTokenResult.message,
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
      };
    }
    // Mutable: a quote refresh re-mints the token and must keep the execute
    // path's auth aligned with the refreshed quotes.
    let accessToken = accessTokenResult.accessToken;

    // Serialize token request amounts to strings for API. Each intent's
    // labels are capped via capCallLabels() so the dialog's phase-1 preview
    // (which reads from serializedIntents below) and the prepare request
    // both ship the same truncated strings.
    const serializedIntents = options.intents.map((intent) => ({
      targetChain: intent.targetChain,
      calls: capCallLabels(intent.calls),
      tokenRequests: intent.tokenRequests?.map((r) => ({
        token: r.token,
        amount: r.amount.toString(),
      })),
      sourceAssets: intent.sourceAssets,
      sourceChainId: intent.sourceChainId,
      moduleInstall: intent.moduleInstall,
    }));

    const requestBody = {
      ...(options.accountAddress && { accountAddress: options.accountAddress }),
      intents: serializedIntents,
      auth: { accessToken },
      ...(this.config.clientId && { clientId: this.config.clientId }),
    };

    // 1. Show dialog immediately + prepare batch in parallel.
    //
    // Same two-phase approach as sendIntent: open the dialog right away with
    // raw call data so the user sees something immediately, then upgrade to the
    // full quote payload once batch-prepare completes. See sendIntent for the
    // full rationale.
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams();
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const signingUrl = `${dialogUrl}/dialog/sign?${params.toString()}`;
    const requestedBlindSigning = this.shouldRequestBlindSigning(options);
    const { dialog, iframe, cleanup, reveal } = this.createModalDialog(signingUrl, {
      hidden: requestedBlindSigning,
    });
    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      intentCount: options.intents.length,
      attributes: { blindSigning: requestedBlindSigning },
    });

    const dialogOrigin = this.getDialogOrigin();

    type BatchPrepareResult =
      | { success: true; data: PrepareBatchIntentResponse; tier: string | null; extensionTokens: string[] }
      | { success: false; error: string; failedIntents?: Array<{ index: number; targetChain: number; error: string }> };
    let earlyBatchResult: BatchPrepareResult | null = null;
    this.emitTelemetry("batch.prepare.started", telemetry, {
      outcome: "started",
      intentCount: options.intents.length,
      targetChains: serializedIntents.map((intent) => intent.targetChain),
    });
    const preparePromise = this.prepareBatchWithFunding(requestBody, sponsorshipModes, telemetry).then((r) => {
      earlyBatchResult = r;
      if (r.success) {
        this.emitTelemetry("batch.prepare.succeeded", telemetry, {
          outcome: "success",
          intentCount: r.data.intents.length,
          targetChains: serializedIntents.map((intent) => intent.targetChain),
        });
      } else {
        this.emitTelemetry("batch.prepare.failed", telemetry, {
          outcome: "failure",
          errorMessage: r.error,
          intentCount: options.intents.length,
        });
      }
      return r;
    });

    // Wait for iframe to be ready
    const dialogResult = await this.waitForDialogReadyDeferred(dialog, iframe, cleanup, {
      blindSigning: requestedBlindSigning,
      reveal,
    });

    // Handle dialog closed before ready
    if (!dialogResult.ready) {
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_CANCELLED",
        errorMessage: "User closed the dialog before it became ready",
      });
      return {
        success: false,
        results: [],
        successCount: 0,
        failureCount: 0,
      };
    }
    const blindSigning = dialogResult.blindSigning;
    this.emitTelemetry("dialog.ready", telemetry, { outcome: "started" });

    // Mutable init payload — starts as early preview, upgraded to full after quote
    let currentBatchPayload: Record<string, unknown>;

    // Helper to handle batch prepare failure
    const handleBatchPrepareFailure = async (
      prepareResult: Extract<BatchPrepareResult, { success: false }>,
    ) => {
      const failedIntents = prepareResult.failedIntents;
      const failureResults: import("./types").BatchIntentItemResult[] = failedIntents?.map((f) => ({
        index: f.index,
        success: false,
        intentId: "",
        status: "failed" as import("./types").IntentStatus,
        error: { message: f.error, code: "PREPARE_FAILED" },
      })) ?? [];

      this.sendPrepareError(iframe, prepareResult.error);
      await this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
      return {
        success: false as const,
        results: failureResults,
        successCount: 0,
        failureCount: failureResults.length,
        error: prepareResult.error,
      };
    };


    let prepareResponse: PrepareBatchIntentResponse;

    if (earlyBatchResult) {
      // Fast path: batch-prepare already finished — send full PASSKEY_INIT
      const prepareResult = earlyBatchResult as BatchPrepareResult;
      if (!prepareResult.success) {
        return handleBatchPrepareFailure(prepareResult);
      }
      prepareResponse = prepareResult.data;
      const batchSponsorshipTokens: SponsorshipTokens = {
        accessToken,
        extensionTokens: prepareResult.extensionTokens,
      };
      currentBatchPayload = {
        mode: "iframe",
        batchMode: true,
        batchIntents: prepareResponse.intents,
        batchFailedIntents: prepareResponse.failedIntents,
        challenge: prepareResponse.challenge,
        binding: prepareResponse.binding,
        accountAddress: prepareResponse.accountAddress,
        userId: prepareResponse.userId,
        expiresAt: prepareResponse.expiresAt,
        tier: prepareResult.tier,
        auth: batchSponsorshipTokens,
        telemetry: this.telemetryPayload(telemetry),
      };
      dialogResult.sendInit(currentBatchPayload);
    } else {
      // Progressive path: send early preview with raw intent calls
      currentBatchPayload = {
        mode: "iframe",
        batchMode: true,
        batchIntents: serializedIntents.map((intent, idx) => ({
          index: idx,
          targetChain: intent.targetChain,
          calls: JSON.stringify(intent.calls),
          // No: transaction, intentOp, expiresAt, originMessages
        })),
        accountAddress: options.accountAddress,
        telemetry: this.telemetryPayload(telemetry),
      };
      dialogResult.sendInit(currentBatchPayload);

      // Wait for batch-prepare to complete, then send quote data
      const prepareResult = await preparePromise;
      if (!prepareResult.success) {
        return handleBatchPrepareFailure(prepareResult);
      }
      prepareResponse = prepareResult.data;
      const batchSponsorshipTokens: SponsorshipTokens = {
        accessToken,
        extensionTokens: prepareResult.extensionTokens,
      };

      // Upgrade the payload with full quote data
      currentBatchPayload = {
        mode: "iframe",
        batchMode: true,
        batchIntents: prepareResponse.intents,
        batchFailedIntents: prepareResponse.failedIntents,
        challenge: prepareResponse.challenge,
        binding: prepareResponse.binding,
        accountAddress: prepareResponse.accountAddress,
        userId: prepareResponse.userId,
        expiresAt: prepareResponse.expiresAt,
        tier: prepareResult.tier,
        auth: batchSponsorshipTokens,
        telemetry: this.telemetryPayload(telemetry),
      };

      // Re-send as PASSKEY_INIT so all dialog versions handle it
      iframe.contentWindow?.postMessage(
        { type: "PASSKEY_INIT", ...currentBatchPayload, fullViewport: true, blindSigning },
        dialogOrigin,
      );
    }

    // 2. Handle iframe remount: resend PASSKEY_INIT on subsequent PASSKEY_READY
    const handleBatchReReady = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.data?.type === "PASSKEY_READY") {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...currentBatchPayload, fullViewport: true, blindSigning },
          dialogOrigin,
        );
      }
    };
    window.addEventListener("message", handleBatchReReady);

    // 3. Wait for batch signing result with auto-refresh support
    const batchResult = await new Promise<SendBatchIntentResult>((resolve) => {
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const message = event.data;

		if (message?.type === "PASSKEY_DISCONNECT") {
			window.removeEventListener("message", handleMessage);
			cleanup();
			this.emitTelemetry("dialog.cancelled", telemetry, {
				outcome: "cancelled",
				errorCode: "USER_REJECTED",
				errorMessage: "Session disconnected",
			});
			resolve({
				success: false,
				results: [],
				successCount: 0,
				failureCount: 0,
			});
			return;
		}

        // Handle quote refresh request
        if (message?.type === "PASSKEY_REFRESH_QUOTE") {
          try {
            // Re-mint the app access token — refreshes fire after the user
            // lingered on review, so the token in `requestBody` may have
            // crossed its 1h expiry (orchestrator would 401 the re-quote).
            const refreshedTokenResult = await this.fetchAccessToken();
            const refreshToken = refreshedTokenResult.ok
              ? refreshedTokenResult.accessToken
              : accessToken;
            const refreshResult = await this.prepareBatchWithFunding(
              { ...requestBody, auth: { accessToken: refreshToken } },
              sponsorshipModes,
              telemetry,
            );

            if (refreshResult.success) {
              const refreshed = refreshResult.data;
              const refreshedAuth: SponsorshipTokens = {
                accessToken: refreshToken,
                extensionTokens: refreshResult.extensionTokens,
              };

              prepareResponse = refreshed;
              accessToken = refreshToken;
              requestBody.auth = { accessToken: refreshToken };
              // Keep iframe-remount replays on the refreshed quotes + auth.
              currentBatchPayload = {
                ...currentBatchPayload,
                batchIntents: refreshed.intents,
                batchFailedIntents: refreshed.failedIntents,
                challenge: refreshed.challenge,
                binding: refreshed.binding,
                expiresAt: refreshed.expiresAt,
                auth: refreshedAuth,
              };
              iframe.contentWindow?.postMessage({
                type: "PASSKEY_REFRESH_COMPLETE",
                batchIntents: refreshed.intents,
                challenge: refreshed.challenge,
                binding: refreshed.binding,
                expiresAt: refreshed.expiresAt,
                auth: refreshedAuth,
              }, dialogOrigin);
            } else {
              iframe.contentWindow?.postMessage({
                type: "PASSKEY_REFRESH_ERROR",
                error: "Failed to refresh batch quotes",
              }, dialogOrigin);
            }
          } catch {
            iframe.contentWindow?.postMessage({
              type: "PASSKEY_REFRESH_ERROR",
              error: "Failed to refresh batch quotes",
            }, dialogOrigin);
          }
          return;
        }

        // Handle signing result with batch results
        if (message?.type === "PASSKEY_SIGNING_RESULT") {
          window.removeEventListener("message", handleMessage);

          if (message.success && message.data?.batchResults) {
            const rawResults: Array<{
              index: number;
              operationId?: string;
              intentId?: string;
              status: string;
              error?: string;
              success?: boolean;
            }> = message.data.batchResults;

            const results: BatchIntentItemResult[] = rawResults.map((r) => ({
              index: r.index,
              success: r.success ?? r.status !== "FAILED",
              intentId: r.intentId || r.operationId || "",
              status: r.status === "FAILED" ? "failed" : "pending",
              error: r.error ? { code: "EXECUTE_FAILED", message: r.error } : undefined,
            }));

            // Merge in prepare-phase failures (e.g. account not deployed on chain).
            // These intents never reached the dialog (they were filtered out before
            // the signing screen), so we re-attach them here to give callers a
            // complete, index-sorted result set covering every input intent.
            const prepareFailures: BatchIntentItemResult[] = (prepareResponse.failedIntents ?? []).map((f) => ({
              index: f.index,
              success: false,
              intentId: "",
              status: "failed" as const,
              error: { code: "PREPARE_FAILED", message: f.error },
            }));
            const allResults = [...results, ...prepareFailures].sort((a, b) => a.index - b.index);

            const successCount = allResults.filter((r) => r.success).length;

            await this.waitForDialogCloseUnlessBlind(blindSigning, dialog, cleanup);
            this.emitTelemetry("batch.sign.succeeded", telemetry, {
              outcome: "success",
              intentCount: allResults.length,
            });

            resolve({
              success: successCount === allResults.length,
              results: allResults,
              successCount,
              failureCount: allResults.length - successCount,
            });
          } else {
            // Signing failed or was cancelled
            cleanup();
            this.emitTelemetry("batch.sign.failed", telemetry, {
              outcome: "failure",
              errorCode: "SIGNING_FAILED",
              errorMessage: "Batch signing failed or was cancelled",
            });
            resolve({
              success: false,
              results: [],
              successCount: 0,
              failureCount: 0,
            });
          }
        }

        // Handle dialog close
        if (message?.type === "PASSKEY_CLOSE") {
          window.removeEventListener("message", handleMessage);
          cleanup();
          this.emitTelemetry("dialog.cancelled", telemetry, {
            outcome: "cancelled",
            errorCode: "USER_CANCELLED",
            errorMessage: "User closed the batch dialog",
          });
          resolve({
            success: false,
            results: [],
            successCount: 0,
            failureCount: 0,
          });
        }
      };

      window.addEventListener("message", handleMessage);
    });

    window.removeEventListener("message", handleBatchReReady);

    this.emitTelemetry("batch.completed", telemetry, {
      outcome: batchResult.success ? "success" : "failure",
      intentCount: batchResult.results.length,
      attributes: {
        successCount: batchResult.successCount,
        failureCount: batchResult.failureCount,
      },
      ...(batchResult.error ? { errorMessage: batchResult.error } : {}),
    });

    return batchResult;
  }

  /**
   * Send transaction status to the dialog iframe
   */
  private sendTransactionStatus(
    iframe: HTMLIFrameElement,
    status: string,
    transactionHash?: string
  ): void {
    const dialogOrigin = this.getDialogOrigin();
    iframe.contentWindow?.postMessage(
      {
        type: "TRANSACTION_STATUS",
        status,
        transactionHash,
      },
      dialogOrigin
    );
  }

  /**
   * Listen for a signing result that belongs to a specific `requestId`.
   *
   * Used by legacy server-side signing request flows (popup/embed/modal) where
   * the dialog was pre-loaded with a signing request created via the API. The
   * `requestId` filter is needed because multiple dialogs may be open or there
   * may be stale messages from a previous dialog in the queue.
   *
   * Resolves on `PASSKEY_SIGNING_RESULT` matching `requestId`, or on
   * `PASSKEY_CLOSE` (user dismissed the dialog).
   *
   * @param requestId - The signing request ID to match against incoming messages.
   * @param dialog - The `<dialog>` element (opened before this call) used to
   *   `showModal()` so the dialog becomes interactive.
   * @param _iframe - Unused; kept for signature consistency with other wait helpers.
   * @param cleanup - Idempotent teardown function that closes and removes the dialog.
   * @returns A `SigningResult` discriminated union.
   */
  private waitForIntentSigningResponse(
    requestId: string,
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void
  ): Promise<SigningResult> {
    const dialogOrigin = this.getDialogOrigin();

    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const message = event.data;
        const payload = message?.data as { requestId?: string; signature?: WebAuthnSignature; passkey?: { credentialId: string; publicKeyX: string; publicKeyY: string; keyId: number } } | undefined;

		if (message?.type === "PASSKEY_DISCONNECT") {
			window.removeEventListener("message", handleMessage);
			cleanup();
			resolve({
				success: false,
				error: { code: "USER_REJECTED", message: "Session disconnected" },
			});
			return;
		}

        if (message?.type === "PASSKEY_SIGNING_RESULT" && payload?.requestId === requestId) {
          window.removeEventListener("message", handleMessage);

          if (message.success && payload.signature) {
            resolve({
              success: true,
              requestId,
              signature: payload.signature,
              passkey: payload.passkey, // Include passkey info for signature encoding
            });
          } else {
            resolve({
              success: false,
              error: parseSigningResultError(message.error),
            });
          }
        } else if (
          message?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // User clicked X button to close/reject. Cross-frame guard: ignore a
          // stray PASSKEY_CLOSE from a sibling same-origin dialog so it can't
          // forge a rejection of this signing request.
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            error: {
              code: "USER_REJECTED",
              message: "User closed the dialog",
            },
          });
        }
      };

      window.addEventListener("message", handleMessage);
      dialog.showModal();
    });
  }

  /**
   * Listen for a signing result from the currently open modal dialog.
   *
   * Unlike `waitForIntentSigningResponse`, this variant does not filter by
   * `requestId` because the inline intent flow (sendIntent / signMessage /
   * signTypedData) owns the dialog exclusively — there is no risk of collisions
   * from other dialogs.
   *
   * Handles two result shapes:
   *   - New "secure flow": dialog executed the intent server-side and returns
   *     only an `intentId` (no signature exposed to the SDK).
   *   - Legacy flow: dialog returns a raw `WebAuthnSignature` for the SDK to
   *     forward to the execute endpoint.
   *
   * @param dialog - The `<dialog>` element wrapping the signing iframe.
   * @param iframe - The signing iframe; used to authenticate the source of
   *   `PASSKEY_CLOSE` so a sibling same-origin dialog can't forge a rejection.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   * @returns A `SigningResult` extended with an optional `signedHash` field
   *   populated when the dialog performs message signing.
   */
  private waitForSigningResponse(
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void
  ): Promise<SigningResult & { signedHash?: string }> {
    const dialogOrigin = this.getDialogOrigin();

    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const message = event.data;
        const payload = message?.data as { signature?: WebAuthnSignature; passkey?: PasskeyCredentials; signedHash?: string; intentId?: string } | undefined;

		if (message?.type === "PASSKEY_DISCONNECT") {
			window.removeEventListener("message", handleMessage);
			cleanup();
			resolve({
				success: false,
				error: { code: "USER_REJECTED", message: "Session disconnected" },
			});
			return;
		}

        if (message?.type === "PASSKEY_SIGNING_RESULT") {
          window.removeEventListener("message", handleMessage);

          // Check if dialog already executed the intent (new secure flow)
          if (message.success && payload?.intentId) {
            resolve({
              success: true,
              intentId: payload.intentId,
            } as SigningResult & { signedHash?: string; intentId?: string });
          } else if (message.success && payload?.signature) {
            // Legacy flow - dialog returns signature for SDK to execute
            resolve({
              success: true,
              signature: payload.signature,
              passkey: payload.passkey,
              signedHash: payload.signedHash,
            });
          } else {
            // Failure path: tear the iframe down here. The passkey app
            // sends `PASSKEY_SIGNING_RESULT(false)` then `PASSKEY_CLOSE`
            // back-to-back on cancel, but we've just removed the
            // listener so the follow-up CLOSE would be dropped, leaving
            // the dialog stuck open.
            cleanup();
            resolve({
              success: false,
              error: parseSigningResultError(message.error),
            });
          }
        } else if (
          message?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // Cross-frame guard: ignore a stray PASSKEY_CLOSE from a sibling
          // same-origin dialog (e.g. an auth modal tearing down) so it can't
          // forge a rejection of this signing request.
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            error: {
              code: "USER_REJECTED",
              message: "User closed the dialog",
            },
          });
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Listen for a signing result while also handling quote-refresh requests.
   *
   * Quotes from the Rhinestone orchestrator have a short TTL (typically ~60 s).
   * When the user takes too long to review and the quote expires, the dialog
   * sends `PASSKEY_REFRESH_QUOTE`. This method intercepts that message, calls
   * the provided `onRefresh` callback to fetch a new quote, and replies with
   * either `PASSKEY_REFRESH_COMPLETE` (success) or `PASSKEY_REFRESH_ERROR`
   * (failure) — allowing the dialog to stay open and show the updated fees
   * without requiring a full restart.
   *
   * Once the user confirms or rejects, the promise resolves with the signing
   * result exactly as `waitForSigningResponse` would.
   *
   * @param dialog - The `<dialog>` element wrapping the signing iframe.
   * @param iframe - The iframe element; used to post refresh messages back.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   * @param dialogOrigin - Trusted origin for message validation (passed in to
   *   avoid redundant `getDialogOrigin()` calls from the hot path).
   * @param onRefresh - Async callback that fetches a fresh quote and returns the
   *   updated intent fields, or `null` if the refresh failed.
   * @returns A `SigningResult` extended with an optional `signedHash`.
   */
  private waitForSigningWithRefresh(
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    dialogOrigin: string,
    onRefresh: () => Promise<{
      intentOp: string;
      expiresAt: string;
      challenge: string;
      originMessages?: Array<{ chainId: number; hash: string }>;
      transaction?: unknown;
      digestResult?: unknown;
      /** Fresh prepare->execute binding for the refreshed quote (opaque). */
      binding?: string;
      /** Auth bundle re-bound to the refreshed intentOp — extension grants
          are digest-bound, so the dialog must swap auth with the quote. */
      auth?: SponsorshipTokens;
    } | null>
  ): Promise<SigningResult & { signedHash?: string }> {
    return new Promise((resolve) => {
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const message = event.data;

		if (message?.type === "PASSKEY_DISCONNECT") {
			window.removeEventListener("message", handleMessage);
			cleanup();
			resolve({
				success: false,
				error: { code: "USER_REJECTED", message: "Session disconnected" },
			});
			return;
		}

        // Handle quote refresh request from dialog
        if (message?.type === "PASSKEY_REFRESH_QUOTE") {
          const refreshedData = await onRefresh();

          if (refreshedData) {
            // Send refreshed quote data to dialog
            iframe.contentWindow?.postMessage({
              type: "PASSKEY_REFRESH_COMPLETE",
              ...refreshedData,
            }, dialogOrigin);
          } else {
            // Send error if refresh failed
            iframe.contentWindow?.postMessage({
              type: "PASSKEY_REFRESH_ERROR",
              error: "Failed to refresh quote",
            }, dialogOrigin);
          }
          return;
        }

        const payload = message?.data as {
          signature?: WebAuthnSignature;
          passkey?: { credentialId: string; publicKeyX: string; publicKeyY: string; keyId: number };
          signedHash?: string;
          intentId?: string;
        } | undefined;

        if (message?.type === "PASSKEY_SIGNING_RESULT") {
          window.removeEventListener("message", handleMessage);

          // Check if dialog already executed the intent (new secure flow)
          if (message.success && payload?.intentId) {
            resolve({
              success: true,
              intentId: payload.intentId,
            } as SigningResult & { signedHash?: string; intentId?: string });
          } else if (message.success && payload?.signature) {
            // Legacy flow - dialog returns signature for SDK to execute
            resolve({
              success: true,
              signature: payload.signature,
              passkey: payload.passkey,
              signedHash: payload.signedHash,
            });
          } else {
            // Failure path: close the iframe. The passkey app sends the
            // follow-up `PASSKEY_CLOSE` after this `RESULT`, but we've just
            // removed the listener so it would be dropped on the floor —
            // tear down here so the user isn't stuck staring at a dialog
            // that's already done.
            cleanup();
            resolve({
              success: false,
              error: parseSigningResultError(message.error),
            });
          }
        } else if (
          message?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // Cross-frame guard: ignore a stray PASSKEY_CLOSE from a sibling
          // same-origin dialog so it can't forge a rejection of this signing
          // request mid-refresh.
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            error: {
              code: "USER_REJECTED",
              message: "User closed the dialog",
            },
          });
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Wait for the dialog to be explicitly closed by the user or the iframe.
   *
   * Resolves on either:
   *   - `PASSKEY_CLOSE` postMessage from the iframe (user clicked the X button
   *     or the dialog programmatically closed itself after showing a result).
   *   - The native `<dialog> close` event (escape key or `dialog.close()` call).
   *
   * Calling `cleanup()` before awaiting this is safe — both resolution paths
   * call `cleanup()` internally but it is idempotent. The primary use case is
   * keeping the dialog open while the SDK polls for a transaction hash, then
   * showing the final success/error state before letting the user dismiss.
   *
   * @param dialog - The `<dialog>` element to watch.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   */
  private waitForDialogClose(
    dialog: HTMLDialogElement,
    cleanup: () => void
  ): Promise<void> {
    const dialogOrigin = this.getDialogOrigin();

    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        if (event.data?.type === "PASSKEY_CLOSE") {
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve();
        }
      };

      // Also handle dialog close via escape key or clicking outside
      const handleClose = () => {
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
        cleanup();
        resolve();
      };

      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Hidden blind-signing iframes have no user-visible Done or Close button.
   * Once the SDK has the result it needs, tear them down immediately instead
   * of waiting for an iframe close event that can never be clicked.
   */
  private async waitForDialogCloseUnlessBlind(
    blindSigning: boolean,
    dialog: HTMLDialogElement,
    cleanup: () => void,
  ): Promise<void> {
    if (blindSigning) {
      cleanup();
      return;
    }
    await this.waitForDialogClose(dialog, cleanup);
  }

  /**
   * After a prepare error has been surfaced in the dialog, wait for the user
   * to either close the dialog (give up) or click "Try Again", which the
   * dialog forwards as `PASSKEY_RETRY_PREPARE`.
   *
   * On `"closed"` the dialog has been cleaned up; on `"retry"` it stays open
   * (showing its loading skeleton) while the caller re-runs prepare. Without
   * this, "Try Again" after a prepare failure was a dead end: the dialog
   * reset its local state but the SDK had already given up, so the quote
   * never arrived and the Sign button stayed disabled forever.
   */
  private waitForPrepareRetryOrClose(
    dialog: HTMLDialogElement,
    cleanup: () => void,
  ): Promise<"retry" | "closed"> {
    const dialogOrigin = this.getDialogOrigin();

    return new Promise((resolve) => {
      const stop = () => {
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        if (event.data?.type === "PASSKEY_RETRY_PREPARE") {
          stop();
          resolve("retry");
        } else if (event.data?.type === "PASSKEY_CLOSE") {
          stop();
          cleanup();
          resolve("closed");
        }
      };

      // Also handle dialog close via escape key or clicking outside
      const handleClose = () => {
        stop();
        cleanup();
        resolve("closed");
      };

      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Inject a `<link rel="preconnect">` tag for the given URL's origin.
   *
   * Browsers use preconnect hints to resolve DNS, perform the TCP handshake,
   * and negotiate TLS before a resource is actually requested, reducing
   * time-to-first-byte when the dialog iframe loads. The tag is deduplicated —
   * if one already exists for the same origin it will not be added again.
   *
   * Only called in browser environments (guarded by `typeof document` in the
   * constructor). Invalid URLs are silently ignored.
   *
   * @param url - Any URL whose origin should be preconnected to.
   */
  private injectPreconnect(url: string): void {
    try {
      const origin = new URL(url).origin;
      if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    } catch {
      // Invalid URL, skip
    }
  }

  /**
   * Warm the dialog ahead of the first user interaction.
   *
   * `preconnect` (run in the constructor) only warms DNS/TLS. This goes
   * further: it loads the dialog into a hidden, off-screen iframe so the
   * browser downloads and parses the dialog HTML + JS bundle and the font, and
   * keeps the cross-origin connection alive. When the user later opens a real
   * auth/sign dialog, the fresh iframe serves those bytes from cache over the
   * warm connection and paints far sooner — so the SDK preload overlay is shown
   * only briefly (or imperceptibly) instead of covering a full cold load.
   *
   * It loads a dedicated `/dialog/warm` route, NOT a real flow route. That
   * matters for two reasons:
   *   - **No side effects.** The real `/dialog/auth` route runs the signup
   *     flow on mount (identity probe → `POST /api/auth/register?step=start`),
   *     which writes an `AuthChallenge` row and counts against the register
   *     rate limiter. Warming must never create backend auth state for a user
   *     who hasn't acted, so the warm route renders nothing and runs no flow.
   *   - **No message cross-talk.** The warm route posts no `PASSKEY_READY` /
   *     `PASSKEY_RENDERED`, so a slow warm can never satisfy a visible modal's
   *     readiness listener and drive it to `PASSKEY_INIT` at the wrong moment.
   * The warm route still pulls the shared Next.js framework + main + dialog
   * layout chunks (the bulk of every flow's bundle), so the real open is fast.
   *
   * Best called on a *likely-intent* signal — `pointerenter`/`focus` of your
   * "Sign in" / "Pay" button — rather than on page load, so visitors who never
   * authenticate don't pay for a cross-origin iframe. Pass `prewarm: true` in
   * the client config to have the SDK schedule this once on an idle callback.
   *
   * Idempotent: repeated calls return the same in-flight/settled promise.
   * Resolves `true` once the hidden iframe's document has loaded, `false` on
   * timeout or failure. Never throws and never blocks a real dialog open — a
   * failed prewarm just means the next open does a normal (cold) load.
   *
   * @returns Whether the dialog became warm.
   */
  async prewarm(): Promise<boolean> {
    if (typeof document === "undefined") return false;
    // Idempotent: a second call (e.g. hover after an idle-scheduled warm)
    // rides the first one's load instead of opening another iframe.
    if (this.prewarmState) return this.prewarmState.ready;

    // Dedicated warm route — silent for modal readiness, but it can emit a
    // deployment-forced disconnect. Pin the host origin in the URL because
    // privacy policies may strip document.referrer and prewarm never sends
    // PASSKEY_INIT to authenticate a postMessage origin later.
    const warmParams = new URLSearchParams({ parentOrigin: window.location.origin });
    const warmUrl = `${this.getDialogUrl()}/dialog/warm?${warmParams.toString()}`;

    // A bare hidden iframe — deliberately NOT the full createModalDialog
    // chrome (no <dialog>, overlay, Escape handler or showModal). We only need
    // the browser to fetch + run the bundle; none of the modal lifecycle
    // applies to a frame the user never sees.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    iframe.title = "1auth dialog prewarm";
    // Off-screen + inert so it can never intercept clicks or take focus, while
    // still being "rendered" (display:none would let some browsers defer the
    // load we are trying to trigger).
    iframe.style.cssText =
      "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;border:0;pointer-events:none;";

    // Resolve on the iframe `load` event rather than a postMessage. The warm
    // route is intentionally message-silent, and `load` fires for cross-origin
    // frames too (we never touch contentDocument), so it is the right
    // signal — and one a stray frame's messages can never spoof.
    const ready = new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (value: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      iframe.onload = () => finish(true);
      iframe.onerror = () => finish(false);
      // Failsafe so the promise always settles even if `load` is missed.
      const timer = setTimeout(() => finish(false), 10000);
    });
    iframe.src = warmUrl;
    document.body.appendChild(iframe);

    this.prewarmState = { iframe, ready };
    return ready;
  }

  /**
   * Tear down the hidden prewarm iframe created by {@link prewarm}.
   *
   * The HTTP cache that prewarm populated survives teardown, so a subsequent
   * open is still bundle-warm; this only releases the parked frame (and its
   * keep-alive connection). Call it when auth is no longer likely on the
   * current view. Safe to call when nothing is warmed.
   */
  destroyPrewarm(): void {
    const state = this.prewarmState;
    if (!state) return;
    this.prewarmState = null;
    state.iframe.remove();
  }

  /**
   * Wait for the dialog iframe to signal ready, but defer sending `PASSKEY_INIT`
   * until the caller decides the time is right.
   *
   * This is the deferred variant of `waitForDialogReady`, used by flows that run
   * the dialog and a background API call in parallel (the "two-phase" approach).
   * Rather than blocking until both the iframe is ready AND the API call is done,
   * this method resolves as soon as `PASSKEY_READY` arrives and returns a
   * `sendInit` callback. The caller invokes `sendInit` once prepare data is
   * available, which may happen before or after the iframe is ready.
   *
   * Timeout: if the iframe never signals ready within 10 seconds (e.g. network
   * error, origin mismatch), resolves with `{ ready: false }` and calls cleanup.
   *
   * @param dialog - The `<dialog>` element wrapping the iframe.
   * @param iframe - The iframe element that will receive `PASSKEY_INIT`.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   * @returns `{ ready: true, sendInit }` when the iframe is ready, or
   *   `{ ready: false }` if the dialog was closed or timed out first.
   */
  private waitForDialogReadyDeferred(
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    options: { blindSigning?: boolean; reveal?: () => void } = {},
  ): Promise<
    | { ready: true; blindSigning: boolean; sendInit: (initMessage: Record<string, unknown>) => void }
    | { ready: false }
  > {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      let settled = false;

      const teardown = () => {
        if (settled) return;
        settled = true;
        clearTimeout(readyTimeout);
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        if (event.data?.type === "PASSKEY_READY") {
          const blindSigning = options.blindSigning === true;
          teardown();
          resolve({
            ready: true,
            blindSigning,
            sendInit: (initMessage: Record<string, unknown>) => {
              if (blindSigning) iframe.focus({ preventScroll: true });
              iframe.contentWindow?.postMessage(
                { type: "PASSKEY_INIT", ...initMessage, fullViewport: true, blindSigning },
                dialogOrigin,
              );
            },
          });
        } else if (
          event.data?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // Match by source, not just origin: the auth dialog (authWithModal)
          // emits a stray PASSKEY_CLOSE from the SAME origin as it tears down.
          // Without the source check, that close resolves the sign dialog's
          // ready-promise as { ready: false } → dialogClosedBeforeReadyPromise
          // → a spurious USER_CANCELLED ("closed before it became ready").
          // Identical guard to the earlyDialogClosePromise fix.
          teardown();
          cleanup();
          resolve({ ready: false });
        }
      };

      const handleClose = () => {
        teardown();
        resolve({ ready: false });
      };

      const readyTimeout = setTimeout(() => {
        teardown();
        cleanup();
        resolve({ ready: false });
      }, 10000);

      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Call the passkey service to obtain a Rhinestone orchestrator quote for an
   * intent (a single target-chain transaction or swap).
   *
   * The service contacts the Rhinestone orchestrator, which returns a signed
   * `intentOp` structure containing the quote, fees, and a WebAuthn challenge
   * the user must sign to authorize execution.
   *
   * The `X-Origin-Tier` response header is forwarded to the dialog so it can
   * display tier-specific UI (e.g. "Free" vs "Premium" badge).
   *
   * Side effect: if the server returns "User not found", the cached user entry
   * is removed from `localStorage` to force re-authentication.
   *
   * @param requestBody - Serialized intent options (bigint amounts converted to
   *   strings before this call).
   * @returns On success: `{ success: true, data: PrepareIntentResponse, tier }`.
   *   On failure: `{ success: false, error: { code, message, details? } }`.
   */
  /**
   * Request a non-sponsored draft solely to obtain the SDK-canonical intent
   * input that the embedding app must authorize. The passkey service returns
   * no signable quote or user data in this phase.
   */
  private async prepareSponsorshipDraft(
    requestBody: Record<string, unknown>,
    telemetry?: TelemetryOperation,
  ): Promise<
    | { success: true; sponsorshipIntentInput: unknown }
    | { success: false; error: { code: string; message: string; details?: OneAuthErrorDetails } }
  > {
    try {
      const response = await fetch(`${this.config.providerUrl}/api/intent/prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.telemetryHeaders(telemetry) },
        body: JSON.stringify({ ...requestBody, quoteMode: "draft" }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: {
            code: "PREPARE_FAILED",
            message: errorData.error || "Failed to prepare sponsorship draft",
            details: (errorData.details ?? errorData.candidateErrors ?? errorData) as OneAuthErrorDetails,
          },
        };
      }
      const data = (await response.json()) as { sponsorshipIntentInput?: unknown };
      if (data.sponsorshipIntentInput === undefined) {
        return {
          success: false,
          error: { code: "PREPARE_FAILED", message: "Prepare response missing sponsorship intent input" },
        };
      }
      return { success: true, sponsorshipIntentInput: data.sponsorshipIntentInput };
    } catch (error) {
      return {
        success: false,
        error: {
          code: "NETWORK_ERROR",
          message: error instanceof Error ? error.message : "Network error",
        },
      };
    }
  }

  private async prepareIntent(
    requestBody: Record<string, unknown>,
    telemetry?: TelemetryOperation,
  ): Promise<
    | { success: true; data: PrepareIntentResponse; tier: string | null }
    | { success: false; error: { code: string; message: string; details?: OneAuthErrorDetails } }
  > {
    try {
      const response = await fetch(`${this.config.providerUrl}/api/intent/prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.telemetryHeaders(telemetry) },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Failed to prepare intent";

        if (errorMessage.includes("User not found")) {
          localStorage.removeItem("1auth-user");
        }

        return {
          success: false,
          error: {
            code: errorMessage.includes("User not found") ? "USER_NOT_FOUND" : "PREPARE_FAILED",
            message: errorMessage,
            details: normalizeOneAuthErrorDetails(
              errorData.details ??
                (errorData.candidateErrors === undefined
                  ? errorData
                  : { candidateErrors: errorData.candidateErrors }),
            ),
          },
        };
      }

      const tier = response.headers.get("X-Origin-Tier");
      return { success: true, data: await response.json(), tier };
    } catch (error) {
      return {
        success: false,
        error: {
          code: "NETWORK_ERROR",
          message: error instanceof Error ? error.message : "Network error",
        },
      };
    }
  }

  /**
   * Call the passkey service to obtain orchestrator quotes for all intents in a
   * batch, returning a single shared WebAuthn challenge.
   *
   * The service prepares each intent independently and assembles a merkle tree
   * whose root becomes the challenge. Signing that root once authorizes every
   * intent in the batch. Partially-failed batches are supported: the response
   * includes both `intents` (succeeded) and `failedIntents` (per-intent errors),
   * so the dialog can still show the successful subset for signing.
   *
   * Side effect: same "User not found" localStorage cleanup as `prepareIntent`.
   *
   * @param requestBody - Serialized batch options (bigint amounts converted to
   *   strings before this call).
   * @returns On success: `{ success: true, data: PrepareBatchIntentResponse, tier }`.
   *   On failure: `{ success: false, error, failedIntents? }`.
   */
  /**
   * Draft, authorize, and authoritatively quote a batch without losing caller indices.
   * Preferred sponsorship failures are retried only by an explicit full-batch
   * self-funded re-quote so every returned intent shares one fresh merkle root.
   */
  private async prepareBatchWithFunding(
    requestBody: Record<string, unknown>,
    sponsorshipModes: SponsorshipMode[],
    telemetry?: TelemetryOperation,
  ): Promise<
    | { success: true; data: PrepareBatchIntentResponse; tier: string | null; extensionTokens: string[] }
    | { success: false; error: string; failedIntents?: Array<{ index: number; targetChain: number; error: string }> }
  > {
    const draftFundingModes = sponsorshipModes.map((mode) =>
      mode === "disabled" ? "self-funded" as const : "required" as const,
    );
    const draftResult = await this.prepareBatchDraft(
      { ...requestBody, quoteMode: "draft", fundingModes: draftFundingModes },
      telemetry,
    );
    if (!draftResult.success) return draftResult;

    const draftFailedIntents = draftResult.data.failedIntents ?? [];
    const draftSuccessfulIndices = new Set(
      Object.keys(draftResult.data.sponsorshipIntentInputs).map(Number),
    );
    if (draftSuccessfulIndices.size === 0) {
      return {
        success: false,
        error: "All intents failed to get sponsorship drafts",
        failedIntents: draftFailedIntents,
      };
    }
    // The final request must contain only draft successes. Keeping a failed
    // original index in the request would make the server require a grant that
    // cannot exist and discard the valid, signable subset.
    const finalIntents = (requestBody.intents as Array<Record<string, unknown>>)
      .flatMap((intent, index) => draftSuccessfulIndices.has(index)
        ? [{ ...intent, originalIndex: index }]
        : []);

    const grantsByIndex: Record<string, string> = {};
    const finalFundingModes = [...draftFundingModes];
    const sponsoredIndices = Object.keys(draftResult.data.sponsorshipIntentInputs)
      .map(Number)
      .filter((index) => sponsorshipModes[index] !== "disabled");
    const grantResults = await Promise.allSettled(
      sponsoredIndices.map(async (index) => ({
        index,
        token: await this.sponsorship!.getExtensionToken(
          JSON.stringify(draftResult.data.sponsorshipIntentInputs[String(index)]),
        ),
      })),
    );
    for (let i = 0; i < grantResults.length; i++) {
      const result = grantResults[i];
      const index = sponsoredIndices[i];
      if (result.status === "fulfilled") {
        grantsByIndex[String(index)] = result.value.token;
      } else if (sponsorshipModes[index] === "preferred") {
        finalFundingModes[index] = "self-funded";
      } else {
        return { success: false, error: `Sponsorship token fetch failed for required intent ${index}` };
      }
    }

    const runFinal = (fundingModes: Array<"required" | "self-funded">) =>
      this.prepareBatchIntent({
        ...requestBody,
        intents: finalIntents,
        quoteMode: "final",
        fundingModes,
        quoteExtensionTokens: grantsByIndex,
      }, telemetry);
    let finalResult = await runFinal(finalFundingModes);
    const preferredSponsoredFailures = finalResult.success
      ? (finalResult.data.failedIntents ?? []).map((failure) => failure.index)
      : (finalResult.failedIntents ?? []).map((failure) => failure.index);
    const preferredFallbackIndices = preferredSponsoredFailures.filter(
      (index) => sponsorshipModes[index] === "preferred" && finalFundingModes[index] === "required",
    );
    if (preferredFallbackIndices.length > 0) {
      for (const index of preferredFallbackIndices) {
        finalFundingModes[index] = "self-funded";
        delete grantsByIndex[String(index)];
      }
      finalResult = await runFinal(finalFundingModes);
    }
    if (!finalResult.success) return finalResult;
    if (draftFailedIntents.length > 0) {
      const draftFailedIndices = new Set(
        draftFailedIntents.map((failure) => failure.index),
      );
      finalResult = {
        ...finalResult,
        data: {
          ...finalResult.data,
          failedIntents: [
            ...draftFailedIntents,
            ...(finalResult.data.failedIntents ?? []).filter(
              (failure) => !draftFailedIndices.has(failure.index),
            ),
          ],
        },
      };
    }

    const sponsoredPreparedIntents = finalResult.data.intents.filter(
      (intent) => finalFundingModes[intent.index] === "required",
    );
    const executionGrantResults = await Promise.allSettled(
      sponsoredPreparedIntents.map(async (intent) => ({
        index: intent.index,
        token: await this.sponsorship!.getExtensionToken(intent.intentOp),
      })),
    );
    const executionGrantsByIndex: Record<string, string> = {};
    for (let i = 0; i < executionGrantResults.length; i++) {
      const result = executionGrantResults[i];
      const index = sponsoredPreparedIntents[i].index;
      if (result.status === "fulfilled") {
        executionGrantsByIndex[String(index)] = result.value.token;
      } else {
        return { success: false, error: `Execution sponsorship token fetch failed for intent ${index}` };
      }
    }

    return {
      ...finalResult,
      extensionTokens: finalResult.data.intents.map((intent) =>
        finalFundingModes[intent.index] === "required" ? executionGrantsByIndex[String(intent.index)] ?? "" : "",
      ),
    };
  }

  /** Request canonical batch sponsorship inputs without returning signable data. */
  private async prepareBatchDraft(
    requestBody: Record<string, unknown>,
    telemetry?: TelemetryOperation,
  ): Promise<
    | { success: true; data: PrepareBatchIntentDraftResponse; tier: string | null }
    | { success: false; error: string; failedIntents?: Array<{ index: number; targetChain: number; error: string }> }
  > {
    try {
      const response = await fetch(`${this.config.providerUrl}/api/intent/batch-prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.telemetryHeaders(telemetry) },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return { success: false, error: errorData.error || "Failed to prepare batch sponsorship draft", failedIntents: errorData.failedIntents };
      }
      return { success: true, data: await response.json(), tier: response.headers.get("X-Origin-Tier") };
    } catch {
      return { success: false, error: "Network error" };
    }
  }

  private async prepareBatchIntent(
    requestBody: Record<string, unknown>,
    telemetry?: TelemetryOperation,
  ): Promise<
    | { success: true; data: PrepareBatchIntentResponse; tier: string | null }
    | { success: false; error: string; failedIntents?: Array<{ index: number; targetChain: number; error: string }> }
  > {
    try {
      const response = await fetch(`${this.config.providerUrl}/api/intent/batch-prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.telemetryHeaders(telemetry) },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Failed to prepare batch intent";

        if (errorMessage.includes("User not found")) {
          localStorage.removeItem("1auth-user");
        }

        return {
          success: false,
          error: errorMessage,
          failedIntents: errorData.failedIntents,
        };
      }

      const tier = response.headers.get("X-Origin-Tier");
      return { success: true, data: await response.json(), tier };
    } catch {
      return { success: false, error: "Network error" };
    }
  }

  /**
   * Forward a prepare-phase error to the dialog iframe so it can display a
   * human-readable failure message instead of hanging on the loading state.
   *
   * The dialog listens for `PASSKEY_PREPARE_ERROR` and transitions to an error
   * view. The user can then dismiss the dialog, at which point
   * `waitForDialogClose` resolves and the SDK returns the error to the caller.
   *
   * @param iframe - The iframe element to post the error to.
   * @param error - Human-readable error message from the prepare response.
   */
  private sendPrepareError(iframe: HTMLIFrameElement, error: string): void {
    const dialogOrigin = this.getDialogOrigin();
    iframe.contentWindow?.postMessage(
      { type: "PASSKEY_PREPARE_ERROR", error },
      dialogOrigin,
    );
  }

  /**
   * Poll for intent status
   *
   * Use this to check on the status of a submitted intent
   * that hasn't completed yet.
   */
  async getIntentStatus(intentId: string): Promise<SendIntentResult> {
    const telemetry = this.createTelemetryOperation("status");
    this.emitTelemetry("intent.status.started", telemetry, {
      outcome: "started",
      attributes: { directStatusCheck: true },
    });
    try {
      const response = await fetch(
        `${this.config.providerUrl}/api/intent/status/${intentId}`,
        {
          headers: {
            ...this.telemetryHeaders(telemetry),
            ...(this.config.clientId ? { "x-client-id": this.config.clientId } : {}),
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        this.emitTelemetry("status.failed", telemetry, {
          outcome: "failure",
          errorCode: "STATUS_FAILED",
          errorMessage: errorData.error || "Failed to get intent status",
        });
        return {
          success: false,
          intentId,
          status: "failed",
          error: {
            code: "STATUS_FAILED",
            message: errorData.error || "Failed to get intent status",
          },
        };
      }

      const data = await response.json();
      this.emitTelemetry("status.succeeded", telemetry, {
        outcome: data.status === "completed" ? "success" : "started",
        status: data.status,
      });
      return {
        success: data.status === "completed",
        intentId,
        status: data.status,
        transactionHash: data.transactionHash,
        operationId: data.operationId,
      };
    } catch (error) {
      this.emitTelemetry("status.failed", telemetry, {
        outcome: "failure",
        ...describeTelemetryError(error),
      });
      return {
        success: false,
        intentId,
        status: "failed",
        error: {
          code: "NETWORK_ERROR",
          message: error instanceof Error ? error.message : "Network error",
        },
      };
    }
  }

  /**
   * Get the history of intents for the authenticated user.
   *
   * Requires an active session (user must be logged in).
   *
   * @example
   * ```typescript
   * // Get recent intents
   * const history = await client.getIntentHistory({ limit: 10 });
   *
   * // Filter by status
   * const pending = await client.getIntentHistory({ status: 'pending' });
   *
   * // Filter by date range
   * const lastWeek = await client.getIntentHistory({
   *   from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
   * });
   * ```
   */
  async getIntentHistory(
    options?: IntentHistoryOptions
  ): Promise<IntentHistoryResult> {
    const telemetry = this.createTelemetryOperation("history", {
      hasStatusFilter: !!options?.status,
    });
    const queryParams = new URLSearchParams();
    if (options?.limit) queryParams.set("limit", String(options.limit));
    if (options?.offset) queryParams.set("offset", String(options.offset));
    if (options?.status) queryParams.set("status", options.status);
    if (options?.from) queryParams.set("from", options.from);
    if (options?.to) queryParams.set("to", options.to);

    const url = `${this.config.providerUrl}/api/intent/history${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;

    const response = await fetch(url, {
      headers: {
        ...this.telemetryHeaders(telemetry),
        ...(this.config.clientId ? { "x-client-id": this.config.clientId } : {}),
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      this.emitTelemetry("history.failed", telemetry, {
        outcome: "failure",
        errorCode: "HISTORY_FAILED",
        errorMessage: errorData.error || "Failed to get intent history",
      });
      throw new Error(errorData.error || "Failed to get intent history");
    }

    const result = await response.json();
    this.emitTelemetry("history.succeeded", telemetry, {
      outcome: "success",
      attributes: {
        total: result.total,
        returned: result.intents?.length ?? 0,
      },
    });
    return result;
  }

  /**
   * Forward a raw EIP-1193 request to the iframe so the active EOA connector
   * can handle it (wallet-connect session or injected wallet). Opens
   * `/dialog/sign-eoa`, which looks up the wagmi connector and delegates to
   * `connector.getProvider().request({ method, params })`. The wallet's own
   * UI is the review screen; the dialog just transports the request.
   */
  async requestWithWallet(options: {
    method: string;
    params?: unknown[] | Record<string, unknown>;
    expectedAddress?: `0x${string}`;
    theme?: ThemeConfig;
  }): Promise<
    | { success: true; result: unknown }
    | { success: false; error: { code: string; message: string } }
  > {
    // Serialise concurrent calls: the EOA iframe is a single shared resource
    // and the iframe's view + classifyInit state assume one in-flight
    // request at a time.
    const job = this.eoaSerialQueue.then(
      () => this.doRequestWithWallet(options),
      () => this.doRequestWithWallet(options),
    );
    this.eoaSerialQueue = job.then(
      () => undefined,
      () => undefined,
    );
    return job;
  }

  private async doRequestWithWallet(options: {
    method: string;
    params?: unknown[] | Record<string, unknown>;
    expectedAddress?: `0x${string}`;
    theme?: ThemeConfig;
  }): Promise<
    | { success: true; result: unknown }
    | { success: false; error: { code: string; message: string } }
  > {
    const dialogOrigin = this.getDialogOrigin();
    const ensureResult = await this.ensureEoaDialog(options?.theme);
    if (!ensureResult.ok) {
      return {
        success: false,
        error: { code: "USER_REJECTED", message: "User closed the dialog" },
      };
    }
    const { dialog, iframe } = ensureResult;

    // requestId binds the SDK call to its dialog instance. Required for
    // every subsequent message: the dialog echoes it on PASSKEY_EOA_RESULT
    // and PASSKEY_EOA_CLOSE so stale messages from a previous flow cannot
    // cross-resolve this promise.
    const requestId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const hideDialog = () => {
      if (dialog.open) dialog.close();
    };

    // Install the result listener BEFORE PASSKEY_INIT is sent. Fast methods
    // like wallet_getCallsStatus can resolve in the same task as the init
    // and emit PASSKEY_EOA_RESULT before a listener attached afterward
    // would see it.
    const responsePromise = this.waitForEoaResponse(
      iframe,
      hideDialog,
      requestId,
      dialogOrigin,
    );

    const initPayload = {
      mode: "iframe",
      method: options.method,
      params: options.params,
      expectedAddress: options.expectedAddress,
      requestId,
    };
    iframe.contentWindow?.postMessage(
      { type: "PASSKEY_INIT", ...initPayload, fullViewport: true },
      dialogOrigin,
    );

    // The iframe may re-emit PASSKEY_READY if its React tree remounts
    // (HMR, Suspense). Re-send PASSKEY_INIT so it picks the current
    // request up — keyed on the same requestId, which the iframe dedupes.
    const handleReReady = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.source !== iframe.contentWindow) return;
      if (event.data?.type === "PASSKEY_READY") {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...initPayload, fullViewport: true },
          dialogOrigin,
        );
      }
    };
    window.addEventListener("message", handleReReady);

    const result = await responsePromise;
    window.removeEventListener("message", handleReReady);
    hideDialog();
    return result;
  }

  /**
   * Lazily create the persistent `/dialog/sign-eoa` iframe and reveal it.
   * Subsequent calls just re-open the existing `<dialog>` so the iframe's
   * in-page state (notably the WalletConnect SignClient) is preserved
   * between transactions.
   */
  private async ensureEoaDialog(theme: ThemeConfig | undefined): Promise<
    | { ok: true; dialog: HTMLDialogElement; iframe: HTMLIFrameElement }
    | { ok: false }
  > {
    if (this.eoaDialogState) {
      const { dialog, iframe } = this.eoaDialogState;
      if (!dialog.open) {
        // dialog.showModal() throws if already open — guarded above.
        dialog.showModal();
      }
      return { ok: true, dialog, iframe };
    }

    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    // Keep the persistent EOA iframe on the same parent-origin hint path as
    // the other dialog routes; the passkey app still authenticates the caller
    // from browser-provided referrer/event.origin before trusting it.
    appendParentOriginParam(params);
    const themeParams = this.getThemeParams(theme);
    if (themeParams) {
      new URLSearchParams(themeParams).forEach((value, key) => {
        params.set(key, value);
      });
    }
    const signingUrl = `${dialogUrl}/dialog/sign-eoa?${params.toString()}`;

    const { dialog, iframe, destroy } = this.createModalDialog(signingUrl, {
      persistent: true,
    });

    const ready = await this.waitForEoaIframeReady(iframe);
    if (!ready) {
      // Iframe never came up — fully tear down and surface as a rejection.
      // Next call will retry from scratch.
      destroy();
      return { ok: false };
    }

    this.eoaDialogState = { dialog, iframe };
    return { ok: true, dialog, iframe };
  }

  private waitForEoaIframeReady(iframe: HTMLIFrameElement): Promise<boolean> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        window.removeEventListener("message", handler);
      };
      const handler = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        if (event.source !== iframe.contentWindow) return;
        if (event.data?.type === "PASSKEY_READY") {
          cleanup();
          resolve(true);
        }
      };
      window.addEventListener("message", handler);
      timeoutId = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 10000);
    });
  }

  private waitForEoaResponse(
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    requestId: string,
    dialogOrigin: string,
  ): Promise<
    | { success: true; result: unknown }
    | { success: false; error: { code: string; message: string } }
  > {
    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        // Three trust checks before reading any payload:
        //   1. event.origin pinned to the dialog origin
        //   2. event.source pinned to this iframe's contentWindow (rejects
        //      messages from sibling frames or popups that grabbed a window
        //      reference)
        //   3. requestId echoed by the dialog matches ours (rejects stale
        //      messages from a prior remount or a concurrent dialog)
        if (event.origin !== dialogOrigin) return;
        if (event.source !== iframe.contentWindow) return;
        const message = event.data;
        if (
          message?.type === "PASSKEY_EOA_RESULT" ||
          message?.type === "PASSKEY_EOA_CLOSE"
        ) {
          if (message.requestId !== requestId) return;
        } else {
          return;
        }
        window.removeEventListener("message", handleMessage);
        if (message.type === "PASSKEY_EOA_CLOSE") {
          cleanup();
          resolve({
            success: false,
            error: { code: "USER_REJECTED", message: "User closed the dialog" },
          });
          return;
        }
        if (message.success) {
          resolve({ success: true, result: message.data?.result });
        } else {
          resolve({
            success: false,
            error: message.error || {
              code: "EOA_REQUEST_FAILED",
              message: "Wallet request failed",
            },
          });
        }
      };
      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Sign an arbitrary message with the user's passkey
   *
   * This is for off-chain message signing (e.g., authentication challenges,
   * terms acceptance, login signatures), NOT for transaction signing.
   * The message is displayed to the user and signed with their passkey.
   *
   * @example
   * ```typescript
   * // Sign a login challenge
   * const result = await client.signMessage({
   *   accountAddress: '0x...',
   *   message: `Sign in to MyApp\nTimestamp: ${Date.now()}\nNonce: ${crypto.randomUUID()}`,
   *   description: 'Verify your identity to continue',
   * });
   *
   * if (result.success) {
   *   // Send signature to your backend for verification
   *   await fetch('/api/verify', {
   *     method: 'POST',
   *     body: JSON.stringify({
   *       signature: result.signature,
   *       message: result.signedMessage,
   *     }),
   *   });
   * }
   * ```
   */
  async signMessage(options: SignMessageOptions): Promise<SignMessageResult> {
    const telemetry = this.createTelemetryOperation("sign", {
      signingMode: "message",
      hasAccountAddress: !!options.accountAddress,
    });
    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const signingUrl = `${dialogUrl}/dialog/sign?${params.toString()}`;
    const requestedBlindSigning = this.shouldRequestBlindSigning(options);

    const { dialog, iframe, cleanup, reveal } = this.createModalDialog(signingUrl, {
      hidden: requestedBlindSigning,
    });
    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      attributes: { blindSigning: requestedBlindSigning },
    });

    const dialogResult = await this.waitForDialogReadyDeferred(dialog, iframe, cleanup, {
      blindSigning: requestedBlindSigning,
      reveal,
    });
    if (!dialogResult.ready) {
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_REJECTED",
        errorMessage: "User closed the dialog before signing",
      });
      return {
        success: false,
        error: {
          code: "USER_REJECTED",
          message: "User closed the dialog",
        },
      };
    }

    const initPayload = {
      mode: "iframe",
      message: options.message,
      challenge: options.challenge || options.message,
      accountAddress: options.accountAddress,
      description: options.description,
      metadata: options.metadata,
      telemetry: this.telemetryPayload(telemetry),
    };
    dialogResult.sendInit(initPayload);
    const blindSigning = dialogResult.blindSigning;
    this.emitTelemetry("dialog.ready", telemetry, { outcome: "started" });

    // Handle iframe remount: if the dialog re-sends PASSKEY_READY (e.g. due to
    // React strict mode, Next.js Suspense, or code-splitting), resend PASSKEY_INIT
    // so the sign page recovers its state.
    const dialogOrigin = this.getDialogOrigin();
    const handleReReady = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.data?.type === "PASSKEY_READY") {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...initPayload, fullViewport: true, blindSigning },
          dialogOrigin,
        );
      }
    };
    window.addEventListener("message", handleReReady);

    const signingResult = await this.waitForSigningResponse(dialog, iframe, cleanup);

    window.removeEventListener("message", handleReReady);
    cleanup();

    if (signingResult.success) {
      this.emitTelemetry("sign.succeeded", telemetry, {
        outcome: "success",
        attributes: { signingMode: "message" },
      });
      return {
        success: true,
        signature: signingResult.signature,
        signedMessage: options.message,
        signedHash: signingResult.signedHash as `0x${string}` | undefined,
        passkey: signingResult.passkey,
      };
    }

    this.emitTelemetry(
      signingResult.error?.code === "USER_REJECTED" ? "dialog.cancelled" : "sign.failed",
      telemetry,
      {
        outcome: signingResult.error?.code === "USER_REJECTED" ? "cancelled" : "failure",
        ...describeTelemetryError(signingResult.error),
        attributes: { signingMode: "message" },
      },
    );
    return {
      success: false,
      error: signingResult.error,
    };
  }

  /**
   * Sign EIP-712 typed data with the user's passkey
   *
   * This method allows signing structured data following the EIP-712 standard.
   * The typed data is displayed to the user in a human-readable format before signing.
   *
   * @example
   * ```typescript
   * // Sign an ERC-2612 Permit
   * const result = await client.signTypedData({
   *   accountAddress: '0x...',
   *   domain: {
   *     name: 'Dai Stablecoin',
   *     version: '1',
   *     chainId: 1,
   *     verifyingContract: '0x6B175474E89094C44Da98b954EecdeCB5BE3830F',
   *   },
   *   types: {
   *     Permit: [
   *       { name: 'owner', type: 'address' },
   *       { name: 'spender', type: 'address' },
   *       { name: 'value', type: 'uint256' },
   *       { name: 'nonce', type: 'uint256' },
   *       { name: 'deadline', type: 'uint256' },
   *     ],
   *   },
   *   primaryType: 'Permit',
   *   message: {
   *     owner: '0xabc...',
   *     spender: '0xdef...',
   *     value: 1000000000000000000n,
   *     nonce: 0n,
   *     deadline: 1735689600n,
   *   },
   * });
   *
   * if (result.success) {
   *   console.log('Signed hash:', result.signedHash);
   * }
   * ```
   */
  async signTypedData(options: SignTypedDataOptions): Promise<SignTypedDataResult> {
    const telemetry = this.createTelemetryOperation("sign", {
      signingMode: "typedData",
      primaryType: options.primaryType,
      hasAccountAddress: !!options.accountAddress,
    });
    // Compute the EIP-712 hash using viem
    // Use unknown cast to work around viem's strict template literal type requirements
    const signedHash = hashTypedData({
      domain: options.domain,
      types: options.types,
      primaryType: options.primaryType,
      message: options.message,
    } as unknown as TypedDataDefinition);

    const dialogUrl = this.getDialogUrl();
    const params = new URLSearchParams({ mode: "iframe" });
    appendParentOriginParam(params);
    this.appendTelemetryParams(params, telemetry);
    const themeParams = this.getThemeParams(options?.theme);
    if (themeParams) {
      const themeParsed = new URLSearchParams(themeParams);
      themeParsed.forEach((value, key) => params.set(key, value));
    }
    const signingUrl = `${dialogUrl}/dialog/sign?${params.toString()}`;
    const requestedBlindSigning = this.shouldRequestBlindSigning(options);

    const { dialog, iframe, cleanup, reveal } = this.createModalDialog(signingUrl, {
      hidden: requestedBlindSigning,
    });
    this.emitTelemetry("dialog.opened", telemetry, {
      outcome: "started",
      attributes: { blindSigning: requestedBlindSigning },
    });

    const dialogResult = await this.waitForDialogReadyDeferred(dialog, iframe, cleanup, {
      blindSigning: requestedBlindSigning,
      reveal,
    });
    if (!dialogResult.ready) {
      this.emitTelemetry("dialog.cancelled", telemetry, {
        outcome: "cancelled",
        errorCode: "USER_REJECTED",
        errorMessage: "User closed the dialog before signing",
      });
      return {
        success: false,
        error: {
          code: "USER_REJECTED",
          message: "User closed the dialog",
        },
      };
    }

    const initPayload = {
      mode: "iframe",
      signingMode: "typedData",
      typedData: {
        domain: options.domain,
        types: options.types,
        primaryType: options.primaryType,
        message: options.message,
      },
      challenge: signedHash,
      accountAddress: options.accountAddress,
      description: options.description,
      telemetry: this.telemetryPayload(telemetry),
    };
    dialogResult.sendInit(initPayload);
    const blindSigning = dialogResult.blindSigning;
    this.emitTelemetry("dialog.ready", telemetry, { outcome: "started" });

    // Handle iframe remount: if the dialog re-sends PASSKEY_READY (e.g. due to
    // React strict mode, Next.js Suspense, or code-splitting), resend PASSKEY_INIT
    // so the sign page recovers its state.
    const dialogOrigin = this.getDialogOrigin();
    const handleReReady = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.data?.type === "PASSKEY_READY") {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...initPayload, fullViewport: true, blindSigning },
          dialogOrigin,
        );
      }
    };
    window.addEventListener("message", handleReReady);

    const signingResult = await this.waitForSigningResponse(dialog, iframe, cleanup);

    window.removeEventListener("message", handleReReady);
    cleanup();

    if (signingResult.success) {
      this.emitTelemetry("sign.succeeded", telemetry, {
        outcome: "success",
        attributes: { signingMode: "typedData", primaryType: options.primaryType },
      });
      return {
        success: true,
        signature: signingResult.signature,
        signedHash,
        passkey: signingResult.passkey,
      };
    }

    this.emitTelemetry(
      signingResult.error?.code === "USER_REJECTED" ? "dialog.cancelled" : "sign.failed",
      telemetry,
      {
        outcome: signingResult.error?.code === "USER_REJECTED" ? "cancelled" : "failure",
        ...describeTelemetryError(signingResult.error),
        attributes: { signingMode: "typedData", primaryType: options.primaryType },
      },
    );
    return {
      success: false,
      error: signingResult.error,
    };
  }

  async signWithPopup(options: SigningRequestOptions): Promise<SigningResult> {
    const request = await this.createSigningRequest(options, "popup");

    // Use dialogUrl to construct the signing URL (override server's URL)
    const dialogUrl = this.getDialogUrl();
    const signingUrl = `${dialogUrl}/dialog/sign/${request.requestId}?mode=popup`;

    const popup = this.openPopup(signingUrl);
    if (!popup) {
      return {
        success: false,
        error: {
          code: "POPUP_BLOCKED",
          message:
            "Popup was blocked by the browser. Please allow popups for this site.",
        },
      };
    }

    return this.waitForPopupResponse(request.requestId, popup);
  }

  async signWithRedirect(
    options: SigningRequestOptions,
    redirectUrl?: string
  ): Promise<void> {
    const finalRedirectUrl = redirectUrl || this.config.redirectUrl;
    if (!finalRedirectUrl) {
      throw new Error(
        "redirectUrl is required for redirect flow. Pass it to signWithRedirect() or set it in the constructor."
      );
    }

    const request = await this.createSigningRequest(
      options,
      "redirect",
      finalRedirectUrl
    );

    // Use dialogUrl to construct the signing URL (override server's URL)
    const dialogUrl = this.getDialogUrl();
    const signingUrl = `${dialogUrl}/dialog/sign/${request.requestId}?mode=redirect&redirectUrl=${encodeURIComponent(finalRedirectUrl)}`;

    window.location.href = signingUrl;
  }

  async signWithEmbed(
    options: SigningRequestOptions,
    embedOptions: EmbedOptions
  ): Promise<SigningResult> {
    const request = await this.createSigningRequest(options, "embed");

    const iframe = this.createEmbed(request.requestId, embedOptions);

    return this.waitForEmbedResponse(request.requestId, iframe, embedOptions);
  }

  /**
   * Create and append a signing iframe to a caller-supplied container element.
   *
   * Used by the `signWithEmbed` flow where the integrator wants the signing UI
   * to appear inline on their page rather than in a modal overlay. The iframe
   * loads a pre-created signing request URL and fires `options.onReady` once
   * the page has loaded.
   *
   * @param requestId - The signing request ID; used to construct the iframe src
   *   and give it a stable DOM id for later removal via `removeEmbed`.
   * @param options - Embed configuration including the container element,
   *   optional dimensions, and lifecycle callbacks.
   * @returns The created `<iframe>` element (already appended to the container).
   */
  private createEmbed(
    requestId: string,
    options: EmbedOptions
  ): HTMLIFrameElement {
    const dialogUrl = this.getDialogUrl();
    const iframe = document.createElement("iframe");
    iframe.src = `${dialogUrl}/dialog/sign/${requestId}?mode=iframe`;
    iframe.style.width = options.width || DEFAULT_EMBED_WIDTH;
    iframe.style.height = options.height || DEFAULT_EMBED_HEIGHT;
    iframe.style.border = "none";
    iframe.style.borderRadius = "12px";
    iframe.style.boxShadow = "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)";
    iframe.id = `passkey-embed-${requestId}`;
    iframe.allow = "publickey-credentials-get *; publickey-credentials-create *; identity-credentials-get";

    iframe.onload = () => {
      options.onReady?.();
    };

    options.container.appendChild(iframe);

    // Tell the dialog about the parent's viewport so DialogCard can
    // cap itself at 75% of the user's screen instead of auto-growing
    // for long sign flows (e.g. a 20-action transaction). Without
    // this, the dialog falls back to its old infinite-growth
    // behaviour and the scroll-gated Sign button never fires.
    this.wireViewportInfo(iframe);

    // Auto-resize the iframe to the dialog's natural content height.
    // Without this, taller screens (e.g. signup with the email field
    // ~552px) get clipped against the fixed `DEFAULT_EMBED_HEIGHT`, and
    // shorter screens leave dead space below. The dialog measures its
    // card via ResizeObserver and posts `PASSKEY_RESIZE` on every change.
    const host = new URL(dialogUrl);
    const onResize = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;
      if (event.origin !== host.origin) return;
      if (event.data?.type !== "PASSKEY_RESIZE") return;
      const h = Number(event.data.height);
      if (Number.isFinite(h) && h > 0) {
        iframe.style.height = `${h}px`;
      }
    };
    window.addEventListener("message", onResize);
    // ResizeObserver in the iframe keeps firing for the dialog's
    // lifetime; we leave the listener attached until the iframe is
    // removed via `removeEmbed`. No cleanup hook is exposed by
    // `createEmbed`; document a follow-up here if leak becomes an issue.

    return iframe;
  }

  /**
   * Post `PASSKEY_VIEWPORT_INFO` with the parent's
   * `window.innerHeight` to the iframe whenever it signals
   * `PASSKEY_READY`, and re-post on `window.resize` (rAF-debounced)
   * so the dialog's internal max-height cap follows the user's
   * actual screen.
   *
   * Inside the iframe, `vh`/`dvh` units refer to the iframe's own
   * height (recursive when the iframe auto-sizes), so the parent is
   * the only reliable source of "what 75% of the user's screen is".
   * The dialog stores the value in `dialog-context` and falls back
   * to auto-grow when the value is absent — so it's safe to call
   * this even for embedders that don't actually cap (modal mode
   * uses `fullViewport` which has its own outer cap).
   */
  private wireViewportInfo(iframe: HTMLIFrameElement): () => void {
    const dialogOrigin = this.getDialogOrigin();

    const post = () => {
      iframe.contentWindow?.postMessage(
        { type: "PASSKEY_VIEWPORT_INFO", height: window.innerHeight },
        dialogOrigin,
      );
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== dialogOrigin) return;
      if (event.source !== iframe.contentWindow) return;
      if (event.data?.type === "PASSKEY_READY") {
        post();
      }
    };
    window.addEventListener("message", handleMessage);

    // rAF-debounced resize. Coalesces drag-resize bursts to one post
    // per animation frame so we don't spam the iframe with messages.
    let raf: number | null = null;
    const onResize = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        post();
      });
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("resize", onResize);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }

  /**
   * Listen for a signing result from an embedded (inline) signing iframe.
   *
   * Matches incoming `PASSKEY_SIGNING_RESULT` messages against `requestId`
   * to avoid reacting to messages from other iframes on the page. On resolution
   * (success or failure), the iframe is removed from the DOM and
   * `embedOptions.onClose` is invoked.
   *
   * @param requestId - The signing request ID to match against incoming messages.
   * @param iframe - The embedded signing iframe.
   * @param embedOptions - Embed configuration; `onClose` is called after cleanup.
   * @returns A `SigningResult` discriminated union.
   */
  private waitForEmbedResponse(
    requestId: string,
    iframe: HTMLIFrameElement,
    embedOptions: EmbedOptions
  ): Promise<SigningResult> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      const cleanup = () => {
        window.removeEventListener("message", handleMessage);
        iframe.remove();
        embedOptions.onClose?.();
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) {
          return;
        }

        const message = event.data;
        // The Messenger sends: { type, success, data: { requestId, signature }, error }
        const payload = message?.data as { requestId?: string; signature?: WebAuthnSignature } | undefined;

        if (
          message?.type === "PASSKEY_SIGNING_RESULT" &&
          payload?.requestId === requestId
        ) {
          cleanup();

          if (message.success && payload.signature) {
            resolve({
              success: true,
              requestId,
              signature: payload.signature,
            });
          } else {
            resolve({
              success: false,
              requestId,
              error: parseSigningResultError(message.error),
            });
          }
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  removeEmbed(requestId: string): void {
    const iframe = document.getElementById(`passkey-embed-${requestId}`);
    if (iframe) {
      iframe.remove();
    }
  }

  async handleRedirectCallback(): Promise<SigningResult> {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("request_id");
    const status = params.get("status");
    const error = params.get("error");
    const errorMessage = params.get("error_message");

    if (error) {
      return {
        success: false,
        requestId: requestId || undefined,
        error: {
          code: parseSigningResultError({ code: error }).code,
          message: errorMessage || "Unknown error",
        },
      };
    }

    if (!requestId) {
      return {
        success: false,
        error: {
          code: "INVALID_REQUEST",
          message: "No request_id found in callback URL",
        },
      };
    }

    if (status !== "completed") {
      return {
        success: false,
        requestId,
        error: {
          code: "UNKNOWN",
          message: `Unexpected status: ${status}`,
        },
      };
    }

    return this.fetchSigningResult(requestId);
  }


  /**
   * Register a signing request with the passkey service and receive a
   * short-lived `requestId`.
   *
   * Used by the legacy popup, redirect, and embed flows. The passkey service
   * stores the challenge and metadata server-side so the dialog page can fetch
   * them by `requestId` without relying on URL parameters alone. This avoids
   * exposing potentially large challenge payloads in query strings.
   *
   * @param options - The signing options (challenge, accountAddress, description, etc.).
   * @param mode - How the dialog will be opened (`"popup"`, `"redirect"`, or `"embed"`).
   * @param redirectUrl - Only required for `"redirect"` mode; the URL the dialog
   *   will navigate back to after signing.
   * @returns The created signing request with its unique `requestId`.
   * @throws If the API call fails.
   */
  private async createSigningRequest(
    options: SigningRequestOptions,
    mode: "popup" | "redirect" | "embed",
    redirectUrl?: string
  ): Promise<CreateSigningRequestResponse> {
    const response = await fetch(
      `${this.config.providerUrl}/api/sign/request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(this.config.clientId && { clientId: this.config.clientId }),
          accountAddress: options.accountAddress,
          challenge: options.challenge,
          description: options.description,
          metadata: options.metadata,
          transaction: options.transaction,
          mode,
          redirectUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || "Failed to create signing request");
    }

    return response.json();
  }

  /**
   * Open a centered popup window for the signing or auth dialog.
   *
   * Positions the popup near the top of the current window (50px from the top)
   * and horizontally centered relative to the browser window. Uses `popup=true`
   * to request a minimal chrome popup (no address bar) on browsers that support
   * the Window Management API.
   *
   * @param url - The full URL to open in the popup.
   * @returns The popup `Window` reference, or `null` if the browser blocked it.
   */
  private openPopup(url: string): Window | null {
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + 50; // Near top of window

    return window.open(
      url,
      "passkey-signing",
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=true`
    );
  }

  /**
   * Wait for the dialog iframe to signal ready, then immediately send init data.
   *
   * This is the synchronous-init variant: `initMessage` is available at call
   * time, so it can be posted as soon as `PASSKEY_READY` arrives. Use
   * `waitForDialogReadyDeferred` when init data depends on an in-flight API call.
   *
   * Also handles early close (X button, escape key, backdrop click) during the
   * ready phase so those paths resolve cleanly without leaking event listeners.
   *
   * Timeout: resolves `false` after 10 seconds if the iframe never signals ready
   * (e.g. origin mismatch or network error loading the dialog app).
   *
   * @param dialog - The `<dialog>` element wrapping the iframe.
   * @param iframe - The iframe element that will receive `PASSKEY_INIT`.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   * @param initMessage - The `PASSKEY_INIT` payload to send when ready.
   * @returns `true` if the dialog is ready and init was sent; `false` if the
   *   dialog was closed or timed out before becoming ready.
   */
  private waitForDialogReady(
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    initMessage: Record<string, unknown>,
    options: { blindSigning?: boolean; reveal?: () => void } = {},
  ): Promise<boolean> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      let settled = false;

      const teardown = () => {
        if (settled) return;
        settled = true;
        clearTimeout(readyTimeout);
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        if (event.data?.type === "PASSKEY_READY") {
          const blindSigning = options.blindSigning === true;
          teardown();
          if (blindSigning) iframe.focus({ preventScroll: true });
          iframe.contentWindow?.postMessage({
            type: "PASSKEY_INIT",
            ...initMessage,
            fullViewport: true,
            blindSigning,
          }, dialogOrigin);
          resolve(true);
        } else if (
          event.data?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // Cross-frame guard (same as waitForDialogReadyDeferred): a stray
          // PASSKEY_CLOSE from a sibling same-origin dialog must not resolve
          // this dialog's ready phase as closed-before-ready.
          teardown();
          cleanup();
          resolve(false);
        }
      };

      // Handle escape key / backdrop click which call cleanup() -> dialog.close()
      const handleClose = () => {
        teardown();
        resolve(false);
      };

      // Timeout: if dialog never signals ready (e.g. origin mismatch), clean up
      const readyTimeout = setTimeout(() => {
        teardown();
        cleanup();
        resolve(false);
      }, 10000);

      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Create and open a full-viewport `<dialog>` containing a passkey iframe.
   *
   * The SDK owns the browser `<dialog>` plus a minimal generic preload shell
   * that appears immediately on click. The passkey iframe owns all branded and
   * origin-specific UI (TitleBar, trust icon, action cards); the shell is
   * removed as soon as the iframe reports that its centered card has painted.
   *
   * Lifecycle:
   *   1. A themed generic preload shell is injected and shown immediately
   *      while the iframe loads. It intentionally contains no TitleBar or
   *      origin chrome so that UI has a single implementation in apps/passkey.
   *   2. When the iframe sends `PASSKEY_RENDERED`, the overlay is removed and
   *      the iframe becomes fully visible in the same frame.
   *   3. The returned `cleanup` function tears down all event listeners,
   *      disconnects the MutationObserver, closes the `<dialog>`, and removes
   *      it from the DOM. It is idempotent — safe to call multiple times.
   *
   * Browser quirks handled:
   *   - The 1Password extension sets `inert` on the `<dialog>`, making it
   *     non-interactive. A MutationObserver removes the attribute immediately.
   *   - Password managers may also set `inert` on dialog siblings; cleanup
   *     restores those as well.
   *   - The iframe sandbox includes `allow-popups-to-escape-sandbox` so that
   *     1Password's popup UI can render outside the sandboxed context.
   *   - `allow-downloads` lets the passkey iframe trigger the recovery-key
   *     JSON fallback download without sending sensitive data to the parent.
   *
   * @param url - The full URL (including query params) to load in the iframe.
   * @returns References to the dialog, iframe, and a cleanup function.
   */
  private createModalDialog(url: string, options?: { persistent?: boolean; hidden?: boolean }): {
    dialog: HTMLDialogElement;
    iframe: HTMLIFrameElement;
    cleanup: () => void;
    destroy: () => void;
    reveal: () => void;
  } {
    const dialogUrl = this.getDialogUrl();
    const hostUrl = new URL(dialogUrl);

    const urlParams = new URL(url, window.location.href).searchParams;
    const { background: backdropBackground, blur: backdropBlur } =
      resolveBackdropStyle(urlParams);
    const themeMode = urlParams.get("theme") || "light";
    const isDark =
      themeMode === "dark" ||
      (themeMode !== "light" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    const theme = isDark ? DARK_TOKENS : LIGHT_TOKENS;

    const dialog = document.createElement("dialog");
    dialog.dataset.passkey = "";
    if (options?.hidden) {
      dialog.dataset.passkeyHidden = "";
    }
    dialog.style.opacity = "1";
    dialog.style.background = "transparent";
    document.body.appendChild(dialog);

    const style = document.createElement("style");
    style.textContent = `
      dialog[data-passkey] {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-width: none;
        max-height: none;
        margin: 0;
        padding: 0;
        border: none;
        background: transparent;
        color-scheme: normal;
        outline: none;
        overflow: hidden;
        pointer-events: auto;
      }
      dialog[data-passkey]::backdrop {
        background: transparent !important;
      }
      dialog[data-passkey][data-passkey-hidden] {
        width: 1px;
        height: 1px;
        max-width: 1px;
        max-height: 1px;
        opacity: 0;
        pointer-events: none;
      }
      dialog[data-passkey][data-passkey-hidden] iframe {
        width: 1px;
        height: 1px;
        opacity: 0 !important;
        pointer-events: none;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
      }
      dialog[data-passkey] [data-passkey-overlay] {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${backdropBackground};
        backdrop-filter: blur(${backdropBlur}px);
        -webkit-backdrop-filter: blur(${backdropBlur}px);
        z-index: 1;
        pointer-events: none;
      }
      dialog[data-passkey][data-passkey-hidden] [data-passkey-overlay] {
        display: none;
      }
      dialog[data-passkey] [data-passkey-preload-card] {
        width: ${LOADING_MODAL_WIDTH}px;
        max-width: 100%;
        overflow: hidden;
        border-radius: ${LOADING_MODAL_RADIUS}px;
        background: ${theme.bgPrimary};
        box-shadow: 0 24px 80px rgba(0, 0, 0, 0.24), 0 2px 12px rgba(0, 0, 0, 0.12);
        font-family: ${LOADING_FONT_FAMILY};
        line-height: ${LOADING_LINE_HEIGHT};
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      dialog[data-passkey] [data-passkey-preload-body] {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        gap: ${LOADING_BODY_GAP}px;
        padding: ${LOADING_MODAL_PADDING}px;
        border-radius: ${LOADING_MODAL_RADIUS}px;
        background: ${theme.bgPrimary};
      }
      dialog[data-passkey] [data-passkey-preload-spinner-wrap] {
        display: flex;
        align-items: center;
        padding: ${LOADING_ICON_PADDING}px;
      }
      dialog[data-passkey] [data-passkey-preload-spinner] {
        position: relative;
        width: ${SPINNER_SIZE}px;
        height: ${SPINNER_SIZE}px;
      }
      dialog[data-passkey] [data-passkey-preload-spinner] svg {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        color: ${BRAND_PURPLE};
        animation: _1auth-spin ${SPINNER_SPIN_DURATION_MS}ms linear infinite;
      }
      dialog[data-passkey] [data-passkey-preload-copy] {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        gap: ${LOADING_TITLE_BLOCK_GAP}px;
        text-align: center;
      }
      dialog[data-passkey] [data-passkey-preload-title] {
        margin: 0;
        color: ${theme.textPrimary};
        font-size: ${LOADING_TITLE_FONT_SIZE}px;
        font-weight: ${LOADING_TITLE_FONT_WEIGHT};
        line-height: ${LOADING_LINE_HEIGHT};
      }
      dialog[data-passkey] [data-passkey-preload-subtitle] {
        margin: 0;
        color: ${theme.textSecondary};
        font-size: ${LOADING_SUBTITLE_FONT_SIZE}px;
        font-weight: ${LOADING_SUBTITLE_FONT_WEIGHT};
        line-height: ${LOADING_LINE_HEIGHT};
      }
      @media (max-width: 639px) {
        dialog[data-passkey] [data-passkey-overlay] {
          align-items: flex-end;
        }
        dialog[data-passkey] [data-passkey-preload-card] {
          width: 100%;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
        }
        dialog[data-passkey] [data-passkey-preload-body] {
          padding-bottom: calc(${LOADING_MODAL_PADDING}px + env(safe-area-inset-bottom));
        }
      }
      @keyframes _1auth-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      dialog[data-passkey] iframe {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
        background-color: transparent;
        color-scheme: normal;
        pointer-events: auto;
        backdrop-filter: blur(${backdropBlur}px);
        -webkit-backdrop-filter: blur(${backdropBlur}px);
        transition: none;
      }
    `;
    dialog.appendChild(style);

    const overlay = document.createElement("div");
    overlay.dataset.passkeyOverlay = "";

    const preloadCard = document.createElement("div");
    preloadCard.dataset.passkeyPreloadCard = "";

    const preloadBody = document.createElement("div");
    preloadBody.dataset.passkeyPreloadBody = "";
    const spinnerWrap = document.createElement("div");
    spinnerWrap.dataset.passkeyPreloadSpinnerWrap = "";
    const spinner = document.createElement("div");
    spinner.dataset.passkeyPreloadSpinner = "";
    const spinnerCenter = SPINNER_SIZE / 2;
    const spinnerGap = SPINNER_CIRCUMFERENCE - SPINNER_ARC;
    spinner.innerHTML =
      `<svg viewBox="0 0 ${SPINNER_SIZE} ${SPINNER_SIZE}" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
      `<circle cx="${spinnerCenter}" cy="${spinnerCenter}" r="${SPINNER_RADIUS}" stroke="currentColor" opacity="0.3" stroke-width="${SPINNER_STROKE}"/>` +
      `<circle cx="${spinnerCenter}" cy="${spinnerCenter}" r="${SPINNER_RADIUS}" stroke="currentColor" stroke-width="${SPINNER_STROKE}" stroke-linecap="round" stroke-dasharray="${SPINNER_ARC} ${spinnerGap}" transform="rotate(-90 ${spinnerCenter} ${spinnerCenter})"/>` +
      `</svg>`;
    spinnerWrap.appendChild(spinner);

    const preloadCopy = document.createElement("div");
    preloadCopy.dataset.passkeyPreloadCopy = "";
    const preloadTitle = document.createElement("p");
    preloadTitle.dataset.passkeyPreloadTitle = "";
    preloadTitle.textContent = LOADING_TITLE;
    const preloadSubtitle = document.createElement("p");
    preloadSubtitle.dataset.passkeyPreloadSubtitle = "";
    preloadSubtitle.textContent = LOADING_SUBTITLE;
    preloadCopy.append(preloadTitle, preloadSubtitle);
    preloadBody.append(spinnerWrap, preloadCopy);
    preloadCard.appendChild(preloadBody);
    overlay.appendChild(preloadCard);
    dialog.appendChild(overlay);

    // Create full-viewport iframe
    const iframe = document.createElement("iframe");
    iframe.setAttribute(
      "allow",
      [
        "payment",
        `publickey-credentials-get ${hostUrl.origin}`,
        `publickey-credentials-create ${hostUrl.origin}`,
        "clipboard-write",
        "identity-credentials-get",
      ].join("; "),
    );
    iframe.setAttribute("aria-label", "Passkey Authentication");
    iframe.setAttribute("tabindex", "0");
    // Sandbox with allow-same-origin preserves the iframe's real origin for
    // WebAuthn and cookies. allow-popups-to-escape-sandbox lets 1Password's
    // browser extension render its popup UI outside the sandboxed context.
    // allow-downloads permits same-iframe recovery JSON export.
    iframe.setAttribute(
      "sandbox",
      "allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads",
    );
    iframe.setAttribute("title", "Passkey");
    // The SDK shell above is visible immediately. Keep the iframe at opacity 0
    // until PASSKEY_RENDERED so the user never sees the pre-layout first paint
    // (the dialog page SSRs with fullViewport=false, so its very first client
    // paint can be the embedded full-width card before the URL-seeded
    // re-render centres it).
    iframe.style.opacity = "0";

    // Reveal the iframe once it has painted and drop the SDK shell in the same
    // frame. The shell exists only to make click-to-visual immediate while the
    // cross-origin iframe boots; once PASSKEY_RENDERED lands, the iframe owns
    // all visible UI.
    // Register the message listener before assigning `src`: warmed auth
    // routes can render and post PASSKEY_RENDERED very quickly, and missing
    // that one-shot signal would leave the iframe invisible indefinitely.
    let revealed = false;
    // Armed when PASSKEY_READY arrives but PASSKEY_RENDERED hasn't yet — see
    // the handler below. Cleared on reveal so a late RENDERED / teardown
    // doesn't leave a dangling timer.
    let readyFailsafe: ReturnType<typeof setTimeout> | undefined;
    const revealIframe = () => {
      if (revealed) return;
      revealed = true;
      clearTimeout(readyFailsafe);
      iframe.style.opacity = "1";
      overlay.style.display = "none";
    };
    // Failsafe: if PASSKEY_RENDERED never arrives (redirect race, message
    // dropped, iframe crash) we don't want the iframe stuck invisible forever.
    // After 8s the iframe has either rendered something the user can interact
    // with or is broken in a way that staying hidden can't help with — either
    // way, revealing is strictly better than a blank modal. 8s is well above
    // the normal sub-second iframe-load path so it never preempts the real
    // PASSKEY_RENDERED signal in healthy flows.
    const revealFailsafe = setTimeout(revealIframe, 8000);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== hostUrl.origin) return;
      // Pin to THIS modal's iframe. Origin alone isn't enough: other
      // same-origin dialog frames can exist on the page (a parked `prewarm()`
      // frame, a second SDK instance, the persistent sign-eoa iframe). Without
      // this, a stray RENDERED/READY from one of them could reveal this modal's
      // iframe before its own content has painted, flashing a blank or
      // half-rendered dialog. (`prewarm`'s warm route is message-silent, so
      // this is defense in depth rather than its only guard.)
      if (event.source !== iframe.contentWindow) return;
      // PASSKEY_RENDERED is the ONLY signal that the iframe has painted its
      // final, correct layout: DialogShell / DialogCard post it after a
      // double-rAF, i.e. once the centered fullViewport card is on screen.
      if (event.data?.type === "PASSKEY_RENDERED") {
        revealIframe();
      }
      // PASSKEY_READY fires on mount, BEFORE the iframe has painted the
      // fullViewport layout. The dialog page server-renders with
      // `fullViewport=false` (no `window` on the server), so its first client
      // paint is the embedded, full-width Loading card; the centered layout
      // only lands after the URL-seeded client re-render. Revealing the
      // iframe on READY therefore flashes that full-width card for a frame or
      // two — the exact "loading screen flash" we're avoiding. So READY does
      // NOT reveal directly; it only arms a short fallback in case RENDERED is
      // lost (redirect remount, dropped frame). By the time this fires the
      // layout has long settled, so we still never expose the full-width card —
      // and we recover far faster than the 8s `revealFailsafe`. Every modal
      // route (auth/connect/account/grant-permission/sign/sign-eoa) reliably
      // posts RENDERED in fullViewport mode, so in healthy flows this never
      // fires.
      else if (
        event.data?.type === "PASSKEY_READY" &&
        !revealed &&
        readyFailsafe === undefined
      ) {
        readyFailsafe = setTimeout(revealIframe, 1000);
      }
      // Application-session cleanup is handled by the client-wide listener so
      // popup and iframe transports share one idempotent path.
    };
    window.addEventListener("message", handleMessage);

    // Append `fullViewport=1` so the iframe's DialogProvider can seed its
    // initial state synchronously. Without this, `fullViewport` defaults to
    // false until PASSKEY_INIT arrives by postMessage — and on slow runs the
    // first paint of Loading.tsx renders as an embedded (full-width, top-of-
    // iframe) card before the message corrects it. Since we reveal the iframe
    // on PASSKEY_RENDERED, that wrong layout would be briefly visible.
    // Reading the flag from the URL eliminates the race.
    const iframeUrl = new URL(url, window.location.href);
    iframeUrl.searchParams.set("fullViewport", "1");
    iframe.setAttribute("src", iframeUrl.toString());
    dialog.appendChild(iframe);

    // Tell the dialog about the parent viewport so its DialogCard can
    // cap at 75% of screen height. Modal flow sets `fullViewport:
    // true` (which uses its own `calc(100dvh - 100px)` outer cap and
    // makes this no-op), but we wire it up defensively so any future
    // non-fullViewport modal route picks up the same gating without
    // a separate SDK change.
    const viewportInfoCleanup = this.wireViewportInfo(iframe);

    // 1Password extension adds `inert` attribute to <dialog>, making it
    // non-interactive. Watch for this and remove it immediately.
    const inertObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "inert") {
          dialog.removeAttribute("inert");
        }
      }
    });
    inertObserver.observe(dialog, { attributes: true });

    // Handle escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cleanup();
      }
    };
    document.addEventListener("keydown", handleEscape);

    const reveal = () => {
      delete dialog.dataset.passkeyHidden;
      if (!revealed) {
        revealed = true;
        clearTimeout(readyFailsafe);
        clearTimeout(revealFailsafe);
      }
      iframe.style.opacity = "1";
      overlay.style.display = "none";
    };

    // Re-assert `target` as the host scroll position across the browser's
    // focus-restore scroll. That scroll fires when a focus-stealing child
    // window closes (the `/sign-ceremony` popup, a PM popout, an OAuth popup)
    // OR when `dialog.close()` hands focus back to the element that opened the
    // modal — and it does NOT reliably land within a fixed rAF or two: some
    // browser×password-manager combos scroll several frames late, so a one-shot
    // restore loses the race and the page snaps (usually to the top). Instead
    // correct every frame for a short budget — once the browser's scroll lands
    // we snap it straight back, and every other frame is a cheap no-op. The
    // budget is short enough never to fight a deliberate user scroll, and while
    // it runs the modal is either up (host isn't scrollable behind it) or was
    // just torn down (position must be held through the close).
    // ponytail: 18-frame (~300ms) budget — bump only if a browser restores later.
    const RESTORE_FRAME_BUDGET = 18;
    const holdScroll = (target: number) => {
      let frames = 0;
      const tick = () => {
        if (window.scrollY !== target) {
          window.scrollTo({ top: target, left: window.scrollX, behavior: "auto" });
        }
        if (++frames < RESTORE_FRAME_BUDGET) requestAnimationFrame(tick);
      };
      tick();
    };

    // Snapshot the settled scroll (after any host-app re-centering) when a
    // focus-stealing child window opened *by the passkey iframe while this
    // dialog is open* blurs the host — chiefly its top-level `/sign-ceremony`
    // popup (browser×password-manager combos that can't run WebAuthn inline)
    // and OAuth popups — then re-apply it when focus returns. The SDK's iframe
    // never leaves the DOM, so an integrator's own dialog-lifecycle scroll
    // handling never fires. Gated on `dialog.open` so a closed persistent
    // dialog never hijacks page scroll; the teardown path (see `destroy`)
    // covers the symmetric snap when the dialog itself closes.
    let scrollBeforeBlur: number | null = null;
    const onHostBlur = () => {
      if (dialog.open) scrollBeforeBlur = window.scrollY;
    };
    const onHostFocus = () => {
      if (!dialog.open || scrollBeforeBlur === null) return;
      holdScroll(scrollBeforeBlur);
    };
    window.addEventListener("blur", onHostBlur);
    window.addEventListener("focus", onHostFocus);

    // Hidden blind-signing requests must not make the host page inert. They
    // still need a rendered iframe so the passkey origin can run WebAuthn.
    if (options?.hidden) {
      dialog.show();
    } else {
      dialog.showModal();
    }

    let cleanedUp = false;
    const destroy = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      clearTimeout(revealFailsafe);
      clearTimeout(readyFailsafe);
      inertObserver.disconnect();
      viewportInfoCleanup();
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("blur", onHostBlur);
      window.removeEventListener("focus", onHostFocus);
      document.removeEventListener("keydown", handleEscape);
      // Preserve host scroll across the close's focus-restore. Closing a modal
      // <dialog> returns focus to whatever opened it (e.g. the integrator's
      // trigger button, which after a pre-dialog re-center can sit near the top
      // of the page), and the browser scroll-restores to that element — snapping
      // the page to the top the moment the card vanishes. The blur/focus path
      // above is gated on `dialog.open`, so it cannot cover this. Skip hidden
      // (blind-signing) frames: off-screen, with no user scroll position to hold.
      const scrollAtClose = !options?.hidden && dialog.open ? window.scrollY : null;
      dialog.close();
      // Clean up 1Password inert attributes left on dialog siblings
      if (dialog.parentNode) {
        for (const sibling of Array.from(dialog.parentNode.children)) {
          if (sibling !== dialog && sibling instanceof HTMLElement && sibling.hasAttribute("inert")) {
            sibling.removeAttribute("inert");
          }
        }
      }
      dialog.remove();
      if (scrollAtClose !== null) holdScroll(scrollAtClose);
    };

    // In persistent mode the iframe lives across many open/close cycles
    // (used by `requestWithWallet` so the WalletConnect SignClient inside
    // the iframe is not recreated for every EOA request). `cleanup` here
    // just hides the dialog; full teardown is reachable via `destroy`.
    const cleanup = options?.persistent
      ? () => {
          if (!dialog.open) return;
          // Same close-time focus-restore snap as `destroy`; hold scroll here
          // too so hiding a persistent dialog doesn't jump the host page.
          const scrollAtClose = options?.hidden ? null : window.scrollY;
          dialog.close();
          if (scrollAtClose !== null) holdScroll(scrollAtClose);
        }
      : destroy;

    return { dialog, iframe, cleanup, destroy, reveal };
  }

  /**
   * Listen for the auth result from the modal dialog.
   *
   * Each modal call generates a per-session nonce that the iframe must
   * echo back in `PASSKEY_CLOSE` / `PASSKEY_LOGIN_RESULT` /
   * `PASSKEY_RETRY_POPUP` messages. Without an echo, a queued CLOSE
   * from the previous iframe could be dispatched to the new modal's
   * listener (postMessage events tagged with `event.source` from a now-
   * destroyed iframe still reach the parent's message handler) and
   * silently cancel the fresh auth attempt. The nonce makes that race
   * harmless: a stale message that doesn't carry the current modal's
   * nonce is ignored.
   *
   * Also handles the `PASSKEY_RETRY_POPUP` message: sent by the dialog when a
   * password manager (e.g. Bitwarden) intercepts the WebAuthn call inside the
   * iframe. In that case the SDK seamlessly re-opens a standalone popup window,
   * where WebAuthn works without cross-origin iframe restrictions, and waits
   * for the result from there instead.
   *
   * @param _dialog - Unused; kept for signature consistency.
   * @param iframe - The iframe element; used to send `PASSKEY_INIT` on ready.
   * @param cleanup - Idempotent teardown for the modal dialog.
   * @returns An `AuthResult` discriminated union.
   */
  private waitForModalAuthResponse(
    _dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    requestId?: string
  ): Promise<AuthResult> {
    const dialogOrigin = this.getDialogOrigin();
    // Per-modal nonce so stale messages from a previous iframe can't
    // cancel a fresh auth attempt. The dialog echoes this back on every
    // result-bearing message; messages without the matching nonce are
    // ignored.
    const modalNonce = generateModalNonce();
    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;
        const data = event.data;

        const matchesSource = event.source === iframe.contentWindow;
        const matchesRequest =
          typeof requestId === "string" && data?.requestId === requestId;
        const matchesNonce = data?.modalNonce === modalNonce;
        const isCurrentFrameClose =
          data?.type === "PASSKEY_CLOSE" && matchesSource;

        if (
          data?.type === "PASSKEY_CLOSE" &&
          (matchesRequest || matchesNonce || isCurrentFrameClose)
        ) {
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            error: {
              code: "USER_CANCELLED",
              message: "Authentication was cancelled",
            },
          });
          return;
        }

        // Send init message once the dialog signals it's ready to receive
        // it; many entry routes fire this from their first useEffect.
        // The nonce travels with INIT and the dialog stores it for later
        // echo on CLOSE / RESULT messages.
        if (data?.type === "PASSKEY_READY") {

          iframe.contentWindow?.postMessage({
            type: "PASSKEY_INIT",
            mode: "iframe",
            fullViewport: true,
            modalNonce,
          }, dialogOrigin);
          return;
        }

        const hasModalNonce = typeof data?.modalNonce === "string";
        const isCurrentDialogMessage =
          matchesSource || matchesNonce || !hasModalNonce;
        if (!isCurrentDialogMessage) return;

        // Result-bearing messages without the current modal's nonce are
        // late-arriving CLOSE / RESULT events from a previously cleaned-
        // up iframe (postMessage queue, tab-throttling, etc.). Drop them
        // so they can't cancel or hijack the fresh modal.
        if (data?.modalNonce && data.modalNonce !== modalNonce) return;

        if (data?.type === "PASSKEY_LOGIN_RESULT") {
          window.removeEventListener("message", handleMessage);
          cleanup();

          if (data.success) {
            resolve({
              success: true,
              user: {
                id: data.data?.user?.id,
                address: data.data?.address as `0x${string}`,
              },
              signerType: data.data?.signerType ?? "passkey",
            });
          } else {
            resolve({
              success: false,
              error: data.error,
            });
          }
        } else if (data?.type === "PASSKEY_RETRY_POPUP") {
          // Password manager (e.g. Bitwarden) interfered with WebAuthn in iframe
          // Retry in popup mode where WebAuthn works without cross-origin restrictions
          window.removeEventListener("message", handleMessage);
          cleanup();

          // Get the current dialog URL and switch to popup mode
          const popupUrl = data.data?.url?.replace("mode=iframe", "mode=popup")
            || `${this.getDialogUrl()}/dialog/auth?mode=popup${this.config.clientId ? `&clientId=${this.config.clientId}` : ''}`;

          // Open popup and wait for result
          this.waitForPopupAuthResponse(popupUrl).then(resolve);
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Open a popup for auth and wait for the result.
   * Used when iframe mode fails (e.g., due to password manager interference).
   */
  private waitForPopupAuthResponse(url: string): Promise<AuthResult> {
    const dialogOrigin = this.getDialogOrigin();
    const modalNonce = generateModalNonce();
    const popup = this.openPopup(url);

    if (!popup) {
      return Promise.resolve({
        success: false,
        error: {
          code: "USER_CANCELLED",
          message: "Authentication was cancelled",
        },
      });
    }

    return new Promise((resolve) => {
      let settled = false;

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin || event.source !== popup) return;

        const data = event.data;
        if (data?.type === "PASSKEY_READY") {
          popup.postMessage({
            type: "PASSKEY_INIT",
            mode: "popup",
            fullViewport: true,
            modalNonce,
          }, dialogOrigin);
          return;
        }

        if (data?.type === "PASSKEY_LOGIN_RESULT") {
          finish(
            data.success
              ? {
                  success: true,
                  user: {
                    id: data.data?.user?.id,
                    address: data.data?.address as `0x${string}`,
                  },
                  signerType: data.data?.signerType ?? "passkey",
                }
              : { success: false, error: data.error },
            true,
          );
        } else if (data?.type === "PASSKEY_CLOSE") {
          finish({
            success: false,
            error: {
              code: "USER_CANCELLED",
              message: "Authentication was cancelled",
            },
          }, true);
        }
      };

      const finish = (result: AuthResult, closePopup: boolean) => {
        if (settled) return;
        settled = true;
        clearInterval(pollTimer);
        window.removeEventListener("message", handleMessage);
        if (closePopup) popup.close();
        resolve(result);
      };

      window.addEventListener("message", handleMessage);

      // Poll because users can close the popup without sending PASSKEY_CLOSE.
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          finish({
            success: false,
            error: {
              code: "USER_CANCELLED",
              message: "Authentication was cancelled",
            },
          }, false);
        }
      }, 500);
    });
  }

  /**
   * Listen for the connect result from the connect dialog.
   *
   * The connect flow is a lightweight "are you still you?" confirmation that
   * does not require a new passkey ceremony. On success, the dialog returns the
   * user's address and an `autoConnected` flag indicating whether the
   * user had previously enabled auto-connect (in which case no UI was shown).
   *
   * On failure with `action: "switch"`, the caller should redirect to the full
   * auth flow because the user has no cached session to confirm.
   *
   * @param _dialog - Unused; kept for signature consistency.
   * @param _iframe - Unused; kept for signature consistency.
   * @param cleanup - Idempotent teardown for the modal dialog.
   * @returns A `ConnectResult` discriminated union.
   */
  private waitForConnectResponse(
    _dialog: HTMLDialogElement,
    _iframe: HTMLIFrameElement,
    cleanup: () => void
  ): Promise<ConnectResult> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const data = event.data;
        if (data?.type === "PASSKEY_CONNECT_RESULT") {
          window.removeEventListener("message", handleMessage);
          cleanup();

          if (data.success) {
            resolve({
              success: true,
              user: {
                address: data.data?.address as `0x${string}`,
              },
              // Carry the signer type through so the SDK/provider can route EOA
              // sessions to the connected wallet (plain tx, no intents) instead
              // of defaulting to passkey intents. Absent = legacy passkey.
              signerType: data.data?.signerType ?? "passkey",
              autoConnected: data.data?.autoConnected,
            });
          } else {
            resolve({
              success: false,
              action: data.data?.action,
              error: data.error,
            });
          }
        } else if (data?.type === "PASSKEY_CLOSE") {
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            action: "cancel",
            error: {
              code: "USER_CANCELLED",
              message: "Connection was cancelled",
            },
          });
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Listen for grant-permission results and bridge sponsorship token requests.
   *
   * The grant iframe owns the passkey ceremony and install intent submission.
   * Extension-token minting stays in the app origin, so the SDK exposes a
   * narrow postMessage round-trip that never gives the iframe app secrets.
   *
   * @param _dialog - Unused; kept for signature consistency.
   * @param iframe - The grant iframe that receives extension-token responses.
   * @param cleanup - Idempotent teardown for the modal dialog.
   * @param sponsor - Whether this grant flow should request an extension token.
   * @returns A `GrantPermissionsResult` discriminated union.
   */
  private waitForGrantPermissionResponse(
    dialog: HTMLDialogElement,
    iframe: HTMLIFrameElement,
    cleanup: () => void,
    sponsor: boolean,
    initPayload: Record<string, unknown>
  ): Promise<GrantPermissionsResult> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      let readySeen = false;

      const teardown = () => {
        clearTimeout(readyTimeout);
        window.removeEventListener("message", handleMessage);
        dialog.removeEventListener("close", handleClose);
      };

      const postInit = () => {
        iframe.contentWindow?.postMessage(
          { type: "PASSKEY_INIT", ...initPayload, fullViewport: true },
          dialogOrigin,
        );
      };

      const handleClose = () => {
        teardown();
        resolve({
          success: false,
          error: {
            code: "USER_CANCELLED",
            message: "User closed the dialog",
          },
        });
      };

      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const data = event.data;

        if (data?.type === "PASSKEY_READY") {
          readySeen = true;
          clearTimeout(readyTimeout);
          // Grant iframe routes can remount in dev/HMR and after Suspense
          // boundaries. Re-send init on every READY so the review page never
          // gets stranded on its initial loading state.
          postInit();
          return;
        }

        if (data?.type === "PASSKEY_GRANT_EXTENSION_TOKEN_REQUEST") {
          const requestId = data.requestId as string | undefined;
          const intentOp = data.intentOp as string | undefined;
          const postResponse = (payload: Record<string, unknown>) => {
            iframe.contentWindow?.postMessage(
              { type: "PASSKEY_GRANT_EXTENSION_TOKEN_RESPONSE", requestId, ...payload },
              dialogOrigin
            );
          };

          if (!requestId || !intentOp) {
            postResponse({ success: false, error: "Invalid extension token request" });
            return;
          }

          if (!sponsor) {
            postResponse({ success: true });
            return;
          }

          if (!this.sponsorship) {
            postResponse({ success: false, error: MISSING_APP_CREDENTIALS_MESSAGE });
            return;
          }

          try {
            const token = await this.sponsorship.getExtensionToken(intentOp);
            postResponse({ success: true, token });
          } catch (err) {
            postResponse({
              success: false,
              error: err instanceof Error ? err.message : String(err),
            });
          }
          return;
        }

        if (data?.type === "PASSKEY_GRANT_PERMISSION_RESULT") {
          if (data.success) {
            // Resolve the grant result but DELIBERATELY keep the dialog open.
            // The grant dialog's `success` state renders a "Permission granted"
            // confirmation with a green Done button (mirroring the sign flow's
            // `confirmed` screen). Calling cleanup() here — as we still do on
            // the failure path — would destroy that iframe before it paints, so
            // the user would never see a success screen. Instead we just stop
            // listening for grant messages; grantPermissions() then awaits
            // waitForDialogClose() so the confirmation stays up until the user
            // clicks Done (PASSKEY_CLOSE). This matches sendIntent, where
            // waitForSigningResponse resolves on success without cleanup and the
            // caller awaits the dialog close.
            teardown();
            resolve({ success: true, ...(data.data ?? {}) });
            return;
          }

          // Failure: there's no success screen to preserve, and the passkey app
          // sends PASSKEY_CLOSE right after this RESULT — but teardown() removes
          // the listener, so that follow-up would be dropped. Close here, the
          // same way the sign failure path does.
          teardown();
          cleanup();
          resolve({
            success: false,
            error: data.error ?? data.data?.error ?? {
              code: "GRANT_PERMISSION_FAILED",
              message: "Permission grant failed",
            },
          });
        } else if (
          data?.type === "PASSKEY_CLOSE" &&
          event.source === iframe.contentWindow
        ) {
          // Same cross-frame guard as the sign/ready paths: a stray
          // PASSKEY_CLOSE from another same-origin dialog (e.g. an auth modal
          // tearing down) must not cancel an in-flight grant. Only honor the
          // close from THIS grant iframe.
          teardown();
          cleanup();
          resolve({
            success: false,
            error: {
              code: "USER_CANCELLED",
              message: "User closed the dialog",
            },
          });
        }
      };

      const readyTimeout = setTimeout(() => {
        if (readySeen) return;
        teardown();
        cleanup();
        resolve({
          success: false,
          error: {
            code: "USER_CANCELLED",
            message: "User closed the dialog",
          },
        });
      }, 10000);

      window.addEventListener("message", handleMessage);
      dialog.addEventListener("close", handleClose);
    });
  }

  /**
   * Listen for a signing result from a server-request-based modal dialog.
   *
   * Similar to `waitForIntentSigningResponse` but intended for the older
   * `signWithModal` path that pre-creates a signing request via the API (rather
   * than inlining all data in `PASSKEY_INIT`). Filters by `requestId` to handle
   * the case where stale messages from a previous dialog may still be in flight.
   *
   * @param requestId - The signing request ID to match against incoming messages.
   * @param _dialog - Unused; kept for signature consistency.
   * @param _iframe - Unused; kept for signature consistency.
   * @param cleanup - Idempotent teardown that closes and removes the dialog.
   * @returns A `SigningResult` discriminated union.
   */
  private waitForModalSigningResponse(
    requestId: string,
    _dialog: HTMLDialogElement,
    _iframe: HTMLIFrameElement,
    cleanup: () => void
  ): Promise<SigningResult> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) return;

        const message = event.data;
        // The Messenger sends: { type, success, data: { requestId, signature }, error }
        // So we need to check message.data.requestId, not message.requestId
        const payload = message?.data as { requestId?: string; signature?: WebAuthnSignature } | undefined;

		if (message?.type === "PASSKEY_DISCONNECT") {
			window.removeEventListener("message", handleMessage);
			cleanup();
			resolve({
				success: false,
				requestId,
				error: { code: "USER_REJECTED", message: "Session disconnected" },
			});
			return;
		}

        if (message?.type === "PASSKEY_SIGNING_RESULT" && payload?.requestId === requestId) {
          window.removeEventListener("message", handleMessage);
          cleanup();

          if (message.success && payload.signature) {
            resolve({
              success: true,
              requestId,
              signature: payload.signature,
            });
          } else {
            resolve({
              success: false,
              requestId,
              error: parseSigningResultError(message.error),
            });
          }
        } else if (message?.type === "PASSKEY_CLOSE") {
          window.removeEventListener("message", handleMessage);
          cleanup();
          resolve({
            success: false,
            requestId,
            error: {
              code: "USER_REJECTED",
              message: "Signing was cancelled",
            },
          });
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Listen for a signing result from a popup window.
   *
   * Polls for popup closure every 500 ms as a fallback to detect when the user
   * closes the popup without completing the signing flow (no `PASSKEY_CLOSE`
   * message is fired in that case because the popup's unload event cannot
   * reliably postMessage to the opener on all browsers).
   *
   * On success or cancellation, the popup is programmatically closed and the
   * interval is cleared.
   *
   * @param requestId - The signing request ID to match against incoming messages.
   * @param popup - The popup `Window` reference returned by `openPopup`.
   * @returns A `SigningResult` discriminated union.
   */
  private waitForPopupResponse(
    requestId: string,
    popup: Window
  ): Promise<SigningResult> {
    const dialogOrigin = this.getDialogOrigin();
    return new Promise((resolve) => {
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          resolve({
            success: false,
            requestId,
            error: {
              code: "USER_REJECTED",
              message: "Popup was closed without completing",
            },
          });
        }
      }, 500);

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== dialogOrigin) {
          return;
        }

        const message = event.data;
        // The Messenger sends: { type, success, data: { requestId, signature }, error }
        const payload = message?.data as { requestId?: string; signature?: WebAuthnSignature } | undefined;

		if (message?.type === "PASSKEY_DISCONNECT") {
			clearInterval(checkClosed);
			window.removeEventListener("message", handleMessage);
			popup.close();
			resolve({
				success: false,
				requestId,
				error: { code: "USER_REJECTED", message: "Session disconnected" },
			});
			return;
		}

        if (
          message?.type === "PASSKEY_SIGNING_RESULT" &&
          payload?.requestId === requestId
        ) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          popup.close();

          if (message.success && payload.signature) {
            resolve({
              success: true,
              requestId,
              signature: payload.signature,
            });
          } else {
            resolve({
              success: false,
              requestId,
              error: parseSigningResultError(message.error),
            });
          }
        }
      };

      window.addEventListener("message", handleMessage);
    });
  }

  /**
   * Fetch the completed signing result from the passkey service by request ID.
   *
   * Used by the redirect flow: after the passkey service redirects back to
   * `redirectUrl`, the integrator calls `handleRedirectCallback()`, which
   * extracts `requestId` from the URL query params and delegates here to
   * retrieve the full signature from the server.
   *
   * @param requestId - The signing request ID from the redirect callback URL.
   * @returns A `SigningResult` discriminated union.
   */
  private async fetchSigningResult(requestId: string): Promise<SigningResult> {
    const response = await fetch(
      `${this.config.providerUrl}/api/sign/request/${requestId}`,
      {
        headers: this.config.clientId
          ? { "x-client-id": this.config.clientId }
          : {},
      }
    );

    if (!response.ok) {
      return {
        success: false,
        requestId,
        error: {
          code: "NETWORK_ERROR",
          message: "Failed to fetch signing result",
        },
      };
    }

    const data = (await response.json()) as Omit<SigningRequestStatus, "error"> & { error?: unknown };

    if (data.status === "COMPLETED" && data.signature) {
      return {
        success: true,
        requestId,
        signature: data.signature,
      };
    }

    const error = parseSigningResultError(
      data.error,
      data.status === "EXPIRED" ? "EXPIRED" : "UNKNOWN",
      `Request status: ${data.status}`,
    );
    return {
      success: false,
      requestId,
      error: {
        ...error,
      },
    };
  }
}
